import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { generateQrDataUrl } from '@wysiwyg/pdf-export';
import { labelVariables, labelVariablesForContext } from './variables';
import { appConfig } from './appConfig';
import { DEFAULT_FONT } from './fontConfig';
import {
  generateLabelPdf,
  resolveBarcodeValue,
  computeLayout as computeLayoutService,
} from './services/pdfGenerator';
import type { LegacyAssetResolver } from './legacyAssets';
import {
  getEffectiveFontFamily,
  getEffectiveImageUrl,
  preloadNewAssets,
  blobToBase64,
  injectNewAssetFontFace,
  normalizeLegacyIds,
} from './legacyAssets';
import { normalizeLoadedTemplate, normalizeQrMarginsForSave } from './templateNormalization';
import {
  ElementKind,
  ImageElement,
  LabelElement,
  Template,
  TextElement,
  VariableOption,
  QrElement,
  BarcodeElement,
  BarcodeFormat,
  BarcodeDataSource,
  PreviewLayout,
  ThemeVariable,
  ThemeVariableType,
  ThemeVariableOption,
  DateKind,
  TextCondition,
  ImageCondition,
  ColorCondition,
  ConditionOperator,
  CustomLayout,
  ShapeElement,
  ShapeType,
  ShapeVertex,
  AssetReference,
  AssetCategory,
} from './types';
import { generateBarcodeDataUrl, type BarcodeResult } from './barcodeGenerator';
import {
  buildExamplesMap,
  renderTextWithData,
  RawDataContext,
  DATE_FORMAT_PRESETS,
  DEFAULT_DATE_FORMAT,
  isDateVariable,
  extractVariableKeys,
  parseExampleObject,
  getDrillPaths,
  ObjectVariableInfo,
  NUMBER_DECIMAL_PRESETS,
  NUMBER_METHOD_PRESETS,
  DEFAULT_NUMBER_DECIMAL_PLACES,
  DEFAULT_NUMBER_METHOD,
  isNumberVariable,
  NumberFormatEntry,
  resolveConditionalTextSource,
} from './textFormatter';
import { snapPoint, snapValue } from './gridSnap';
import { QrSizeInput } from './QrSizeInput';
import {
  getQrDesignGeometry,
  getMinimumQrModuleSize,
  getQrCacheKey,
  getQrTemplateGeometry,
  getQrMargin,
  getResizedQrMargin,
  getResizedQrDesignGeometry,
  QR_DEFAULT_ERROR_CORRECTION,
  QR_DESIGN_PAYLOAD,
} from './qrSizing';
import { wrapLines } from './textWrap';
import {
  DEFAULT_PREVIEW_LAYOUT_ID,
  defaultPreviewLayouts,
  getPreviewLayouts,
  loadPreviewLayouts,
} from './previewLayouts';
import {
  buildThemeVariableKey,
  parseThemeVariableValue,
  removeVariableKeyFromText,
  replaceVariableKeyInText,
  themeVariablesToDataMap,
  themeVariablesToExamples,
} from './themeVariables';
import {
  buildConditionTypeByKey,
  defaultOperatorForType,
  matchesCondition,
  operatorLabels,
  operatorsForType,
} from './conditions';
import { type MockDataBundle, normalizeCoaKeys } from './coaUtils';
import {
  getEffectiveShelfLifeDays,
  getUsedPrintDateVariables,
  withCalculatedPrintDates,
  withCanonicalDateAliases,
} from '../utils/printDates';
import { prepareRenderData } from './templateRuntime';
import TacValueEditor from '../components/TacValueEditor';
import { elementHasTacContext, getTacAdjustment, resolveMarketTac, tacMarketAllowsCalculation, withTacMarketPolicy, TAC_PERCENT_KEY, tacElementContext, tacPackageKey, withTacSources } from '../utils/tac';
import FullScreenConfirmOverlay from '../components/FullScreenConfirmOverlay/FullScreenConfirmOverlay';
import { metrcGlobalsData } from '@wysiwyg/moss-runtime';
import { useEditorPackageCoa } from '../hooks/useEditorPackageCoa';
import { showToastError } from '../components/ToastNotifications';
import {
  type PackageCoaOption,
  type Product,
  fetchPackagesWithCoa,
  getStagedPackageOptionId,
} from './packageUtils';
import { AssetBrowser } from './AssetBrowser';
import { type AssetApi, resolveAssetUrl, fetchAssetContent } from './assetService';
import './wysiwyg.css';
import IconButton from '../components/IconButton';
import MarketsSelector from '../MarketsSelector';
import { getFormatOrDefault, getFormatCapabilities } from './formats/index';
import type { FormatResolvers, FormatCapabilities } from './formats/types';
import {
  TextElementIcon,
  ImageElementIcon,
  QrElementIcon,
  BarcodeElementIcon,
  ShapeElementIcon,
} from '../components/ElementIcons';
import {
  getCOAByPackageId,
  getPackagesStagedTotals,
  getPackagesTotals,
} from '../hooks/usePackages';
import {
  getFileContentUrl,
  getFileLink,
  getStaticFiles,
  uploadFileId,
} from '../hooks/useFiles';
import { showToastWarning } from '../components/ToastNotifications';
import {
  getZplDisplayFont,
  resolveZplFontFamily,
  ZPL_FONT_OPTIONS,
} from './formats/zpl/fonts';

const palette = {
  pageBg: 'var(--bg)',
  cardBg: 'var(--panel)',
  cardAlt: 'var(--bg)',
  border: 'var(--border)',
  text: 'var(--primary)',
  muted: 'var(--muted)',
  accent: 'var(--brand)',
  accentDark: 'var(--brand)',
  radius: 'var(--radius)',
};

const MIN_FONT_SIZE = 1;
// Shared by the Width/Height inputs and the save guard so the two never disagree.
const MIN_TEMPLATE_DIMENSION = 0.01;
type LabelDataSnapshot = { common: Record<string, any>; qrList: string[] };

export const buildPdfGenerationData = (
  labelData: LabelDataSnapshot,
  mockData: MockDataBundle,
  calculatedCoa: Record<string, any> = mockData.coa,
) => ({
  common: labelData.common,
  coa: calculatedCoa,
  job: mockData.job,
  product: mockData.product,
  qrValues: labelData.qrList,
});
const MIN_CUSTOM_DIMENSION = 0.1;
const DEFAULT_CUSTOM_PAGE_WIDTH = 8.5;
const DEFAULT_CUSTOM_PAGE_HEIGHT = 11;
const DRAG_EDGE_SCROLL_THRESHOLD = 72;
const DRAG_EDGE_SCROLL_MAX_SPEED = 28;

const AUTO_LAYOUT_ID = 'auto-label-size';

const uid = () => Math.random().toString(36).slice(2, 10);

const generateSignetLogoSvg = (color: string): string => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 -4 260 258">
    <path transform="scale(0.30,0.30), translate(18 3)" fill="${color}" d="m212.48,77.47c2.38-9.3,5.75-21.1,6.95-29.02C189.53,25.07,156.5,7.41,120.11,0,75.6,9.99,35.52,23.34,0,45.31c14.5,65.01,61.03,136.86,120.11,168.27,47.66-33.87,72.72-83,87.6-121.22-26.49-33.06-69.68-55.97-121.79-60.93,46.38-4.2,94.94,17.36,126.57,46.03Zm-92.38,102.25c-40.54-27.57-69.34-68.66-85.65-118.22.05-.01.11-.01.16-.03,53.75-8.79,104.38,6.42,139.41,36.7.02.03.06.05.09.07-14.46,34.47-33.56,64.15-54.01,81.48h0Z"/>
</svg>`;
  return `data:image/svg+xml;base64,${btoa(svg)}`;
};

const defaultTemplate: Template = {
  id: 'draft-1',
  name: 'Untitled Label',
  organization: '',
  defaultLayoutId: AUTO_LAYOUT_ID,
  width: 480,
  height: 270,
  realWidth: 6.67,
  realHeight: 3.75,
  realUnit: 'in',
  background: '#f7f8fa',
  elements: [],
  themeVariables: [],
};

type EditorProps = {
  initialTemplate?: Template;
  onSave?: (template: Template, marketIds: number[], brandIds: number[]) => void;
  onLoad?: (template: Template) => void;
  legacyAssetResolver?: LegacyAssetResolver;
  organizationId?: number;
  marketIds?: number[];
  availableMarkets?: Array<{ id: number; name: string; code?: string; tacCalculationEnabled?: boolean }>;
  brandIds?: Array<number | string>;
  availableBrands?: Array<{ id?: number | string; name: string }>;
  provenance?: { market: string; category: string };
  onChangeStartingPoint?: (hasChanges: boolean) => void;
};

const parseBrandId = (id: unknown): number | undefined => {
  if (typeof id === 'number') return Number.isSafeInteger(id) && id > 0 ? id : undefined;
  if (typeof id !== 'string') return undefined;

  const trimmed = id.trim();
  if (!/^\d+$/.test(trimmed)) return undefined;

  const parsed = Number(trimmed);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : undefined;
};

const normalizeBrandIds = (ids: unknown): number[] => {
  if (!Array.isArray(ids)) return [];
  const normalized: number[] = [];
  const seen = new Set<number>();
  ids.forEach((id) => {
    const parsed = parseBrandId(id);
    if (parsed === undefined || seen.has(parsed)) return;
    seen.add(parsed);
    normalized.push(parsed);
  });
  return normalized;
};

const normalizeBrands = (brands: unknown): Array<{ id: number; name: string }> => {
  if (!Array.isArray(brands)) return [];
  const normalized: Array<{ id: number; name: string }> = [];
  const seen = new Set<number>();
  brands.forEach((brand) => {
    if (!brand || typeof brand !== 'object') return;
    const candidate = brand as { id?: unknown; name?: unknown };
    const id = parseBrandId(candidate.id);
    if (id === undefined || seen.has(id)) return;
    seen.add(id);
    normalized.push({ id, name: typeof candidate.name === 'string' ? candidate.name : '' });
  });
  return normalized;
};

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const formatDimensionHint = (value: number): string => String(Number(value.toFixed(3)));

// Anything a decimal can pass through while being typed, including a bare "." and a trailing ".".
const DECIMAL_DRAFT = /^(?:\d+\.?\d*|\.\d*)?$/;

const draftFromValue = (value: number | undefined): string =>
  value === undefined ? '' : String(value);

const customDimensionInputStyle: React.CSSProperties = {
  width: 70,
  padding: '4px 6px',
  borderRadius: 6,
  border: `1px solid ${palette.border}`,
  background: palette.cardBg,
  color: palette.text,
  fontSize: 12,
};

// A controlled type="number" sanitizes incomplete text, so the "." in ".5" is dropped before the
// next keystroke arrives. Holding the raw draft in a text input keeps normal decimal entry working.
const useDimensionDraft = (
  value: number | undefined,
  onCommit: (value: number | undefined) => void,
) => {
  const [draft, setDraft] = useState(() => draftFromValue(value));
  const committedRef = useRef(value);

  // Re-seed when the value changes elsewhere (e.g. importing a template) but not from our own commit.
  useEffect(() => {
    if (committedRef.current === value) return;
    committedRef.current = value;
    setDraft(draftFromValue(value));
  }, [value]);

  const commit = (next: number | undefined) => {
    committedRef.current = next;
    onCommit(next);
  };

  return { draft, setDraft, commit };
};

type DimensionInputProps = {
  label: string;
  value: number | undefined;
  onCommit: (value: number | undefined) => void;
  minimum: number;
  emptyFallback: number | undefined;
  placeholder?: string;
};

const DimensionInput: React.FC<DimensionInputProps> = ({
  label,
  value,
  onCommit,
  minimum,
  emptyFallback,
  placeholder,
}) => {
  const { draft, setDraft, commit } = useDimensionDraft(value, onCommit);

  const resolve = (raw: string, clamp: boolean): number | undefined => {
    const parsed = Number(raw);
    if (raw === '' || !Number.isFinite(parsed)) return emptyFallback;
    return clamp ? Math.max(minimum, parsed) : parsed;
  };

  return (
    <input
      type="text"
      inputMode="decimal"
      aria-label={label}
      placeholder={placeholder}
      value={draft}
      onChange={(e) => {
        const next = e.target.value;
        if (!DECIMAL_DRAFT.test(next)) return;
        setDraft(next);
        // Clamping waits for blur so a half-typed number is never rewritten mid-edit.
        commit(resolve(next, false));
      }}
      onBlur={() => {
        const next = resolve(draft, true);
        setDraft(draftFromValue(next));
        commit(next);
      }}
      style={customDimensionInputStyle}
    />
  );
};

const dimensionStepButtonStyle: React.CSSProperties = {
  width: 18,
  height: 14,
  padding: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: 0,
  background: 'transparent',
  color: palette.text,
  fontSize: 8,
  lineHeight: 1,
  cursor: 'pointer',
};

type CanvasDimensionFieldProps = {
  axis: 'Width' | 'Height';
  unit: 'in' | 'cm';
  value: number | undefined;
  invalid: boolean;
  describedBy?: string;
  onChange: (value: number | undefined) => void;
};

// Validation lives in getTemplateDimensionValidationError rather than a native min, because the raw
// draft has to survive a half-typed decimal that a type="number" would sanitize away.
const CanvasDimensionField: React.FC<CanvasDimensionFieldProps> = ({
  axis,
  unit,
  value,
  invalid,
  describedBy,
  onChange,
}) => {
  const label = `${axis} (${unit})`;
  const inputId = useId();
  const stepAmount = unit === 'cm' ? 0.1 : 0.01;
  const atMinimum = (value ?? MIN_TEMPLATE_DIMENSION) <= MIN_TEMPLATE_DIMENSION;
  const { draft, setDraft, commit } = useDimensionDraft(value, onChange);

  const clampToMinimum = (next: number) =>
    Number(Math.max(MIN_TEMPLATE_DIMENSION, next).toFixed(3));

  const stepBy = (direction: 1 | -1) => {
    const next = clampToMinimum((value ?? MIN_TEMPLATE_DIMENSION) + direction * stepAmount);
    setDraft(draftFromValue(next));
    commit(next);
  };

  return (
    <div style={{ display: 'grid', gap: 2, minWidth: 0 }}>
      <label htmlFor={inputId} style={{ fontSize: 11, color: palette.muted }}>
        {label}
      </label>
      <div className="wysiwyg-dimension-control">
        <input
          id={inputId}
          type="text"
          inputMode="decimal"
          // A text input drops the spinbutton semantics and arrow-key stepping that type="number"
          // gave for free, so both are restored here alongside the announced minimum.
          role="spinbutton"
          aria-valuenow={value}
          aria-valuemin={MIN_TEMPLATE_DIMENSION}
          value={draft}
          aria-invalid={invalid}
          aria-describedby={describedBy}
          onChange={(e) => {
            const next = e.target.value;
            if (!DECIMAL_DRAFT.test(next)) return;
            setDraft(next);
            const parsed = Number(next);
            // onChange reads undefined as "leave this axis alone", so an incomplete draft stays local.
            if (next !== '' && Number.isFinite(parsed)) commit(parsed);
          }}
          onKeyDown={(e) => {
            if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
            e.preventDefault();
            stepBy(e.key === 'ArrowUp' ? 1 : -1);
          }}
          onBlur={() => {
            const parsed = Number(draft);
            if (draft === '' || !Number.isFinite(parsed)) {
              setDraft(draftFromValue(value));
              return;
            }
            const next = clampToMinimum(parsed);
            setDraft(draftFromValue(next));
            commit(next);
          }}
          style={{
            padding: '6px 26px 6px 8px',
            borderRadius: 8,
            border: `1px solid ${palette.border}`,
            background: palette.cardBg,
            color: palette.text,
            width: 110,
          }}
        />
        <div className="wysiwyg-dimension-stepper">
          <button
            type="button"
            aria-label={`Increase ${axis.toLowerCase()}`}
            onClick={() => stepBy(1)}
            style={dimensionStepButtonStyle}
          >
            ▲
          </button>
          <button
            type="button"
            aria-label={`Decrease ${axis.toLowerCase()}`}
            disabled={atMinimum}
            onClick={() => stepBy(-1)}
            style={{
              ...dimensionStepButtonStyle,
              cursor: atMinimum ? 'not-allowed' : 'pointer',
              opacity: atMinimum ? 0.4 : 1,
            }}
          >
            ▼
          </button>
        </div>
      </div>
    </div>
  );
};

const convertUnits = (value: number, from: 'in' | 'cm', to: 'in' | 'cm') => {
  if (from === to) return value;
  return to === 'cm' ? value * 2.54 : value / 2.54;
};

// Repair size comes from the bundled default layout so it is a real label size rather than a magic
// number. Org layouts fetched at runtime replace this list, so treat it as a sane default, not a match.
const DEFAULT_LABEL_LAYOUT =
  defaultPreviewLayouts.find((layout) => layout.id === DEFAULT_PREVIEW_LAYOUT_ID) ??
  defaultPreviewLayouts[0];
const DEFAULT_TEMPLATE_REAL_WIDTH_IN = convertUnits(
  DEFAULT_LABEL_LAYOUT.labelWidth,
  DEFAULT_LABEL_LAYOUT.unit,
  'in',
);
const DEFAULT_TEMPLATE_REAL_HEIGHT_IN = convertUnits(
  DEFAULT_LABEL_LAYOUT.labelHeight,
  DEFAULT_LABEL_LAYOUT.unit,
  'in',
);

const realToPixels = (value: number, unit: 'in' | 'cm', dpi: number) => {
  const inches = unit === 'cm' ? value / 2.54 : value;
  return inches * dpi;
};

const pixelsToReal = (value: number, unit: 'in' | 'cm', dpi: number) => {
  const inches = value / dpi;
  return unit === 'cm' ? inches * 2.54 : inches;
};

// A persisted template spells a missing size as null, '' or omission, and Number() turns the first
// two into 0. Zero and negatives are kept so the minimum-dimension guard still reports them.
const toPersistedNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
};

const getDefaultLabelCount = (value: unknown): number => {
  const persisted = toPersistedNumber(value);
  return persisted !== undefined && persisted >= 1 ? Math.round(persisted) : 1;
};

// Pixels stay authoritative when present; a template carrying neither representation is repaired to
// the default label size expressed in that template's own unit.
const resolveDimensionPair = (
  realValue: unknown,
  pixelValue: unknown,
  fallbackInches: number,
  unit: 'in' | 'cm',
  dpi: number,
): { real: number | undefined; pixels: number } => {
  const real = toPersistedNumber(realValue);
  const pixels = toPersistedNumber(pixelValue);

  if (real !== undefined) return { real, pixels: pixels ?? realToPixels(real, unit, dpi) };
  if (pixels !== undefined) return { real: undefined, pixels };

  const fallback = Number(convertUnits(fallbackInches, 'in', unit).toFixed(3));
  return { real: fallback, pixels: realToPixels(fallback, unit, dpi) };
};

// Templates predating the dpi field must fall back to their format's DPI; a hardcoded 72 reads a
// 203 DPI label ~2.8x too large.
const resolveTemplateDpi = (tpl: Template): number => {
  const dpi = toPersistedNumber(tpl.dpi);
  return dpi !== undefined && dpi > 0 ? dpi : getFormatOrDefault(tpl.outputFormat).defaultDpi;
};

// The canvas inputs, the invalid flags and the save guard must all read the same real-unit value.
const getTemplateRealDimensions = (tpl: Template): { width: number; height: number } => {
  const unit = tpl.realUnit || 'in';
  const dpi = resolveTemplateDpi(tpl);
  const width = resolveDimensionPair(
    tpl.realWidth,
    tpl.width,
    DEFAULT_TEMPLATE_REAL_WIDTH_IN,
    unit,
    dpi,
  );
  const height = resolveDimensionPair(
    tpl.realHeight,
    tpl.height,
    DEFAULT_TEMPLATE_REAL_HEIGHT_IN,
    unit,
    dpi,
  );

  return {
    width: width.real ?? pixelsToReal(width.pixels, unit, dpi),
    height: height.real ?? pixelsToReal(height.pixels, unit, dpi),
  };
};

const downloadTemplateJson = (name: string, json: string) => {
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${name || 'label-template'}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const toCanvasPoint = (
  canvas: HTMLDivElement | null,
  clientX: number,
  clientY: number,
  scale = 1,
) => {
  if (!canvas) return { x: 0, y: 0 };
  const rect = canvas.getBoundingClientRect();
  const invScale = scale || 1;
  return {
    x: (clientX - rect.left) / invScale,
    y: (clientY - rect.top) / invScale,
  };
};

const LINE_HEIGHT = 1;

type ConditionVariableOption = {
  key: string;
  label: string;
  type: ThemeVariableType;
  source: 'theme' | 'component';
};

const normalizeConditionType = (type?: string | ThemeVariableType): ThemeVariableType => {
  if (type === 'number') return 'number';
  if (type === 'boolean') return 'boolean';
  return 'string';
};

export const friendlyVariableName = (key: string) => {
  const prettifyToken = (token: string) => {
    if (!token) return token;
    if (/^[a-z]{1,3}$/.test(token)) return token.toUpperCase();
    if (/^[A-Z0-9]+$/.test(token)) return token;
    return token.charAt(0).toUpperCase() + token.slice(1);
  };
  return key
    .split('.')
    .map((segment) => segment.split(/[_-]/).map(prettifyToken).join(' '))
    .join(' ');
};

export const filterLabelVariables = (variables: typeof labelVariables, search: string) => {
  const term = search.trim().toLowerCase();
  if (!term) return variables;

  return variables.filter(
    (item) =>
      item.key.toLowerCase().includes(term) ||
      item.description.toLowerCase().includes(term) ||
      item.displayName?.toLowerCase().includes(term) ||
      friendlyVariableName(item.key).toLowerCase().includes(term),
  );
};

const normalizePackageSearchValue = (value?: unknown) =>
  String(value ?? '').trim().toLowerCase();

const packageOptionMatchesSearch = (pkg: PackageCoaOption, searchTerm: string) => {
  const term = normalizePackageSearchValue(searchTerm);
  if (term.length < 3) return true;

  const packageLabel = normalizePackageSearchValue(pkg.label);
  return packageLabel.includes(term);
};

const mapActivePackageOption = (pkg: any): PackageCoaOption | null => {
  const pkgId = String(pkg.id || pkg.packageId || pkg.PackageId);
  if (!pkgId || pkgId === 'undefined') return null;

  return {
    id: pkgId,
    label: pkg.label || `Package ${pkgId}`,
    facilityId: pkg.facilityId ?? pkg.FacilityId ?? null,
    facilityName: pkg.facilityName ?? pkg.FacilityName ?? '',
    facilityLicense:
      pkg.facilityLicense ??
      pkg.FacilityLicense ??
      pkg.license ??
      pkg.License ??
      '',
    productId: pkg.productId || pkg.ProductId || undefined,
    labTestingStateName: pkg.labTestingStateName || '',
    marketCode: pkg.marketCode || '',
    coaData: {
      packageTag: pkg.label || '',
      productName: pkg.productName || '',
      strain: pkg.strain || '',
      productCategoryName: pkg.productCategoryName || '',
      quantity: pkg.quantity || 0,
      facilityId: pkg.facilityId,
      labTestingStateName: pkg.labTestingStateName || '',
      marketCode: pkg.marketCode || '',
    },
  };
};

const mapStagedPackageOption = (pkg: any, index = 0): PackageCoaOption | null => {
  const packageLabel = pkg.packageLabel || pkg.PackageLabel || pkg.package_label || pkg.label || pkg.Label;
  if (!packageLabel || normalizePackageSearchValue(packageLabel) === 'undefined') return null;

  let coaData: Record<string, any> = {
    packageTag: packageLabel,
    productName: pkg.productName || pkg.ProductName || '',
    strain: '',
    productCategoryName: '',
    quantity: 0,
    facilityId: pkg.facilityId || pkg.FacilityId,
    labTestingStateName: '',
    marketCode: '',
  };
  let rawData = pkg.data || pkg.Data;
  if (rawData) {
    try {
      if (typeof rawData === 'string') rawData = JSON.parse(rawData);
      if (typeof rawData === 'string') rawData = JSON.parse(rawData);
      coaData = { ...coaData, ...(withCanonicalDateAliases(normalizeCoaKeys(rawData)) || {}) };
    } catch {
      // ignore parse errors for staged COA data
    }
  }

  return {
    id: getStagedPackageOptionId(pkg, packageLabel, index),
    label: `[Staged] ${packageLabel}`,
    facilityId: pkg.facilityId ?? pkg.FacilityId ?? null,
    facilityName: pkg.facilityName ?? pkg.FacilityName ?? '',
    facilityLicense:
      pkg.facilityLicense ??
      pkg.FacilityLicense ??
      pkg.license ??
      pkg.License ??
      '',
    productId: pkg.productId || pkg.ProductId || undefined,
    labTestingStateName:
      coaData.labTestingStateName ||
      coaData.labTestingState ||
      pkg.labTestingStateName ||
      pkg.LabTestingStateName ||
      '',
    marketCode: coaData.marketCode || pkg.marketCode || pkg.MarketCode || '',
    coaData,
  };
};

const previewLayoutsEndpoint = appConfig.previewLayoutsEndpoint;

// Ensure image elements have required defaults (for legacy/migrated templates)
const ensureImageDefaults = (tpl: Template): Template => ({
  ...tpl,
  elements: (tpl.elements || []).map((el) =>
    el.type === 'image'
      ? {
          ...el,
          url: (el as ImageElement).url ?? '',
          fit: (el as ImageElement).fit ?? 'contain',
          opacity: (el as ImageElement).opacity ?? 1,
        }
      : el,
  ),
});

const normalizeTemplate = (tpl: Template): Template => {
  const dpi = resolveTemplateDpi(tpl);
  const unit = tpl.realUnit || 'in';
  const width = resolveDimensionPair(
    tpl.realWidth,
    tpl.width,
    DEFAULT_TEMPLATE_REAL_WIDTH_IN,
    unit,
    dpi,
  );
  const height = resolveDimensionPair(
    tpl.realHeight,
    tpl.height,
    DEFAULT_TEMPLATE_REAL_HEIGHT_IN,
    unit,
    dpi,
  );

  return ensureImageDefaults(
    normalizeLoadedTemplate({
      ...tpl,
      width: width.pixels,
      height: height.pixels,
      realWidth: width.real,
      realHeight: height.real,
      organization: tpl.organization || '',
      defaultLayoutId: tpl.defaultLayoutId || DEFAULT_PREVIEW_LAYOUT_ID,
      themeVariables: tpl.themeVariables || [],
      customLayout: tpl.customLayout,
    }),
  );
};

const isBelowMinimumDimension = (value: number): boolean =>
  !Number.isFinite(value) || value < MIN_TEMPLATE_DIMENSION;

export const getTemplateDimensionValidationError = (tpl: Template): string => {
  const { width, height } = getTemplateRealDimensions(tpl);
  const widthIsInvalid = isBelowMinimumDimension(width);
  const heightIsInvalid = isBelowMinimumDimension(height);

  if (widthIsInvalid && heightIsInvalid)
    return `Width and height must be at least ${MIN_TEMPLATE_DIMENSION}.`;
  if (widthIsInvalid) return `Width must be at least ${MIN_TEMPLATE_DIMENSION}.`;
  if (heightIsInvalid) return `Height must be at least ${MIN_TEMPLATE_DIMENSION}.`;
  return '';
};

const isLegacyTemplate = (tpl: Template): boolean => Boolean(tpl.templateFn);

const buildAutoLayout = (tpl: Template): PreviewLayout => {
  const unit = tpl.realUnit || 'in';
  const tplDpi = tpl.dpi ?? 72;
  const labelWidth = tpl.realWidth ?? tpl.width / tplDpi;
  const labelHeight = tpl.realHeight ?? tpl.height / tplDpi;
  return {
    id: AUTO_LAYOUT_ID,
    label: 'Match label size (1-up)',
    unit,
    pageWidth: labelWidth,
    pageHeight: labelHeight,
    labelWidth,
    labelHeight,
    marginTop: 0,
    marginRight: 0,
    marginBottom: 0,
    marginLeft: 0,
    spacingX: 0,
    spacingY: 0,
    columns: 1,
    rows: 1,
    format: 'sheets',
    printer: 'generic',
    shape: 'rectangle',
  };
};

const ensureMeasureContext = (canvasRef: React.MutableRefObject<HTMLCanvasElement | null>) => {
  if (!canvasRef.current) {
    const c = document.createElement('canvas');
    canvasRef.current = c;
  }
  const ctx = canvasRef.current.getContext('2d');
  return ctx;
};

const EDITOR_SETTINGS_STORAGE_KEY = 'wysiwyg-editor-settings';

interface EditorSettings {
  designZoom: number;
  showOutlines: boolean;
  showRequiredBadges: boolean;
  showRulers: boolean;
  snapToGrid: boolean;
}

const defaultEditorSettings: EditorSettings = {
  designZoom: 1,
  showOutlines: false,
  showRequiredBadges: false,
  showRulers: false,
  snapToGrid: true,
};

const loadEditorSettings = (): EditorSettings => {
  try {
    const raw = localStorage.getItem(EDITOR_SETTINGS_STORAGE_KEY);
    if (!raw) return defaultEditorSettings;
    return { ...defaultEditorSettings, ...JSON.parse(raw) };
  } catch {
    return defaultEditorSettings;
  }
};

const saveEditorSettings = (settings: Partial<EditorSettings>) => {
  try {
    const current = loadEditorSettings();
    localStorage.setItem(EDITOR_SETTINGS_STORAGE_KEY, JSON.stringify({ ...current, ...settings }));
  } catch {
    // ignore storage errors
  }
};

export const LabelEditor: React.FC<EditorProps> = ({
  initialTemplate = defaultTemplate,
  onSave,
  onLoad,
  legacyAssetResolver,
  organizationId,
  marketIds,
  availableMarkets = [],
  brandIds,
  availableBrands = [],
  provenance,
  onChangeStartingPoint,
}) => {
  const normalizedAvailableBrands = useMemo(() => normalizeBrands(availableBrands), [availableBrands]);
  const api = useMemo(() => ({
    getPackagesTotals,
    getPackagesStagedTotals,
    getCOAByPackageId,
    getPreviewLayouts,
    getFileId: ({ file, fileType, mimeType, organizationId: nextOrganizationId }) =>
      uploadFileId({ file, fileType, mimeType, organizationId: nextOrganizationId }),
    getStaticFiles,
    getFileLink,
    getFileContentUrl,
  }), []);
  const normalizedInitialTemplate = useMemo(
    () => normalizeTemplate(initialTemplate),
    [initialTemplate],
  );
  const initialCustomLayout = normalizedInitialTemplate.customLayout;
  const initialPreviewLayoutId =
    normalizedInitialTemplate.defaultLayoutId ||
    (initialCustomLayout ? 'custom' : DEFAULT_PREVIEW_LAYOUT_ID);
  const initialLabelCount = getDefaultLabelCount(normalizedInitialTemplate.defaultLabelCount);
  const [template, setTemplate] = useState<Template>(normalizedInitialTemplate);
  const [tacCalculationEnabled, setTacCalculationEnabled] = useState(true);
  const [pendingTacInsertion, setPendingTacInsertion] = useState<{ variable: VariableOption; x: number; y: number; context: string } | null>(null);
  const [tacAddAcknowledged, setTacAddAcknowledged] = useState(false);
  const hasQrElement = template.elements.some((element) => element.type === 'qr');
  const templateUnit = template.realUnit || 'in';
  const templateDpi = resolveTemplateDpi(template);
  const { width: canvasWidth, height: canvasHeight } = getTemplateRealDimensions(template);
  const templateWidthIsInvalid = isBelowMinimumDimension(canvasWidth);
  const templateHeightIsInvalid = isBelowMinimumDimension(canvasHeight);
  const dimensionValidationError = getTemplateDimensionValidationError(template);
  const dimensionsErrorId = useId();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [pendingInsertions, setPendingInsertions] = useState<{
    elementIds: string[];
    qrIds: string[];
  }[]>([]);
  const [importValue, setImportValue] = useState('');
  const [search, setSearch] = useState('');
  const [newThemeName, setNewThemeName] = useState('');
  const [newThemeType, setNewThemeType] = useState<ThemeVariableType>('string');
  const [newThemeValue, setNewThemeValue] = useState<string>('');
  const [newThemeOptions, setNewThemeOptions] = useState<ThemeVariableOption[]>([]);
  const [themeVarDragIdx, setThemeVarDragIdx] = useState<number | null>(null);
  const [themeVarDropIdx, setThemeVarDropIdx] = useState<number | null>(null);
  const [optionDragState, setOptionDragState] = useState<{
    variableId: string;
    fromIdx: number;
  } | null>(null);
  const [optionDropIdx, setOptionDropIdx] = useState<number | null>(null);
  const [showRequiredBadges, setShowRequiredBadges] = useState(
    () => loadEditorSettings().showRequiredBadges,
  );
  const [showOutlines, setShowOutlines] = useState(() => loadEditorSettings().showOutlines);
  const [showAddedPane, setShowAddedPane] = useState(false);
  const [showThemePane, setShowThemePane] = useState(false);
  const [showPropertiesPane, setShowPropertiesPane] = useState(true);
  const [legacyWarningDismissed, setLegacyWarningDismissed] = useState(false);
  const [canvasTab, setCanvasTab] = useState<'design' | 'preview'>('design');
  const previewLayoutSelectId = useId();
  const [previewSizeId, setPreviewSizeId] = useState<string>(initialPreviewLayoutId);
  const [previewLayouts, setPreviewLayouts] = useState<PreviewLayout[]>(defaultPreviewLayouts);
  const [previewLayoutSearch, setPreviewLayoutSearch] = useState<string>('');
  const [isPreviewLayoutDropdownOpen, setIsPreviewLayoutDropdownOpen] = useState(false);
  // Custom layout state
  const [customUnit, setCustomUnit] = useState<'in' | 'cm'>(initialCustomLayout?.unit ?? 'in');
  const [customPageWidth, setCustomPageWidth] = useState<number | undefined>(
    initialCustomLayout?.pageWidth ?? DEFAULT_CUSTOM_PAGE_WIDTH,
  );
  const [customPageHeight, setCustomPageHeight] = useState<number | undefined>(
    initialCustomLayout?.pageHeight ?? DEFAULT_CUSTOM_PAGE_HEIGHT,
  );
  const [customLabelWidth, setCustomLabelWidth] = useState<number | undefined>(
    initialCustomLayout?.labelWidth,
  );
  const [customLabelHeight, setCustomLabelHeight] = useState<number | undefined>(
    initialCustomLayout?.labelHeight,
  );
  const [customMarginTop, setCustomMarginTop] = useState<number>(
    initialCustomLayout?.marginTop ?? 0.5,
  );
  const [customMarginRight, setCustomMarginRight] = useState<number>(
    initialCustomLayout?.marginRight ?? 0.5,
  );
  const [customMarginBottom, setCustomMarginBottom] = useState<number>(
    initialCustomLayout?.marginBottom ?? 0.5,
  );
  const [customMarginLeft, setCustomMarginLeft] = useState<number>(
    initialCustomLayout?.marginLeft ?? 0.5,
  );
  const [customSpacingX, setCustomSpacingX] = useState<number>(
    initialCustomLayout?.spacingX ?? 0.1,
  );
  const [customSpacingY, setCustomSpacingY] = useState<number>(
    initialCustomLayout?.spacingY ?? 0.1,
  );
  const [customColumns, setCustomColumns] = useState<number | undefined>(
    initialCustomLayout?.columns,
  );
  const [customRows, setCustomRows] = useState<number | undefined>(initialCustomLayout?.rows);
  const [customFormat, setCustomFormat] = useState<'sheets' | 'roll'>(
    initialCustomLayout?.format ?? 'sheets',
  );
  const [customShape, setCustomShape] = useState<'rectangle' | 'round-rectangle' | 'circle'>(
    initialCustomLayout?.shape ?? 'rectangle',
  );
  const [customLabelRotation, setCustomLabelRotation] = useState<0 | 90 | 180 | 270>(
    initialCustomLayout?.labelRotation ?? 0,
  );
  const previewRef = useRef<HTMLDivElement | null>(null);
  const previewLayoutDropdownRef = useRef<HTMLDivElement | null>(null);
  const previewLayoutSearchInputRef = useRef<HTMLInputElement | null>(null);
  const [previewZoom, setPreviewZoom] = useState<number>(1);
  const [previewTopOffset, setPreviewTopOffset] = useState<number>(
    normalizedInitialTemplate.topOffset ?? 0,
  );
  const [previewLeftOffset, setPreviewLeftOffset] = useState<number>(
    normalizedInitialTemplate.leftOffset ?? 0,
  );
  const [pageRotation, setPageRotation] = useState<0 | 90 | 180 | 270>(
    normalizedInitialTemplate.pageRotation ?? 0,
  );
  const [labelCount, setLabelCount] = useState<number>(initialLabelCount);
  const [labelCountInput, setLabelCountInput] = useState<number>(initialLabelCount);
  const [qrIndexOffset, setQrIndexOffset] = useState<number>(0);
  const [qrValues, setQrValues] = useState<string[]>([]);
  const [commonData, setCommonData] = useState<Record<string, any>>({});
  const labelDataRef = useRef<LabelDataSnapshot>({ common: {}, qrList: [] });
  const labelDataRequestIdRef = useRef(0);
  const pdfOperationRef = useRef<'preview' | 'export' | null>(null);
  const [coaData, setCoaData] = useState<Record<string, any>>({});
  const [selectedCoaId, setSelectedCoaId] = useState<string>('none');
  const [selectedPackageOption, setSelectedPackageOption] = useState<PackageCoaOption | null>(null);
  const {
    coa: resolvedCoaData,
    product: resolvedProductData,
    isLoadingCoa,
  } = useEditorPackageCoa(organizationId, selectedPackageOption);
  const tacMarketEnabled = withTacMarketPolicy(resolvedCoaData, availableMarkets, selectedPackageOption?.marketCode)._tacMarketCalculationEnabled;
  const mockDataBundle = useMemo<MockDataBundle>(() => ({
    coa: selectedPackageOption ? { ...resolvedCoaData, _tacMarketCalculationEnabled: tacMarketEnabled } : resolvedCoaData,
    job: {},
    product: resolvedProductData,
    coaFlattened: selectedPackageOption ? { coa: { ...resolvedCoaData, _tacMarketCalculationEnabled: tacMarketEnabled } } : {},
  }), [resolvedCoaData, resolvedProductData, selectedPackageOption, tacMarketEnabled]);
  const [initialPackages, setInitialPackages] = useState<PackageCoaOption[]>([]);
  const [activePackageResults, setActivePackageResults] = useState<PackageCoaOption[]>([]);
  const [stagedPackageResults, setStagedPackageResults] = useState<PackageCoaOption[]>([]);
  const [isLoadingPackages, setIsLoadingPackages] = useState<boolean>(false);
  const [isPackageDropdownOpen, setIsPackageDropdownOpen] = useState<boolean>(false);
  const [packageSearchTerm, setPackageSearchTerm] = useState<string>('');
  const [packageSearchDebounced, setPackageSearchDebounced] = useState<string>('');
  const [isSearchingPackages, setIsSearchingPackages] = useState<boolean>(false);
  const packageDropdownRef = useRef<HTMLDivElement | null>(null);
  const packageSearchRequestIdRef = useRef(0);
  const [activeMarketIds, setActiveMarketIds] = useState<number[]>(() => marketIds || []);
  const [activeBrandIds, setActiveBrandIds] = useState<number[]>(() => normalizeBrandIds(brandIds));
  const [allBrands, setAllBrands] = useState(() => normalizedInitialTemplate.allBrands === true);
  const startingPointSnapshotRef = useRef<string>();
  const getStartingPointSnapshot = () => JSON.stringify({ template, activeMarketIds, activeBrandIds, allBrands });
  const captureStartingPointSnapshot = () => {
    if (!onChangeStartingPoint) return;
    startingPointSnapshotRef.current ??= getStartingPointSnapshot();
  };
  const dragAutoScrollPointerRef = useRef<{ x: number; y: number } | null>(null);
  const dragAutoScrollIntervalRef = useRef<number | null>(null);
  const dragAutoScrollActiveRef = useRef(false);
  const normalizedPackageSearchTerm = useMemo(
    () => normalizePackageSearchValue(packageSearchTerm),
    [packageSearchTerm],
  );
  const hasPackageSearchApi = Boolean(organizationId);
  const isPackageSearchActive = normalizedPackageSearchTerm.length >= 3 && hasPackageSearchApi;
  const searchPackageOptions = useMemo(
    () => [...activePackageResults, ...stagedPackageResults],
    [activePackageResults, stagedPackageResults],
  );
  const allPackageCoaOptions = useMemo(
    () => [...searchPackageOptions, ...initialPackages],
    [searchPackageOptions, initialPackages],
  );
  const visiblePackageCoaOptions = useMemo(
    () => {
      const source = isPackageSearchActive ? searchPackageOptions : initialPackages;
      return source.filter((pkg) => packageOptionMatchesSearch(pkg, normalizedPackageSearchTerm));
    },
    [initialPackages, isPackageSearchActive, normalizedPackageSearchTerm, searchPackageOptions],
  );

  useEffect(() => {
    setActiveMarketIds(marketIds || []);
  }, [marketIds]);

  useEffect(() => {
    setActiveBrandIds(normalizeBrandIds(brandIds));
  }, [brandIds]);

  const stopDragViewportAutoScroll = useCallback(() => {
    dragAutoScrollActiveRef.current = false;
    dragAutoScrollPointerRef.current = null;
    if (dragAutoScrollIntervalRef.current !== null) {
      window.clearInterval(dragAutoScrollIntervalRef.current);
      dragAutoScrollIntervalRef.current = null;
    }
  }, []);

  const startDragViewportAutoScroll = useCallback((clientX: number, clientY: number) => {
    dragAutoScrollActiveRef.current = true;
    dragAutoScrollPointerRef.current = { x: clientX, y: clientY };

    if (dragAutoScrollIntervalRef.current !== null) return;

    dragAutoScrollIntervalRef.current = window.setInterval(() => {
      if (!dragAutoScrollActiveRef.current) return;

      const pointer = dragAutoScrollPointerRef.current;
      const scrollRoot = document.scrollingElement;
      if (!pointer || !scrollRoot) return;

      let deltaY = 0;
      if (pointer.y < DRAG_EDGE_SCROLL_THRESHOLD) {
        deltaY = -Math.ceil(
          ((DRAG_EDGE_SCROLL_THRESHOLD - pointer.y) / DRAG_EDGE_SCROLL_THRESHOLD) *
            DRAG_EDGE_SCROLL_MAX_SPEED,
        );
      } else {
        const bottomDistance = window.innerHeight - pointer.y;
        if (bottomDistance < DRAG_EDGE_SCROLL_THRESHOLD) {
          deltaY = Math.ceil(
            ((DRAG_EDGE_SCROLL_THRESHOLD - bottomDistance) / DRAG_EDGE_SCROLL_THRESHOLD) *
              DRAG_EDGE_SCROLL_MAX_SPEED,
          );
        }
      }

      if (deltaY === 0) return;

      const maxScrollTop = Math.max(0, scrollRoot.scrollHeight - scrollRoot.clientHeight);
      if ((deltaY < 0 && scrollRoot.scrollTop <= 0) || (deltaY > 0 && scrollRoot.scrollTop >= maxScrollTop)) {
        return;
      }

      window.scrollBy(0, deltaY);
    }, 16);
  }, []);

  useEffect(() => {
    const handleGlobalDragOver = (event: DragEvent) => {
      if (!dragAutoScrollActiveRef.current) return;
      dragAutoScrollPointerRef.current = { x: event.clientX, y: event.clientY };
    };

    const handleGlobalDragStop = () => {
      stopDragViewportAutoScroll();
    };

    window.addEventListener('dragover', handleGlobalDragOver);
    window.addEventListener('drop', handleGlobalDragStop);
    window.addEventListener('dragend', handleGlobalDragStop);

    return () => {
      window.removeEventListener('dragover', handleGlobalDragOver);
      window.removeEventListener('drop', handleGlobalDragStop);
      window.removeEventListener('dragend', handleGlobalDragStop);
      stopDragViewportAutoScroll();
    };
  }, [stopDragViewportAutoScroll]);

  const [isPrinting, setIsPrinting] = useState<boolean>(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [isGeneratingPreview, setIsGeneratingPreview] = useState<boolean>(false);
  const [qrCacheBump, setQrCacheBump] = useState<number>(0);
  const dataEndpoints: { commonData?: string; qrValues?: string } = appConfig.dataEndpoints || {};
  const commonEndpoint = dataEndpoints.commonData;
  const qrEndpoint = dataEndpoints.qrValues;
  const qrCacheRef = useRef<Record<string, string>>({});
  const designQrCacheRef = useRef<Record<string, string>>({}); // Separate cache for design canvas QR codes
  const barcodeCacheRef = useRef<Record<string, BarcodeResult>>({});
  // Legacy asset caches (loaded from BE for migrated templates)
  const [legacyImages, setLegacyImages] = useState<Record<string, string>>({});
  const [legacyFonts, setLegacyFonts] = useState<Record<string, string>>({});
  const [legacyAssetsLoading, setLegacyAssetsLoading] = useState(false);
  // Refs to access latest legacy assets in async callbacks (avoids stale closure issues)
  const legacyImagesRef = useRef<Record<string, string>>({});
  const legacyFontsRef = useRef<Record<string, string>>({});
  const legacyAssetsLoadingRef = useRef(false);
  // Keep refs in sync with state
  React.useEffect(() => {
    legacyImagesRef.current = legacyImages;
  }, [legacyImages]);
  React.useEffect(() => {
    legacyFontsRef.current = legacyFonts;
  }, [legacyFonts]);
  React.useEffect(() => {
    legacyAssetsLoadingRef.current = legacyAssetsLoading;
  }, [legacyAssetsLoading]);

  // Asset browser state for fonts and images
  const [assetBrowserState, setAssetBrowserState] = useState<{
    isOpen: boolean;
    category: AssetCategory;
    targetElementId: string;
    conditionId?: string; // For targeting a specific condition's image
  } | null>(null);

  // New asset caches (for assets uploaded via AssetBrowser)
  const [newAssetImages, setNewAssetImages] = useState<Record<string, string>>({});
  const [newAssetFonts, setNewAssetFonts] = useState<Record<string, string>>({});
  const newAssetImagesRef = useRef<Record<string, string>>({});
  const newAssetFontsRef = useRef<Record<string, string>>({});
  React.useEffect(() => {
    newAssetImagesRef.current = newAssetImages;
  }, [newAssetImages]);
  React.useEffect(() => {
    newAssetFontsRef.current = newAssetFonts;
  }, [newAssetFonts]);

  useEffect(() => {
    if (!organizationId || !activeMarketIds?.length) {
      setInitialPackages([]);
      setActivePackageResults([]);
      setStagedPackageResults([]);
      setIsLoadingPackages(false);
      setIsSearchingPackages(false);
      return;
    }

    let cancelled = false;
    setIsLoadingPackages(true);

    fetchPackagesWithCoa(organizationId, activeMarketIds, { logPrefix: '[LabelEditor]', totalsPageSize: 10 })
      .then((packages) => {
        if (!cancelled) {
          setInitialPackages(packages);
        }
      })
      .catch((err) => {
        console.error('Failed to fetch packages:', err);
        if (!cancelled) {
          setInitialPackages([]);
          setActivePackageResults([]);
          setStagedPackageResults([]);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoadingPackages(false);
      });

    return () => {
      cancelled = true;
    };
  }, [organizationId, activeMarketIds]);

  useEffect(() => {
    packageSearchRequestIdRef.current += 1;
    setActivePackageResults([]);
    setStagedPackageResults([]);
    setIsSearchingPackages(normalizedPackageSearchTerm.length >= 3 && hasPackageSearchApi);
  }, [hasPackageSearchApi, normalizedPackageSearchTerm]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPackageSearchDebounced(packageSearchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [packageSearchTerm]);

  useEffect(() => {
    if (!organizationId) return;

    const searchTermForRequest = normalizePackageSearchValue(packageSearchDebounced);

    if (searchTermForRequest.length < 3) {
      setIsSearchingPackages(false);
      return;
    }

    if (searchTermForRequest !== normalizedPackageSearchTerm) return;

    let cancelled = false;
    const requestId = packageSearchRequestIdRef.current;
    const isLatestSearch = () => packageSearchRequestIdRef.current === requestId;
    let activeDone = false;
    let stagedDone = false;
    const finishIfDone = () => {
      if (!cancelled && isLatestSearch() && activeDone && stagedDone) {
        setIsSearchingPackages(false);
      }
    };
    setIsSearchingPackages(true);

    getPackagesTotals({
      organizationId,
      label: searchTermForRequest,
      page: 0,
      pageSize: 20,
    })
      .then((activeResponse) => {
        if (cancelled || !isLatestSearch()) return;

        const activeData = activeResponse?.data || activeResponse || [];
        const activePackages: PackageCoaOption[] = (Array.isArray(activeData) ? activeData : [])
          .map(mapActivePackageOption)
          .filter((p): p is PackageCoaOption => Boolean(p));

        setActivePackageResults(
          activePackages.filter((pkg) => packageOptionMatchesSearch(pkg, searchTermForRequest))
        );
      })
      .catch((err) => {
        console.error('[LabelEditor] Failed to search active packages:', err);
        if (!cancelled && isLatestSearch()) setActivePackageResults([]);
      })
      .finally(() => {
        activeDone = true;
        finishIfDone();
      });

    getPackagesStagedTotals({
          organizationId,
          label: searchTermForRequest,
          page: 0,
          pageSize: 20,
        })
        .then((stagedResponse) => {
          if (cancelled || !isLatestSearch()) return;

          const stagedData = stagedResponse?.data || [];
          const stagedPackages: PackageCoaOption[] = (Array.isArray(stagedData) ? stagedData : [])
            .map(mapStagedPackageOption)
            .filter((p): p is PackageCoaOption => Boolean(p));

          setStagedPackageResults(
            stagedPackages.filter((pkg) => packageOptionMatchesSearch(pkg, searchTermForRequest))
          );
        })
        .catch((err) => {
          console.error('[LabelEditor] Failed to search staged packages:', err);
          if (!cancelled && isLatestSearch()) setStagedPackageResults([]);
        })
        .finally(() => {
          stagedDone = true;
          finishIfDone();
        });

    return () => { cancelled = true; };
  }, [organizationId, packageSearchDebounced, normalizedPackageSearchTerm]);

  useEffect(() => {
    if (!isPackageDropdownOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (packageDropdownRef.current && !packageDropdownRef.current.contains(e.target as Node)) {
        setIsPackageDropdownOpen(false);
        setPackageSearchTerm('');
      }
    };
    const handleFocusOutside = (e: FocusEvent) => {
      if (packageDropdownRef.current && !packageDropdownRef.current.contains(e.target as Node)) {
        setIsPackageDropdownOpen(false);
        setPackageSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('focusin', handleFocusOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('focusin', handleFocusOutside);
    };
  }, [isPackageDropdownOpen]);

  const transparentPx = 'data:image/gif;base64,R0lGODlhAQABAAD/ACw=';
  // Compute effective label dimensions for custom layout (fall back to template size)
  const effectiveCustomLabelWidth =
    customLabelWidth ?? template.realWidth ?? template.width / templateDpi;
  const effectiveCustomLabelHeight =
    customLabelHeight ?? template.realHeight ?? template.height / templateDpi;
  const availablePreviewLayouts = useMemo<PreviewLayout[]>(
    () => [
      buildAutoLayout(template),
      ...previewLayouts,
      {
        id: 'custom',
        label: 'Custom...',
        unit: customUnit,
        pageWidth: customPageWidth ?? DEFAULT_CUSTOM_PAGE_WIDTH,
        pageHeight: customPageHeight ?? DEFAULT_CUSTOM_PAGE_HEIGHT,
        labelWidth: effectiveCustomLabelWidth,
        labelHeight: effectiveCustomLabelHeight,
        marginTop: customMarginTop,
        marginRight: customMarginRight,
        marginBottom: customMarginBottom,
        marginLeft: customMarginLeft,
        spacingX: customSpacingX,
        spacingY: customSpacingY,
        columns: customColumns,
        rows: customRows,
        format: customFormat,
        printer: 'custom',
        shape: customShape,
        labelRotation: customLabelRotation,
      },
    ],
    [
      previewLayouts,
      customPageWidth,
      customPageHeight,
      customUnit,
      effectiveCustomLabelWidth,
      effectiveCustomLabelHeight,
      customMarginTop,
      customMarginRight,
      customMarginBottom,
      customMarginLeft,
      customSpacingX,
      customSpacingY,
      customColumns,
      customRows,
      customFormat,
      customShape,
      customLabelRotation,
      template,
    ],
  );
  const sortedPreviewLayouts = useMemo(
    () =>
      availablePreviewLayouts
        .slice()
        .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })),
    [availablePreviewLayouts],
  );
  const filteredPreviewLayouts = useMemo(() => {
    const term = previewLayoutSearch.trim().toLowerCase();
    if (!term) return sortedPreviewLayouts;
    return sortedPreviewLayouts.filter((layout) => {
      const label = (layout.label || '').toLowerCase();
      const id = (layout.id || '').toLowerCase();
      return label.includes(term) || id.includes(term);
    });
  }, [sortedPreviewLayouts, previewLayoutSearch]);
  const [showRulers, setShowRulers] = useState<boolean>(() => loadEditorSettings().showRulers);
  const [snapToGrid, setSnapToGrid] = useState<boolean>(() => loadEditorSettings().snapToGrid);
  const [designZoom, setDesignZoom] = useState<number>(() => loadEditorSettings().designZoom);
  const baseDisplayScale = 72 / (template.dpi ?? 72);
  const effectiveZoom = designZoom * baseDisplayScale;
  const formatDef = getFormatOrDefault(template.outputFormat);
  const caps: FormatCapabilities = formatDef.capabilities;
  const rulerSize = 28;
  const rulerUnit = template.realUnit || 'in';
  const pxPerUnit = template.realWidth
    ? template.width / template.realWidth
    : realToPixels(1, rulerUnit as 'in' | 'cm', templateDpi);
  const effectiveRulerSize = showRulers ? rulerSize : 0;
  const minorStep = pxPerUnit / (rulerUnit === 'in' ? 8 : 10);
  const badgeScale = Math.max(0.4, Math.min(1, Math.min(template.width, template.height) / 480));
  const formatRulerLabel = (value: number) => {
    const rounded = Math.round(value * 10) / 10;
    return Number.isInteger(rounded) ? `${rounded}` : rounded.toFixed(1);
  };

  useEffect(() => {
    saveEditorSettings({ designZoom });
  }, [designZoom]);
  useEffect(() => {
    saveEditorSettings({ showOutlines });
  }, [showOutlines]);
  useEffect(() => {
    saveEditorSettings({ showRequiredBadges });
  }, [showRequiredBadges]);
  useEffect(() => {
    saveEditorSettings({ showRulers });
  }, [showRulers]);
  useEffect(() => {
    saveEditorSettings({ snapToGrid });
  }, [snapToGrid]);

  const handlePreviewLayoutChange = (layoutId: string) => {
    setPreviewSizeId(layoutId);
    applyTemplate((prev) => ({ ...prev, defaultLayoutId: layoutId }));
  };

  useEffect(() => {
    if (!isPreviewLayoutDropdownOpen) {
      setPreviewLayoutSearch('');
      return;
    }

    const focusTimer = window.setTimeout(() => {
      previewLayoutSearchInputRef.current?.focus();
      previewLayoutSearchInputRef.current?.select();
    }, 0);

    const handlePointerDown = (event: MouseEvent) => {
      if (!previewLayoutDropdownRef.current?.contains(event.target as Node)) {
        setIsPreviewLayoutDropdownOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsPreviewLayoutDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener('mousedown', handlePointerDown);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isPreviewLayoutDropdownOpen]);

  const horizontalTicks: React.ReactNode[] = [];
  const verticalTicks: React.ReactNode[] = [];
  const horizontalLabels: React.ReactNode[] = [];
  const verticalLabels: React.ReactNode[] = [];
  const tickTolerance = 0.02;

  React.useEffect(() => {
    let cancelled = false;
    loadPreviewLayouts(previewLayoutsEndpoint, defaultPreviewLayouts, { api })
      .then((layouts) => {
        if (cancelled) return;
        setPreviewLayouts(layouts);
        const desiredId = templateRef.current?.defaultLayoutId || DEFAULT_PREVIEW_LAYOUT_ID;
        const layoutIds = layouts.map((layout) => layout.id);
        const desiredExists =
          desiredId === AUTO_LAYOUT_ID || desiredId === 'custom' || layoutIds.includes(desiredId);
        const fallbackId =
          desiredId === AUTO_LAYOUT_ID ? AUTO_LAYOUT_ID : (layoutIds[0] ?? 'custom');
        setPreviewSizeId((current) => {
          if (desiredExists) return desiredId;
          if (current === AUTO_LAYOUT_ID) return AUTO_LAYOUT_ID;
          if (current === 'custom' || layoutIds.includes(current)) return current;
          return fallbackId;
        });
        if (
          !desiredExists &&
          desiredId !== AUTO_LAYOUT_ID &&
          desiredId !== 'custom' &&
          templateRef.current.defaultLayoutId !== fallbackId
        ) {
          setTemplate((prev) => ({ ...prev, defaultLayoutId: fallbackId }));
        }
      })
      .catch((err) => console.warn('Failed to load preview layouts', err));

    return () => {
      cancelled = true;
    };
  }, [previewLayoutsEndpoint, api]);

  // Compute a stable key for legacy image assets to trigger re-preload when they change
  const legacyImageAssetKey = useMemo(() => {
    return template.elements
      .filter((el): el is ImageElement => el.type === 'image' && !!el['image-asset']?.['mongo-id'])
      .flatMap((el) => normalizeLegacyIds(el['image-asset']!['mongo-id']))
      .sort()
      .join(',');
  }, [template.elements]);

  // Preload legacy assets (images and fonts) when template changes
  React.useEffect(() => {
    if (!legacyAssetResolver) return;
    // Skip if there are no legacy assets to preload
    if (!legacyImageAssetKey) return;

    let cancelled = false;
    setLegacyAssetsLoading(true);

    legacyAssetResolver
      .preloadTemplateAssets(template)
      .then(({ images, fonts }) => {
        if (cancelled) return;
        setLegacyImages(images);
        setLegacyFonts(fonts);
      })
      .catch((err) => {
        console.warn('[LabelEditor] Failed to preload legacy assets', err);
      })
      .finally(() => {
        if (!cancelled) {
          setLegacyAssetsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [legacyAssetResolver, template.id, legacyImageAssetKey]);

  const newImageAssetKey = useMemo(() => {
    const blobNames: string[] = [];
    for (const el of template.elements) {
      if (el.type === 'image') {
        const imgEl = el as ImageElement;
        if (imgEl.imageAsset?.blobName) {
          blobNames.push(imgEl.imageAsset.blobName);
        }
        if (imgEl.conditions) {
          for (const condition of imgEl.conditions) {
            if (condition.imageAsset?.blobName) {
              blobNames.push(condition.imageAsset.blobName);
            }
          }
        }
      }
    }
    return blobNames.sort().join(',');
  }, [template.elements]);

  const newFontAssetKey = useMemo(() => {
    return template.elements
      .filter((el) => el.type === 'text' && (el as TextElement).fontAsset?.blobName)
      .map((el) => (el as TextElement).fontAsset!.blobName)
      .sort()
      .join(',');
  }, [template.elements]);

  React.useEffect(() => {
    if (!api || !organizationId) return;
    if (!newImageAssetKey && !newFontAssetKey) return;

    let cancelled = false;

    preloadNewAssets(template, api as unknown as AssetApi, organizationId)
      .then(({ images, fonts }) => {
        if (cancelled) return;
        setNewAssetImages(images);
        setNewAssetFonts(fonts);
      })
      .catch((err) => {
        console.warn('[LabelEditor] Failed to preload new assets', err);
      });

    return () => {
      cancelled = true;
    };
  }, [api, organizationId, template.id, newImageAssetKey, newFontAssetKey]);

  React.useEffect(() => {
    setLabelCountInput(labelCount);
  }, [labelCount]);

  React.useEffect(() => {
    const persistedLabelCount = toPersistedNumber(normalizedInitialTemplate.defaultLabelCount);
    const nextLabelCount = getDefaultLabelCount(persistedLabelCount);
    setLabelCount(nextLabelCount);
    setLabelCountInput(nextLabelCount);
    setTemplate((prev) => {
      if (persistedLabelCount !== undefined && persistedLabelCount >= 1) {
        return prev.defaultLabelCount === nextLabelCount
          ? prev
          : { ...prev, defaultLabelCount: nextLabelCount };
      }

      if (prev.defaultLabelCount === undefined) return prev;
      const { defaultLabelCount: _, ...templateWithoutLabelCount } = prev;
      return templateWithoutLabelCount;
    });
  }, [normalizedInitialTemplate.defaultLabelCount]);

  // Sync custom layout values to template when custom layout is selected
  React.useEffect(() => {
    if (previewSizeId !== 'custom') return;
    applyTemplate((prev) => ({
      ...prev,
      customLayout: {
        unit: customUnit,
        pageWidth: customPageWidth ?? DEFAULT_CUSTOM_PAGE_WIDTH,
        pageHeight: customPageHeight ?? DEFAULT_CUSTOM_PAGE_HEIGHT,
        labelWidth: customLabelWidth,
        labelHeight: customLabelHeight,
        marginTop: customMarginTop,
        marginRight: customMarginRight,
        marginBottom: customMarginBottom,
        marginLeft: customMarginLeft,
        spacingX: customSpacingX,
        spacingY: customSpacingY,
        columns: customColumns,
        rows: customRows,
        format: customFormat,
        shape: customShape,
        labelRotation: customLabelRotation,
      },
    }));
  }, [
    previewSizeId,
    customUnit,
    customPageWidth,
    customPageHeight,
    customLabelWidth,
    customLabelHeight,
    customMarginTop,
    customMarginRight,
    customMarginBottom,
    customMarginLeft,
    customSpacingX,
    customSpacingY,
    customColumns,
    customRows,
    customFormat,
    customShape,
    customLabelRotation,
  ]);

  //sync print alignment offsets and page rotation to template
  React.useEffect(() => {
    applyTemplate((prev) => ({
      ...prev,
      topOffset: previewTopOffset,
      leftOffset: previewLeftOffset,
      pageRotation,
    }));
  }, [previewTopOffset, previewLeftOffset, pageRotation]);

  for (let pos = 0; pos <= template.width + 0.5; pos += minorStep) {
    const unitValue = pos / pxPerUnit;
    const isMajor = Math.abs(unitValue - Math.round(unitValue)) < tickTolerance;
    const isHalf = !isMajor && Math.abs(unitValue * 2 - Math.round(unitValue * 2)) < tickTolerance;
    const height = isMajor ? 14 : isHalf ? 10 : 6;
    const key = `hx-${Math.round(pos * 1000)}`;
    horizontalTicks.push(
      <div
        key={key}
        style={{
          position: 'absolute',
          left: pos,
          bottom: 0,
          width: 1,
          height,
          background: palette.text,
          opacity: isMajor ? 0.9 : 0.5,
        }}
      />,
    );
    if (isMajor) {
      horizontalLabels.push(
        <div
          key={`hl-${key}`}
          style={{
            position: 'absolute',
            left: pos + 2,
            top: 2,
            fontSize: 10,
            color: palette.muted,
            userSelect: 'none',
          }}
        >
          {formatRulerLabel(unitValue)}
        </div>,
      );
    }
  }

  for (let pos = 0; pos <= template.height + 0.5; pos += minorStep) {
    const unitValue = pos / pxPerUnit;
    const isMajor = Math.abs(unitValue - Math.round(unitValue)) < tickTolerance;
    const isHalf = !isMajor && Math.abs(unitValue * 2 - Math.round(unitValue * 2)) < tickTolerance;
    const width = isMajor ? 14 : isHalf ? 10 : 6;
    const key = `vy-${Math.round(pos * 1000)}`;
    verticalTicks.push(
      <div
        key={key}
        style={{
          position: 'absolute',
          right: 0,
          top: pos,
          height: 1,
          width,
          background: palette.text,
          opacity: isMajor ? 0.9 : 0.5,
        }}
      />,
    );
    if (isMajor) {
      verticalLabels.push(
        <div
          key={`vl-${key}`}
          style={{
            position: 'absolute',
            right: 4,
            top: pos + 2,
            fontSize: 10,
            color: palette.muted,
            userSelect: 'none',
            transform: 'translateY(-50%)',
          }}
        >
          {formatRulerLabel(unitValue)}
        </div>,
      );
    }
  }

  const renderPreview = () => {
    if (!caps.supportsPageLayout) {
      const labelData: Record<string, unknown> = {
        ...formatPreviewFullData,
        qrValue: qrValues[0] || 'SAMPLE-QR-001',
      };
      const resolvers: FormatResolvers = {
        formatTextContent: (el, data) => formatTextContent(el, data as Record<string, any>),
        resolveImageUrl: (el, data) => resolveImageUrl(el, data as Record<string, any>),
        resolveBarcodeData: (el, data) => resolveBarcodeData(el, data as Record<string, any>),
        fetchImageBytes: async (url: string) => {
          try {
            const resp = await fetch(url);
            if (!resp.ok) return undefined;
            const buf = await resp.arrayBuffer();
            return new Uint8Array(buf);
          } catch {
            return undefined;
          }
        },
      };
      const FormatPreview = formatDef.PreviewPanel;
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ fontSize: 12, color: palette.muted }}>{formatDef.displayName} Preview</div>
          <React.Suspense fallback={<span style={{ fontSize: 12, color: palette.muted }}>Loading preview...</span>}>
            <FormatPreview template={template} labelData={labelData} resolvers={resolvers} />
          </React.Suspense>
        </div>
      );
    }

    const layoutForMax = computeLayout(1);
    const maxLabels = layoutForMax.labelsPerPage;
    const previewCount = Math.min(Math.max(1, labelCountInput), maxLabels);
    const previewPlan = computeLayout(previewCount);
    const printPlan = computeLayout(labelCount);
    const selectedLayout = previewPlan.selectedLayout || availablePreviewLayouts[0];
    const unit = previewPlan.unit || 'in';
    const displayWidth = previewPlan.pageWidth;
    const displayHeight = previewPlan.pageHeight;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
              flexWrap: 'wrap',
            }}
          >
            <div>
              <div style={{ fontSize: 12, color: palette.muted }}>Print Preview</div>
              <div style={{ fontWeight: 700, color: palette.text }}>
                {previewSizeId === 'custom'
                  ? `Custom ${displayWidth.toFixed(2)} x ${displayHeight.toFixed(2)} ${unit}`
                  : selectedLayout?.label}
              </div>
              <div style={{ fontSize: 12, color: palette.muted }}>
                Labels/page: {printPlan.labelsPerPage} · Pages: {printPlan.pageCount}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <label
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 12,
                  color: palette.text,
                }}
              >
                <span style={{ color: palette.muted }}>Layout</span>
                <div ref={previewLayoutDropdownRef} style={{ position: 'relative', minWidth: 220 }}>
                  <button
                    id={previewLayoutSelectId}
                    type="button"
                    aria-haspopup="listbox"
                    aria-expanded={isPreviewLayoutDropdownOpen}
                    aria-controls={`${previewLayoutSelectId}-menu`}
                    onClick={() => setIsPreviewLayoutDropdownOpen((prev) => !prev)}
                    style={{
                      padding: 8,
                      borderRadius: 8,
                      border: `1px solid ${palette.border}`,
                      background: palette.cardBg,
                      color: palette.text,
                      minWidth: 220,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 8,
                      textAlign: 'left',
                      cursor: 'pointer',
                    }}
                  >
                    <span>
                      {availablePreviewLayouts.find((layout) => layout.id === previewSizeId)?.label ||
                        'Select layout'}
                    </span>
                    <span style={{ color: palette.muted }} aria-hidden="true">
                      {isPreviewLayoutDropdownOpen ? '▲' : '▼'}
                    </span>
                  </button>

                  {isPreviewLayoutDropdownOpen && (
                    <div
                      id={`${previewLayoutSelectId}-menu`}
                      role="listbox"
                      aria-label="Layout options"
                      style={{
                        position: 'absolute',
                        top: 'calc(100% + 6px)',
                        left: 0,
                        right: 0,
                        zIndex: 30,
                        padding: 8,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 8,
                        borderRadius: 8,
                        border: `1px solid ${palette.border}`,
                        background: palette.cardBg,
                        boxShadow: '0 12px 32px rgba(7, 24, 34, 0.16)',
                      }}
                    >
                      <input
                        ref={previewLayoutSearchInputRef}
                        type="text"
                        placeholder="Search layouts"
                        value={previewLayoutSearch}
                        onChange={(e) => setPreviewLayoutSearch(e.target.value)}
                        aria-label="Search layouts"
                        onKeyDown={(e) => e.stopPropagation()}
                        style={{
                          padding: 8,
                          borderRadius: 8,
                          border: `1px solid ${palette.border}`,
                          background: palette.cardAlt,
                          color: palette.text,
                        }}
                      />
                      <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                        {filteredPreviewLayouts.length === 0 ? (
                          <div style={{ padding: 8, fontSize: 12, color: palette.muted }}>
                            No layouts match your search.
                          </div>
                        ) : (
                          filteredPreviewLayouts.map((opt) => {
                            const isSelected = opt.id === previewSizeId;
                            return (
                              <button
                                key={opt.id}
                                type="button"
                                role="option"
                                aria-selected={isSelected}
                                onClick={() => {
                                  handlePreviewLayoutChange(opt.id);
                                  setIsPreviewLayoutDropdownOpen(false);
                                }}
                                style={{
                                  width: '100%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  gap: 8,
                                  padding: '10px',
                                  marginBottom: 6,
                                  borderRadius: 8,
                                  border: `1px solid ${isSelected ? palette.text : palette.border}`,
                                  background: isSelected ? palette.cardAlt : palette.cardBg,
                                  color: palette.text,
                                  textAlign: 'left',
                                  cursor: 'pointer',
                                }}
                              >
                                <span>{opt.label}</span>
                                {isSelected ? (
                                  <span style={{ color: palette.muted, fontSize: 12 }}>Selected</span>
                                ) : null}
                              </button>
                            );
                          })
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </label>
              {previewSizeId === 'custom' && (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                    padding: 12,
                    background: palette.cardAlt,
                    borderRadius: 8,
                    border: `1px solid ${palette.border}`,
                  }}
                >
                  {/* Page Size Row */}
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span
                      style={{ fontSize: 12, fontWeight: 600, color: palette.text, minWidth: 80 }}
                    >
                      Page
                    </span>
                    <label
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        fontSize: 12,
                        color: palette.text,
                      }}
                    >
                      <span style={{ color: palette.muted }}>W</span>
                      <DimensionInput
                        label="Page width"
                        value={customPageWidth}
                        onCommit={setCustomPageWidth}
                        minimum={MIN_CUSTOM_DIMENSION}
                        emptyFallback={DEFAULT_CUSTOM_PAGE_WIDTH}
                      />
                    </label>
                    <label
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        fontSize: 12,
                        color: palette.text,
                      }}
                    >
                      <span style={{ color: palette.muted }}>H</span>
                      <DimensionInput
                        label="Page height"
                        value={customPageHeight}
                        onCommit={setCustomPageHeight}
                        minimum={MIN_CUSTOM_DIMENSION}
                        emptyFallback={DEFAULT_CUSTOM_PAGE_HEIGHT}
                      />
                    </label>
                    <select
                      value={customUnit}
                      onChange={(e) => setCustomUnit(e.target.value as 'in' | 'cm')}
                      style={{
                        padding: '4px 6px',
                        borderRadius: 6,
                        border: `1px solid ${palette.border}`,
                        background: palette.cardBg,
                        color: palette.text,
                        fontSize: 12,
                      }}
                    >
                      <option value="in">in</option>
                      <option value="cm">cm</option>
                    </select>
                    <select
                      value={customFormat}
                      onChange={(e) => setCustomFormat(e.target.value as 'sheets' | 'roll')}
                      style={{
                        padding: '4px 6px',
                        borderRadius: 6,
                        border: `1px solid ${palette.border}`,
                        background: palette.cardBg,
                        color: palette.text,
                        fontSize: 12,
                      }}
                    >
                      <option value="sheets">Sheets</option>
                      <option value="roll">Roll</option>
                    </select>
                  </div>
                  {/* Label Size Row */}
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span
                      style={{ fontSize: 12, fontWeight: 600, color: palette.text, minWidth: 80 }}
                    >
                      Label
                    </span>
                    <label
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        fontSize: 12,
                        color: palette.text,
                      }}
                    >
                      <span style={{ color: palette.muted }}>W</span>
                      {/* The inherited size is a placeholder, not a value, so it cannot overwrite a half-typed override. */}
                      <DimensionInput
                        label="Label width"
                        value={customLabelWidth}
                        onCommit={setCustomLabelWidth}
                        minimum={MIN_CUSTOM_DIMENSION}
                        emptyFallback={undefined}
                        placeholder={formatDimensionHint(effectiveCustomLabelWidth)}
                      />
                    </label>
                    <label
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        fontSize: 12,
                        color: palette.text,
                      }}
                    >
                      <span style={{ color: palette.muted }}>H</span>
                      <DimensionInput
                        label="Label height"
                        value={customLabelHeight}
                        onCommit={setCustomLabelHeight}
                        minimum={MIN_CUSTOM_DIMENSION}
                        emptyFallback={undefined}
                        placeholder={formatDimensionHint(effectiveCustomLabelHeight)}
                      />
                    </label>
                    <select
                      value={customShape}
                      onChange={(e) =>
                        setCustomShape(e.target.value as 'rectangle' | 'round-rectangle' | 'circle')
                      }
                      style={{
                        padding: '4px 6px',
                        borderRadius: 6,
                        border: `1px solid ${palette.border}`,
                        background: palette.cardBg,
                        color: palette.text,
                        fontSize: 12,
                      }}
                    >
                      <option value="rectangle">Rectangle</option>
                      <option value="round-rectangle">Rounded</option>
                      <option value="circle">Circle</option>
                    </select>
                    <select
                      value={customLabelRotation}
                      onChange={(e) =>
                        setCustomLabelRotation(Number(e.target.value) as 0 | 90 | 180 | 270)
                      }
                      style={{
                        padding: '4px 6px',
                        borderRadius: 6,
                        border: `1px solid ${palette.border}`,
                        background: palette.cardBg,
                        color: palette.text,
                        fontSize: 12,
                      }}
                    >
                      <option value={0}>0°</option>
                      <option value={90}>90°</option>
                      <option value={180}>180°</option>
                      <option value={270}>270°</option>
                    </select>
                  </div>
                  {/* Grid Row */}
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span
                      style={{ fontSize: 12, fontWeight: 600, color: palette.text, minWidth: 80 }}
                    >
                      Grid
                    </span>
                    <label
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        fontSize: 12,
                        color: palette.text,
                      }}
                    >
                      <span style={{ color: palette.muted }}>Cols</span>
                      <input
                        type="number"
                        min={1}
                        step={1}
                        value={customColumns ?? ''}
                        placeholder="auto"
                        onChange={(e) =>
                          setCustomColumns(
                            e.target.value
                              ? Math.max(1, Math.round(Number(e.target.value)))
                              : undefined,
                          )
                        }
                        style={{
                          width: 60,
                          padding: '4px 6px',
                          borderRadius: 6,
                          border: `1px solid ${palette.border}`,
                          background: palette.cardBg,
                          color: palette.text,
                          fontSize: 12,
                        }}
                      />
                    </label>
                    <label
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        fontSize: 12,
                        color: palette.text,
                      }}
                    >
                      <span style={{ color: palette.muted }}>Rows</span>
                      <input
                        type="number"
                        min={1}
                        step={1}
                        value={customRows ?? ''}
                        placeholder="auto"
                        onChange={(e) =>
                          setCustomRows(
                            e.target.value
                              ? Math.max(1, Math.round(Number(e.target.value)))
                              : undefined,
                          )
                        }
                        style={{
                          width: 60,
                          padding: '4px 6px',
                          borderRadius: 6,
                          border: `1px solid ${palette.border}`,
                          background: palette.cardBg,
                          color: palette.text,
                          fontSize: 12,
                        }}
                      />
                    </label>
                    <label
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        fontSize: 12,
                        color: palette.text,
                      }}
                    >
                      <span style={{ color: palette.muted }}>Gap X</span>
                      <input
                        type="number"
                        min={0}
                        step={customUnit === 'in' ? 0.01 : 0.1}
                        value={customSpacingX}
                        onChange={(e) => setCustomSpacingX(Math.max(0, Number(e.target.value)))}
                        style={{
                          width: 60,
                          padding: '4px 6px',
                          borderRadius: 6,
                          border: `1px solid ${palette.border}`,
                          background: palette.cardBg,
                          color: palette.text,
                          fontSize: 12,
                        }}
                      />
                    </label>
                    <label
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        fontSize: 12,
                        color: palette.text,
                      }}
                    >
                      <span style={{ color: palette.muted }}>Gap Y</span>
                      <input
                        type="number"
                        min={0}
                        step={customUnit === 'in' ? 0.01 : 0.1}
                        value={customSpacingY}
                        onChange={(e) => setCustomSpacingY(Math.max(0, Number(e.target.value)))}
                        style={{
                          width: 60,
                          padding: '4px 6px',
                          borderRadius: 6,
                          border: `1px solid ${palette.border}`,
                          background: palette.cardBg,
                          color: palette.text,
                          fontSize: 12,
                        }}
                      />
                    </label>
                  </div>
                  {/* Margins Row */}
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span
                      style={{ fontSize: 12, fontWeight: 600, color: palette.text, minWidth: 80 }}
                    >
                      Margins
                    </span>
                    <label
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        fontSize: 12,
                        color: palette.text,
                      }}
                    >
                      <span style={{ color: palette.muted }}>T</span>
                      <input
                        type="number"
                        min={0}
                        step={customUnit === 'in' ? 0.01 : 0.1}
                        value={customMarginTop}
                        onChange={(e) => setCustomMarginTop(Math.max(0, Number(e.target.value)))}
                        style={{
                          width: 55,
                          padding: '4px 6px',
                          borderRadius: 6,
                          border: `1px solid ${palette.border}`,
                          background: palette.cardBg,
                          color: palette.text,
                          fontSize: 12,
                        }}
                      />
                    </label>
                    <label
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        fontSize: 12,
                        color: palette.text,
                      }}
                    >
                      <span style={{ color: palette.muted }}>R</span>
                      <input
                        type="number"
                        min={0}
                        step={customUnit === 'in' ? 0.01 : 0.1}
                        value={customMarginRight}
                        onChange={(e) => setCustomMarginRight(Math.max(0, Number(e.target.value)))}
                        style={{
                          width: 55,
                          padding: '4px 6px',
                          borderRadius: 6,
                          border: `1px solid ${palette.border}`,
                          background: palette.cardBg,
                          color: palette.text,
                          fontSize: 12,
                        }}
                      />
                    </label>
                    <label
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        fontSize: 12,
                        color: palette.text,
                      }}
                    >
                      <span style={{ color: palette.muted }}>B</span>
                      <input
                        type="number"
                        min={0}
                        step={customUnit === 'in' ? 0.01 : 0.1}
                        value={customMarginBottom}
                        onChange={(e) => setCustomMarginBottom(Math.max(0, Number(e.target.value)))}
                        style={{
                          width: 55,
                          padding: '4px 6px',
                          borderRadius: 6,
                          border: `1px solid ${palette.border}`,
                          background: palette.cardBg,
                          color: palette.text,
                          fontSize: 12,
                        }}
                      />
                    </label>
                    <label
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        fontSize: 12,
                        color: palette.text,
                      }}
                    >
                      <span style={{ color: palette.muted }}>L</span>
                      <input
                        type="number"
                        min={0}
                        step={customUnit === 'in' ? 0.01 : 0.1}
                        value={customMarginLeft}
                        onChange={(e) => setCustomMarginLeft(Math.max(0, Number(e.target.value)))}
                        style={{
                          width: 55,
                          padding: '4px 6px',
                          borderRadius: 6,
                          border: `1px solid ${palette.border}`,
                          background: palette.cardBg,
                          color: palette.text,
                          fontSize: 12,
                        }}
                      />
                    </label>
                  </div>
                </div>
              )}
              <label
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 12,
                  color: palette.text,
                }}
              >
                <span style={{ color: palette.muted }}>Total labels</span>
                <input
                  type="number"
                  min={1}
                  step={1}
                  value={labelCountInput}
                  onChange={(e) => updateLabelCount(e.target.value)}
                  style={{
                    width: 90,
                    padding: '6px 8px',
                    borderRadius: 8,
                    border: `1px solid ${palette.border}`,
                    background: palette.cardBg,
                    color: palette.text,
                  }}
                />
              </label>
              <button
                onClick={generatePdfPreview}
                disabled={isGeneratingPreview || isPrinting}
                className="wysiwyg-chip secondary"
                style={{
                  opacity: isGeneratingPreview || isPrinting ? 0.6 : 1,
                  cursor: isGeneratingPreview || isPrinting ? 'not-allowed' : 'pointer',
                }}
              >
                {isGeneratingPreview ? 'Generating…' : 'Generate Preview'}
              </button>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <label
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 12,
                color: palette.text,
              }}
            >
              <span style={{ color: palette.muted }}>Top offset</span>
              <input
                type="number"
                min={0}
                step={rulerUnit === 'in' ? 0.01 : 0.05}
                value={previewTopOffset}
                onChange={(e) => setPreviewTopOffset(Math.max(0, Number(e.target.value)))}
                style={{
                  width: 80,
                  padding: '6px 8px',
                  borderRadius: 8,
                  border: `1px solid ${palette.border}`,
                  background: palette.cardBg,
                  color: palette.text,
                }}
              />
              <span>{rulerUnit}</span>
            </label>
            <label
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 12,
                color: palette.text,
              }}
            >
              <span style={{ color: palette.muted }}>Left offset</span>
              <input
                type="number"
                min={0}
                step={rulerUnit === 'in' ? 0.01 : 0.05}
                value={previewLeftOffset}
                onChange={(e) => setPreviewLeftOffset(Math.max(0, Number(e.target.value)))}
                style={{
                  width: 80,
                  padding: '6px 8px',
                  borderRadius: 8,
                  border: `1px solid ${palette.border}`,
                  background: palette.cardBg,
                  color: palette.text,
                }}
              />
              <span>{rulerUnit}</span>
            </label>
            <label
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 12,
                color: palette.text,
              }}
            >
              <span style={{ color: palette.muted }}>Page rotation</span>
              <select
                value={pageRotation}
                onChange={(e) => setPageRotation(Number(e.target.value) as 0 | 90 | 180 | 270)}
                style={{
                  padding: '6px 8px',
                  borderRadius: 8,
                  border: `1px solid ${palette.border}`,
                  background: palette.cardBg,
                  color: palette.text,
                  minWidth: 80,
                }}
              >
                <option value={0}>0°</option>
                <option value={90}>90°</option>
                <option value={180}>180°</option>
                <option value={270}>270°</option>
              </select>
            </label>
          </div>
        </div>
        <div
          style={{
            overflow: 'auto',
            border: `1px dashed ${palette.border}`,
            borderRadius: 12,
            padding: 12,
            background: palette.cardBg,
            minHeight: 500,
          }}
        >
          {isGeneratingPreview && (
            <div role="status" style={{ color: palette.muted, marginBottom: 8 }}>
              {pdfPreviewUrl ? 'Updating PDF preview… Showing the previous preview.' : 'Generating PDF preview…'}
            </div>
          )}
          {pdfPreviewUrl ? (
            <iframe
              src={`${pdfPreviewUrl}${pdfPreviewUrl.includes('#') ? '&' : '#'}zoom=300`}
              style={{ width: '100%', minHeight: 800, border: 'none', borderRadius: 8 }}
              title="PDF Preview"
            />
          ) : isGeneratingPreview ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: 500,
                color: palette.muted,
              }}
            >
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 14, marginBottom: 8 }}>Generating PDF preview...</div>
                <div style={{ fontSize: 12 }}>This may take a moment for large label counts</div>
              </div>
            </div>
          ) : (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: 500,
                color: palette.muted,
              }}
            >
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 14, marginBottom: 8 }}>No preview available</div>
                <div style={{ fontSize: 12 }}>Click a button below to generate preview</div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Delegate to the shared computeLayout service (single source of truth for layout computation).
  // This wrapper resolves the selected layout from component state and forwards to the service.
  function computeLayout(totalLabels: number = labelCount) {
    const selectedLayout =
      availablePreviewLayouts.find((layout) => layout.id === previewSizeId) ||
      availablePreviewLayouts[0];
    return computeLayoutService({
      layout: selectedLayout,
      labelCount: totalLabels,
      templateWidth: template.width,
      templateHeight: template.height,
      topOffset: previewTopOffset,
      leftOffset: previewLeftOffset,
      rotation: pageRotation,
    });
  }

  const revokePdfUrl = (url: string | null) => {
    if (!url) return;
    try {
      URL.revokeObjectURL(url);
    } catch {
      // URL cleanup is best-effort in browsers without object URL support.
    }
  };

  const generatePdfPreview = async () => {
    if (pdfOperationRef.current === 'export') return;
    const requestId = ++labelDataRequestIdRef.current;
    pdfOperationRef.current = 'preview';
    setIsGeneratingPreview(true);
    setPdfError(null);

    try {
      // Wait for legacy assets to finish loading before generating preview
      if (legacyAssetsLoadingRef.current) {
        const startTime = Date.now();
        const maxWait = 30000; // 30 seconds max
        while (legacyAssetsLoadingRef.current && Date.now() - startTime < maxWait) {
          await new Promise((r) => setTimeout(r, 100));
          if (requestId !== labelDataRequestIdRef.current) return;
        }
      }
      const selectedLayout =
        availablePreviewLayouts.find((layout) => layout.id === previewSizeId) ||
        availablePreviewLayouts[0];
      const countMax = computeLayout(1).labelsPerPage;
      const effectiveCount = Math.min(Math.max(1, labelCountInput), countMax);

      const labelData = await fetchLabelData({ requiredLabels: effectiveCount, requestId });
      if (requestId !== labelDataRequestIdRef.current) return;

      const result = await generateLabelPdf({
        template,
        layout: selectedLayout,
        labelCount: effectiveCount,
        rotation: pageRotation,
        topOffset: previewTopOffset,
        leftOffset: previewLeftOffset,
        qrIndexOffset,
        data: buildPdfGenerationData(labelData, mockDataBundle, coaData.coa ?? mockDataBundle.coa),
        legacyImages: legacyImagesRef.current,
        legacyFonts: legacyFontsRef.current,
        newAssetImages: newAssetImagesRef.current,
        newAssetFonts: newAssetFontsRef.current,
        isPreview: true,
      });

      if (requestId !== labelDataRequestIdRef.current) {
        revokePdfUrl(result.url);
        return;
      }
      setPdfPreviewUrl(result.url);
    } catch (err) {
      if (requestId !== labelDataRequestIdRef.current) return;
      console.error('PDF preview generation failed', err);
      setPdfError(err instanceof Error ? err.message : 'PDF preview generation failed. Please try again.');
    } finally {
      if (requestId === labelDataRequestIdRef.current) {
        pdfOperationRef.current = null;
        setIsGeneratingPreview(false);
      }
    }
  };

  const handleExportPdf = async () => {
    if (pdfOperationRef.current === 'export') return;
    const requestId = ++labelDataRequestIdRef.current;
    pdfOperationRef.current = 'export';
    setIsGeneratingPreview(false);
    setIsPrinting(true);
    setPdfError(null);
    try {
      const effectiveCount = Math.max(1, labelCountInput);
      updateLabelCount(String(effectiveCount));

      // Wait for legacy assets to finish loading before generating PDF
      if (legacyAssetsLoadingRef.current) {
        // Poll until loading completes (with timeout)
        const startTime = Date.now();
        const maxWait = 30000; // 30 seconds max
        while (legacyAssetsLoadingRef.current && Date.now() - startTime < maxWait) {
          await new Promise((r) => setTimeout(r, 100));
          if (requestId !== labelDataRequestIdRef.current) return;
        }
        if (legacyAssetsLoadingRef.current) {
          console.warn('[handleExportPdf] Legacy assets still loading after timeout');
        }
      }

      const labelData = await fetchLabelData({ requiredLabels: effectiveCount, requestId });
      if (requestId !== labelDataRequestIdRef.current) return;

      const selectedLayout =
        availablePreviewLayouts.find((layout) => layout.id === previewSizeId) ||
        availablePreviewLayouts[0];

      const result = await generateLabelPdf({
        template,
        layout: selectedLayout,
        labelCount: effectiveCount,
        rotation: pageRotation,
        topOffset: previewTopOffset,
        leftOffset: previewLeftOffset,
        qrIndexOffset,
        data: buildPdfGenerationData(labelData, mockDataBundle, coaData.coa ?? mockDataBundle.coa),
        legacyImages: legacyImagesRef.current,
        legacyFonts: legacyFontsRef.current,
        newAssetImages: newAssetImagesRef.current,
        newAssetFonts: newAssetFontsRef.current,
        isPreview: false,
      });

      if (requestId === labelDataRequestIdRef.current && result.url) {
        window.open(result.url, '_blank');
      } else {
        revokePdfUrl(result.url);
      }
    } catch (err) {
      if (requestId === labelDataRequestIdRef.current) {
        console.error(err);
        setPdfError(err instanceof Error ? err.message : 'PDF generation failed. Please try again.');
      }
    } finally {
      if (requestId === labelDataRequestIdRef.current) {
        pdfOperationRef.current = null;
        setIsPrinting(false);
      }
    }
  };
  const [dragState, setDragState] = useState<{
    offsets: Record<string, { offsetX: number; offsetY: number }>;
    primaryId: string;
  } | null>(null);
  const [marqueeState, setMarqueeState] = useState<{
    startX: number;
    startY: number;
    currentX: number;
    currentY: number;
    additive: boolean;
  } | null>(null);
  const [resizeState, setResizeState] = useState<{
    id: string;
    handle: 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';
    startX: number;
    startY: number;
    startWidth: number;
    startHeight: number;
    startLeft: number;
    startTop: number;
    startFontSize?: number;
    startInnerTextHeight?: number;
    startQrMargin?: number;
  } | null>(null);
  const [vertexDragState, setVertexDragState] = useState<{
    id: string;
    vertexKey: 'v1' | 'v2' | 'v3' | 'v4';
    startMouseX: number;
    startMouseY: number;
    startVertex: ShapeVertex;
  } | null>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const historyRef = useRef<Template[]>([]);
  const redoRef = useRef<Template[]>([]);
  const clipboardRef = useRef<LabelElement[]>([]);
  const templateRef = useRef<Template>(normalizedInitialTemplate);
  const measureCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // Image upload disabled - legacy images loaded from backend
  // const imageUploadInputRef = useRef<HTMLInputElement | null>(null);

  const selectedElements = template.elements.filter((el: LabelElement) => selectedIds.has(el.id));
  const selected = selectedElements[0] || null;
  const selectedTextBinding = selected?.type === 'text' ? selected.variableKey : undefined;
  const hasUndersizedSelectedQr = selectedElements.some(
    (element) =>
      element.type === 'qr' && Math.min(element.width ?? 0, element.height ?? 0) < 37,
  );

  const selectElement = (id: string, additive: boolean) => {
    if (additive) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    } else {
      setSelectedIds(new Set([id]));
    }
  };

  const clearSelection = () => setSelectedIds(new Set());

  const selectMultiple = (ids: string[]) => setSelectedIds(new Set(ids));

  useEffect(() => {
    if (pendingInsertions.length === 0) return;

    const committedIds = new Set(template.elements.map((element) => element.id));
    const insertedIds = pendingInsertions
      .flatMap((insertion) => insertion.elementIds)
      .filter((id) => committedIds.has(id));
    const blockedQr = pendingInsertions.some((insertion) =>
      insertion.qrIds.some((id) => !committedIds.has(id)),
    );

    if (insertedIds.length > 0) selectMultiple(insertedIds);
    if (blockedQr) {
      showToastWarning('Only one Retail ID QR code can be added per template.');
    }
    setPendingInsertions([]);
  }, [pendingInsertions, template.elements]);

  const getCommonValue = <T,>(
    elements: LabelElement[],
    getter: (el: LabelElement) => T,
  ): T | undefined => {
    if (elements.length === 0) return undefined;
    const first = getter(elements[0]);
    const allSame = elements.every((el) => getter(el) === first);
    return allSame ? first : undefined;
  };

  const updateSelectedElements = (
    updater: (el: LabelElement) => LabelElement,
    options?: { skipHistory?: boolean },
  ) => {
    applyTemplate(
      (prev: Template) => ({
        ...prev,
        elements: prev.elements.map((el: LabelElement) =>
          selectedIds.has(el.id) ? updater(el) : el,
        ),
      }),
      options,
    );
  };

  const updateSelectedOfType = <T extends LabelElement>(
    elementType: LabelElement['type'],
    updater: (el: T) => T,
    options?: { skipHistory?: boolean },
  ) => {
    applyTemplate(
      (prev: Template) => ({
        ...prev,
        elements: prev.elements.map((el: LabelElement) =>
          selectedIds.has(el.id) && el.type === elementType ? updater(el as T) : el,
        ),
      }),
      options,
    );
  };

  const getCommonValueOfType = <T extends LabelElement, V>(
    elementType: LabelElement['type'],
    getter: (el: T) => V,
  ): V | undefined => {
    const elementsOfType = selectedElements.filter((el) => el.type === elementType) as T[];
    if (elementsOfType.length === 0) return undefined;
    const first = getter(elementsOfType[0]);
    const allSame = elementsOfType.every((el) => getter(el) === first);
    return allSame ? first : undefined;
  };

  const storageKey = (name: string) => `label-template:${name || 'unnamed'}`;

  const themeVariables = template.themeVariables || [];
  const scopedLabelVariables = useMemo(() => labelVariablesForContext({
    marketCodes: [
      ...availableMarkets.filter(market => activeMarketIds.includes(market.id)).map(market => market.code),
      selectedPackageOption?.marketCode,
      resolvedCoaData?.marketCode,
    ],
    coa: coaData.coa ?? resolvedCoaData,
  }), [availableMarkets, activeMarketIds, selectedPackageOption?.marketCode, resolvedCoaData, coaData.coa]);

  const conditionVariables = useMemo<ConditionVariableOption[]>(() => {
    const themeOptions = (themeVariables || []).map((v) => ({
      key: v.key,
      label: v.name,
      type: normalizeConditionType(v.type),
      source: 'theme' as const,
    }));

    const componentOptions = scopedLabelVariables.map((v) => ({
      key: v.key,
      label: friendlyVariableName(v.key),
      type: normalizeConditionType(v.type),
      source: 'component' as const,
    }));

    return [...themeOptions, ...componentOptions];
  }, [themeVariables, scopedLabelVariables]);

  const conditionTypeByKey = useMemo(() => {
    return buildConditionTypeByKey(themeVariables);
  }, [themeVariables]);

  const themeConditionVariables = useMemo(
    () => conditionVariables.filter((v) => v.source === 'theme'),
    [conditionVariables],
  );

  const componentConditionVariables = useMemo(
    () => conditionVariables.filter((v) => v.source === 'component'),
    [conditionVariables],
  );

  const themeData = useMemo(() => themeVariablesToDataMap(themeVariables), [themeVariables]);

  // Raw data context for second-pass variable resolution in legacy templates.
  // This provides nested data sources for resolving prefixed variables like
  // {{theme.netWeight}}, {{coa.batch}}, {{job.packageTag}}, etc.
  // Uses prepareRenderData for consistency with pdfGenerator.
  const rawDataContext = useMemo((): RawDataContext => {
    const { rawDataContext: ctx } = prepareRenderData({
      template,
      coa: coaData.coa ?? mockDataBundle.coa,
      job: mockDataBundle.job,
      product: mockDataBundle.product,
    });
    return ctx;
  }, [coaData.coa, mockDataBundle.coa, mockDataBundle.job, mockDataBundle.product, template]);

  const variableExamples = useMemo(() => {
    const base = buildExamplesMap(scopedLabelVariables);
    const themeExamples = themeVariablesToExamples(themeVariables);
    return { ...base, ...themeExamples };
  }, [themeVariables, scopedLabelVariables]);

  // Only use example data when no COA is selected
  const effectiveExamples = useMemo(
    () => (selectedCoaId === 'none' ? variableExamples : undefined),
    [selectedCoaId, variableExamples],
  );

  const formatPreviewFullData = useMemo((): Record<string, unknown> => {
    if (caps.supportsPageLayout) return {};
    const { fullData } = prepareRenderData({
      template,
      coa: coaData.coa ?? mockDataBundle.coa,
      job: mockDataBundle.job,
      product: mockDataBundle.product,
      common: commonData,
    });
    return fullData;
  }, [caps.supportsPageLayout, coaData.coa, mockDataBundle.coa, mockDataBundle.job, mockDataBundle.product, commonData, template]);

  // Detect object variables in the currently selected text element's content.
  // These are variables that resolve to objects and need drill-down UI.
  // Also detects drilled-down sub-paths (e.g., coa.totals.cbd.percent) and shows
  // the parent object's drill-down UI so users can change their selection.
  const objectVariablesInText = useMemo((): (ObjectVariableInfo & { currentPath?: string })[] => {
    if (!selected || selected.type !== 'text') return [];

    const text = (selected as TextElement).text || '';
    const variableKeys = extractVariableKeys(text);

    // Build a map of object variable keys to their info
    const objectVarMap = new Map<string, ObjectVariableInfo & { currentPath?: string }>();

    for (const key of variableKeys) {
      // First, check if this key is directly an object variable
      const directMatch = scopedLabelVariables.find((v) => v.key === key && v.type === 'object');
      if (directMatch) {
        const parsed = parseExampleObject(directMatch.example || '');
        if (parsed) {
          objectVarMap.set(key, {
            variableKey: key,
            drillPaths: getDrillPaths(parsed),
            currentPath: undefined, // Not drilled down yet
          });
        }
        continue;
      }

      // Check if this key is a sub-path of an object variable
      // e.g., "coa.totals.cbd.weight.amount" is a sub-path of "coa.totals.cbd"
      for (const varDef of scopedLabelVariables) {
        if (varDef.type !== 'object') continue;

        // Check if the current key starts with this object variable's key + "."
        if (key.startsWith(varDef.key + '.')) {
          const subPath = key.slice(varDef.key.length + 1); // e.g., "weight.amount"
          const parsed = parseExampleObject(varDef.example || '');
          if (parsed) {
            // Only add if not already in the map (avoid duplicates)
            if (!objectVarMap.has(varDef.key)) {
              objectVarMap.set(varDef.key, {
                variableKey: varDef.key,
                drillPaths: getDrillPaths(parsed),
                currentPath: subPath, // Currently drilled to this path
              });
            }
          }
          break; // Found a match, no need to check other object variables
        }
      }
    }

    return Array.from(objectVarMap.values());
  }, [selected, scopedLabelVariables]);

  //detect whether any number variables appear in the currently selected text element
  const hasNumberVariablesInText = useMemo((): boolean => {
    if (!selected || selected.type !== 'text') return false;

    const text = (selected as TextElement).text || '';
    const variableKeys = extractVariableKeys(text);

    const varKey = (selected as TextElement).variableKey;
    if (varKey && !variableKeys.includes(varKey)) {
      variableKeys.push(varKey);
    }

    return variableKeys.some((key) => isNumberVariable(key, themeVariables));
  }, [selected, themeVariables]);

  //detect whether any date variables appear in the currently selected text element
  const hasDateVariablesInText = useMemo((): boolean => {
    if (!selected || selected.type !== 'text') return false;

    const text = (selected as TextElement).text || '';
    const variableKeys = extractVariableKeys(text);

    const varKey = (selected as TextElement).variableKey;
    if (varKey && !variableKeys.includes(varKey)) {
      variableKeys.push(varKey);
    }

    return variableKeys.some((key) => isDateVariable(key, themeVariables));
  }, [selected, themeVariables]);

  // Check if rendered text contains undefined variables ({{...}})
  const hasUndefinedVariables = (text: string) => /\{\{[^{}]+\}\}/.test(text);

  const resolveConditionValue = (
    key: string,
    data: Record<string, any>,
    type?: ThemeVariableType,
    context: RawDataContext = rawDataContext,
  ) => {
    const rendered = renderTextWithData(`{{${key}}}`, data, variableExamples, context);
    if (!rendered || rendered.includes('{{')) return undefined;
    if (type === 'number') {
      const n = Number(rendered);
      return Number.isFinite(n) ? n : undefined;
    }
    if (type === 'boolean') return parseThemeVariableValue(rendered, 'boolean');
    return rendered;
  };

  const findMatchingCondition = <
    T extends { variableKey: string; operator: ConditionOperator; value?: string },
  >(
    conditions: T[] | undefined,
    data: Record<string, any>,
    context: RawDataContext = rawDataContext,
  ): T | undefined => {
    if (!conditions || !conditions.length) return undefined;
    for (const condition of conditions) {
      const type = conditionTypeByKey[condition.variableKey];
      const value = resolveConditionValue(condition.variableKey, data, type, context);
      if (matchesCondition(value, condition, type)) return condition;
    }
    return undefined;
  };

  const resolveColorCondition = (
    conditions: ColorCondition[] | undefined,
    data?: Record<string, any>,
  ): string | undefined => {
    if (!conditions || !conditions.length) return undefined;
    const effectiveData = data || { ...commonData, ...coaData, ...themeData };
    const match = findMatchingCondition<ColorCondition>(conditions, effectiveData);
    return match?.color;
  };

  const formatTextContent = (
    el: TextElement,
    data?: Record<string, any>,
    useExamples?: boolean,
  ) => {
    const { data: effectiveData, rawDataContext: elementRawContext } = tacElementContext(
      el, data || { ...commonData, ...coaData, ...themeData }, rawDataContext, themeData);
    const match = findMatchingCondition<TextCondition>(el.conditions, effectiveData, elementRawContext);
    const source = resolveConditionalTextSource(match?.text, el.text, el.variableKey);
    const examples =
      useExamples !== undefined ? (useExamples ? variableExamples : undefined) : effectiveExamples;

    const dateFormats: Record<string, string> = {};
    const effectiveDateFormat = el.dateFormat || DEFAULT_DATE_FORMAT;

    // Apply date format to the explicit variableKey if it's a date variable
    if (el.variableKey && isDateVariable(el.variableKey, themeVariables)) {
      dateFormats[el.variableKey] = effectiveDateFormat;
    }
    // Apply date format to all date variables found in the text
    const keysInText = extractVariableKeys(source || '');
    for (const key of keysInText) {
      if (isDateVariable(key, themeVariables) && !dateFormats[key]) {
        dateFormats[key] = effectiveDateFormat;
      }
    }

    const numberFormats: Record<string, NumberFormatEntry> = {};
    const effectiveDecimalPlaces = el.numberDecimalPlaces ?? DEFAULT_NUMBER_DECIMAL_PLACES;
    const effectiveMethod = el.numberFormatMethod ?? DEFAULT_NUMBER_METHOD;

    if (effectiveDecimalPlaces !== -1) {
      //apply num formats
      if (el.variableKey && isNumberVariable(el.variableKey, themeVariables)) {
        numberFormats[el.variableKey] = {
          decimalPlaces: effectiveDecimalPlaces,
          method: effectiveMethod,
        };
      }
      for (const key of keysInText) {
        if (isNumberVariable(key, themeVariables) && !numberFormats[key]) {
          numberFormats[key] = { decimalPlaces: effectiveDecimalPlaces, method: effectiveMethod };
        }
      }
    }

    let result = renderTextWithData(source || '', effectiveData, examples, elementRawContext, {
      dateFormats,
      numberFormats,
      themeVariables,
    });
    // Guard against non-string return values so `.toLowerCase`/`.toUpperCase` never throw.
    if (typeof result !== 'string') {
      result = result == null ? '' : String(result);
    }
    const transform = el.textTransform;
    if (transform && transform !== 'none') {
      if (transform === 'lowercase') result = result.toLowerCase();
      else if (transform === 'uppercase') result = result.toUpperCase();
    }
    return result;
  };


  const updateLabelCount = (value: string) => {
    const next = getDefaultLabelCount(value);
    setLabelCount(next);
    setLabelCountInput(next);
    applyTemplate((prev) => ({ ...prev, defaultLabelCount: next }), { skipHistory: true });
  };
  const resolveImageUrl = (el: ImageElement, data?: Record<string, any>) => {
    const effectiveData = data || { ...commonData, ...coaData, ...themeData };
    const match = findMatchingCondition<ImageCondition>(el.conditions, effectiveData || {});

    if (match) {
      const condAssetBlobName = match.imageAsset?.blobName;
      if (condAssetBlobName) {
        const cachedUrl =
          newAssetImagesRef.current[condAssetBlobName] || newAssetImages[condAssetBlobName];
        if (cachedUrl) {
          return cachedUrl;
        }
        console.warn(
          `[resolveImageUrl] Condition asset image ${condAssetBlobName} not found in cache`,
        );
      }
      if (match.url?.trim()?.length) {
        return match.url;
      }
    }

    const newAssetBlobName = el.imageAsset?.blobName;
    if (newAssetBlobName) {
      const cachedUrl =
        newAssetImagesRef.current[newAssetBlobName] || newAssetImages[newAssetBlobName];
      if (cachedUrl) {
        return cachedUrl;
      }
      console.warn(`[resolveImageUrl] New asset image ${newAssetBlobName} not found in cache`);
    }

    const rawMongoId = el['image-asset']?.['mongo-id'];
    if (rawMongoId) {
      const ids = normalizeLegacyIds(rawMongoId);
      for (const mongoId of ids) {
        const cachedUrl = legacyImagesRef.current[mongoId] || legacyImages[mongoId];
        if (cachedUrl) {
          return cachedUrl;
        }
      }
      console.warn(
        `[resolveImageUrl] Legacy image ${ids.join(',')} not found in cache (ref has ${Object.keys(legacyImagesRef.current).length}, state has ${Object.keys(legacyImages).length} entries)`,
      );
    }

    return el.url || '';
  };

  const resolveEffectiveFontFamily = (el: TextElement): string => {
    if (template.outputFormat === 'zpl') return getZplDisplayFont(el.fontFamily);
    if (caps.displayFont) return caps.displayFont;
    const mergedFonts = { ...newAssetFonts, ...newAssetFontsRef.current };
    return getEffectiveFontFamily(el, legacyFonts, DEFAULT_FONT, mergedFonts);
  };

  const breakLongTokens = (text: string, maxTokenLength = 24, chunkSize = 12) => {
    if (!text) return text;
    const tokens = text.split(' ');
    for (let i = 0; i < tokens.length; i += 1) {
      const t = tokens[i];
      if (t.length <= maxTokenLength) continue;
      const pieces: string[] = [];
      for (let j = 0; j < t.length; j += chunkSize) {
        pieces.push(t.slice(j, j + chunkSize));
      }
      tokens[i] = pieces.join(' '); // insert wrap points inside long IDs
    }
    return tokens.join(' ');
  };

  const fetchLabelData = async (opts: { requiredLabels?: number; requestId: number }) => {
    const requiredLabels = Math.max(1, opts?.requiredLabels ?? labelCount);
    const baseline = labelDataRef.current;

    try {
      if (!commonEndpoint && !qrEndpoint) {
        return baseline;
      }

      let common: Record<string, any> = baseline.common;
      let qrList: string[] = baseline.qrList;

      const fetchJsonSafe = async (url: string) => {
        try {
          const res = await fetch(url);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return await res.json();
        } catch (e) {
          console.warn('Data fetch skipped', url, e);
          return undefined;
        }
      };

      if (commonEndpoint) {
        const payload = await fetchJsonSafe(commonEndpoint);
        if (payload) common = payload;
      }
      if (opts.requestId !== labelDataRequestIdRef.current) return baseline;
      if (qrEndpoint) {
        const payload = await fetchJsonSafe(qrEndpoint);
        if (payload !== undefined) {
          qrList = Array.isArray(payload)
            ? payload.map(String)
            : Array.isArray((payload as any)?.values)
              ? (payload as any).values.map(String)
              : baseline.qrList;
        }
      }

      // Ensure we have enough QR codes for the requested label count.
      const qrResult = [...(qrList || [])];
      if (qrResult.length === 0) {
        for (let i = 0; i < requiredLabels; i += 1) {
          qrResult.push(`QR-${i + 1}`);
        }
      } else if (qrResult.length < requiredLabels) {
        const last = qrResult[qrResult.length - 1];
        while (qrResult.length < requiredLabels) {
          qrResult.push(`${last}-${qrResult.length + 1}`);
        }
      }

      const result = { common: common || {}, qrList: qrResult };
      if (opts.requestId === labelDataRequestIdRef.current) {
        labelDataRef.current = result;
        setCommonData(result.common);
        setQrValues(result.qrList);
      }
      return result;
    } catch (err) {
      console.error('Failed to load label data', err);
      return baseline;
    }
  };

  const handleCoaChange = (coaId: string) => {
    // Capture the selected option before clearing the search term.
    const packageOption = allPackageCoaOptions.find((pkg) => pkg.id === coaId);
    setSelectedPackageOption(coaId === 'none' ? null : packageOption || null);

    setSelectedCoaId(coaId);
    setIsPackageDropdownOpen(false);
    setPackageSearchTerm('');
  };

  useEffect(() => {
    if (!selectedPackageOption) {
      setCoaData({});
      return;
    }

    const datedCoa = withCalculatedPrintDates(
      mockDataBundle.coa,
      mockDataBundle.coa.packagedDate || selectedPackageOption.coaData.packagedDate || commonData.packagedDate || '',
      getEffectiveShelfLifeDays(mockDataBundle.product.shelfLifeDays, template.shelfLifeDays),
      getUsedPrintDateVariables(template),
    );
    const { templateFnData } = prepareRenderData({
      template,
      coa: datedCoa,
      job: mockDataBundle.job,
      product: mockDataBundle.product,
    });
    setCoaData({ ...mockDataBundle.coaFlattened, coa: datedCoa, ...templateFnData });
  }, [
    commonData.packagedDate,
    mockDataBundle,
    selectedPackageOption,
    template.elements,
    template.shelfLifeDays,
    template.templateFn,
    template.themeVariables,
    template.tacAdjustments,
  ]);

  const qrElements = useMemo(
    () => template.elements.filter((e) => e.type === 'qr') as QrElement[],
    [template],
  );

  const getQrValueForIndex = (globalIdx: number) => {
    const qrIdx = globalIdx + qrIndexOffset;
    const qrValue = qrValues.length
      ? (qrValues[qrIdx] ?? qrValues[qrValues.length - 1])
      : (qrElements[0]?.data as string | undefined) || `QR-${qrIdx}`;
    return { qrIdx, qrValue };
  };

  const getQrKey = (value: string, el: QrElement) =>
    getQrCacheKey({
      value,
      width: el.width,
      height: el.height,
      margin: el.margin,
      fg: resolveColorCondition(el.fgConditions) || el.fg,
      bg: resolveColorCondition(el.bgConditions) || el.bg,
      errorCorrectionLevel: el.errorCorrectionLevel,
      dpi: template.dpi ?? 72,
    });

  const getBarcodeKey = (value: string, el: BarcodeElement) =>
    `${value}|${el.format}|${el.barWidth}|${el.height}|${el.margin}|${resolveColorCondition(el.lineColorConditions) || el.lineColor}|${resolveColorCondition(el.backgroundConditions) || el.background}`;

  const resolveBarcodeData = (el: BarcodeElement, data?: Record<string, any>) => {
    return resolveBarcodeValue(
      el,
      data || { ...commonData, ...coaData, ...themeData },
      variableExamples,
      rawDataContext,
    );
  };

  const ensureQrCache = async (requiredLabels?: number) => {
    const total = Math.max(1, requiredLabels ?? labelCount);
    const newEntries: Record<string, string> = {};
    const pendingKeys = new Set<string>();
    const tasks: Promise<void>[] = [];

    for (let i = 0; i < total; i += 1) {
      const { qrValue } = getQrValueForIndex(i);
      qrElements.forEach((qrEl) => {
        const key = getQrKey(qrValue, qrEl);
        if (!qrCacheRef.current[key] && !pendingKeys.has(key)) {
          pendingKeys.add(key);
          tasks.push(
            generateQrDataUrl({
              value: qrValue,
              width: qrEl.width,
              height: qrEl.height,
              marginPx: qrEl.margin,
              fg: resolveColorCondition(qrEl.fgConditions) || qrEl.fg,
              bg: resolveColorCondition(qrEl.bgConditions) || qrEl.bg,
              errorCorrectionLevel: qrEl.errorCorrectionLevel,
              dpi: template.dpi ?? 72,
              outputFormat: template.outputFormat,
            })
              .then((url) => {
                newEntries[key] = url;
              })
              .catch((err) => {
                console.error('QR render failed', err);
              }),
          );
        }
      });
    }

    if (tasks.length) {
      await Promise.all(tasks);
      if (Object.keys(newEntries).length) {
        Object.assign(qrCacheRef.current, newEntries);
        setQrCacheBump((v) => v + 1);
      }
    }
  };

  const getDesignQrKey = (el: QrElement) =>
    getQrCacheKey({
      value: el.data || 'SAMPLE-QR-DATA',
      width: el.width,
      height: el.height,
      margin: el.margin,
      fg: resolveColorCondition(el.fgConditions) || el.fg,
      bg: resolveColorCondition(el.bgConditions) || el.bg,
      errorCorrectionLevel: el.errorCorrectionLevel,
      dpi: template.dpi ?? 72,
      namespace: 'design',
    });

  const ensureDesignQrCache = async () => {
    const newEntries: Record<string, string> = {};
    const tasks: Promise<void>[] = [];

    qrElements.forEach((qrEl) => {
      const key = getDesignQrKey(qrEl);
      if (!designQrCacheRef.current[key]) {
        const previewValue = qrEl.data || 'SAMPLE-QR-DATA';
        tasks.push(
          generateQrDataUrl({
            value: previewValue,
            width: qrEl.width,
            height: qrEl.height,
            marginPx: qrEl.margin,
            fg: resolveColorCondition(qrEl.fgConditions) || qrEl.fg,
            bg: resolveColorCondition(qrEl.bgConditions) || qrEl.bg,
            errorCorrectionLevel: qrEl.errorCorrectionLevel,
            dpi: template.dpi ?? 72,
            outputFormat: template.outputFormat,
          })
            .then((url) => {
              newEntries[key] = url;
            })
            .catch((err) => {
              console.error('Design QR render failed', err);
            }),
        );
      }
    });

    if (tasks.length) {
      await Promise.all(tasks);
      if (Object.keys(newEntries).length) {
        Object.assign(designQrCacheRef.current, newEntries);
        setQrCacheBump((v) => v + 1); // Trigger re-render
      }
    }
  };

  const barcodeElements = useMemo(
    () => template.elements.filter((e) => e.type === 'barcode') as BarcodeElement[],
    [template],
  );

  const ensureBarcodeCache = (requiredLabels?: number) => {
    const total = Math.max(1, requiredLabels ?? labelCount);

    for (let i = 0; i < total; i += 1) {
      const { qrIdx, qrValue } = getQrValueForIndex(i);
      const labelData = {
        ...commonData,
        ...coaData,
        ...themeData,
        qrIndex: qrIdx,
        qrValue,
        labelIndex: i,
      };
      barcodeElements.forEach((bcEl) => {
        const resolvedData = resolveBarcodeValue(bcEl, labelData, variableExamples, rawDataContext);
        const key = getBarcodeKey(resolvedData, bcEl);
        if (!barcodeCacheRef.current[key]) {
          barcodeCacheRef.current[key] = generateBarcodeDataUrl({
            value: resolvedData,
            format: bcEl.format,
            barWidth: bcEl.barWidth,
            height: bcEl.height,
            margin: bcEl.margin,
            lineColor: bcEl.lineColor,
            background: bcEl.background,
          });
        }
      });
    }
  };

  React.useEffect(() => {
    ensureQrCache().catch((err) => console.error(err));
  }, [labelCount, qrValues, qrIndexOffset, qrElements]);

  // Ensure design QR cache is populated when QR elements change
  React.useEffect(() => {
    ensureDesignQrCache().catch((err) => console.error(err));
  }, [qrElements]);

  const applyTemplate = (
    updater: (prev: Template) => Template,
    options?: { skipHistory?: boolean },
  ) => {
    setTemplate((prev: Template) => {
      const next = updater(prev);
      if (next === prev) return prev;

      if (!options?.skipHistory) {
        const snapshot = JSON.parse(JSON.stringify(prev)) as Template;
        historyRef.current = [...historyRef.current, snapshot].slice(-20);
        redoRef.current = [];
      }
      const nextCaps = getFormatCapabilities(next.outputFormat);
      let result = next;
      if (nextCaps.fixedBackground) {
        result = { ...result, background: nextCaps.fixedBackground };
      }
      if (nextCaps.fixedTextColor) {
        result = {
          ...result,
          elements: result.elements.map((el) =>
            el.type === 'text' ? { ...el, color: nextCaps.fixedTextColor } : el,
          ),
        };
      }
      return result;
    });
  };

  const pushHistory = () => {
    const snapshot = JSON.parse(JSON.stringify(templateRef.current)) as Template;
    historyRef.current = [...historyRef.current, snapshot].slice(-20);
    redoRef.current = [];
  };

  React.useEffect(() => {
    templateRef.current = template;
  }, [template]);

  const measureTextHeight = (
    content: string,
    width: number,
    fontSize: number,
    fontWeight: number,
    fontFamily: string,
    lineHeightMultiplier?: number,
    scaleX?: number,
    scaleY?: number,
    fontStyle: TextElement['fontStyle'] = 'normal',
  ) => {
    const ctx = ensureMeasureContext(measureCanvasRef);
    const paddingY = 0;
    const paddingX = 0;
    const effectiveLineHeight = lineHeightMultiplier ?? LINE_HEIGHT;
    const sx = scaleX ?? 1;
    const sy = scaleY ?? 1;
    if (!ctx) return fontSize * effectiveLineHeight * sy;
    ctx.font = `${fontStyle} ${fontWeight} ${fontSize}px ${fontFamily}`;
    const metrics = ctx.measureText('M');
    const ascent = metrics.actualBoundingBoxAscent || fontSize * 0.8;
    const descent = metrics.actualBoundingBoxDescent || fontSize * 0.2;
    const baseLineHeight = ascent + descent;
    const lineHeight = baseLineHeight * effectiveLineHeight;
    const maxWidth = Math.max(20, (width - paddingX * 2) / sx);
    const wrapped = wrapLines(content, maxWidth, (t) => ctx.measureText(t).width);
    const lines = Math.max(1, wrapped.length);
    return (lines * lineHeight + paddingY * 2) * sy;
  };

  //Pre-wraps text using Canvas measurement so designer matches ZPL preview exactly
  const wrapTextForDesigner = (
    content: string,
    width: number,
    fontSize: number,
    fontWeight: number,
    fontFamily: string,
    scaleX?: number,
    fontStyle: TextElement['fontStyle'] = 'normal',
  ): string => {
    if (!content) return content;
    const ctx = ensureMeasureContext(measureCanvasRef);
    if (!ctx) return content;
    const sx = scaleX ?? 1;
    const maxWidth = Math.max(20, width / sx);
    ctx.font = `${fontStyle} ${fontWeight} ${fontSize}px ${fontFamily}`;
    //Delegates to shared wrapLines so designer + ZPL preview cannot drift.
    //Author-inserted \n survive because wrapLines splits on them before word wrapping.
    return wrapLines(content, maxWidth, (t) => ctx.measureText(t).width).join('\n');
  };

  const getElementSize = (el: LabelElement) => {
    if (el.type === 'text') {
      const textEl = el as TextElement;
      const content = formatTextContent(textEl);
      const fontFamily = resolveEffectiveFontFamily(textEl);
      const height = measureTextHeight(
        content,
        textEl.width,
        textEl.fontSize,
        textEl.fontWeight,
        fontFamily,
        textEl.lineHeight,
        textEl.scaleX,
        textEl.scaleY,
        textEl.fontStyle,
      );
      return { w: textEl.width, h: height };
    }
    if (el.type === 'image')
      return { w: (el as ImageElement).width, h: (el as ImageElement).height };
    if (el.type === 'barcode') {
      const bc = el as BarcodeElement;
      const resolvedValue = resolveBarcodeData(bc);
      const bcKey = getBarcodeKey(resolvedValue, bc);
      let cached = barcodeCacheRef.current[bcKey];
      if (!cached) {
        cached = generateBarcodeDataUrl({
          value: resolvedValue || ' ',
          format: bc.format,
          barWidth: bc.barWidth,
          height: bc.height,
          margin: bc.margin,
          lineColor: bc.lineColor,
          background: bc.background,
          scaleFactor: 1, // Designer uses 1x scale for accurate WYSIWYG
        });
        barcodeCacheRef.current[bcKey] = cached;
      }
      // Use cached dimensions which include margins
      return { w: cached.width, h: cached.height };
    }
    if (el.type === 'shape') {
      const shape = el as ShapeElement;
      // SVG shapes use width/height directly
      if (shape.shapeType === 'svg') {
        return { w: shape.width || 100, h: shape.height || 100 };
      }
      // Calculate bounding box from vertices (relative coords)
      const vertices = [shape.v1, shape.v2];
      if (shape.v3) vertices.push(shape.v3);
      if (shape.v4) vertices.push(shape.v4);
      const xs = vertices.map((v) => v.x);
      const ys = vertices.map((v) => v.y);
      const minX = Math.min(...xs);
      const maxX = Math.max(...xs);
      const minY = Math.min(...ys);
      const maxY = Math.max(...ys);
      // Width and height of the bounding box
      const w = Math.max(1, maxX - minX);
      const h = Math.max(1, maxY - minY);
      return { w, h, offsetX: minX, offsetY: minY };
    }
    const qr = el as QrElement;
    return { w: qr.width, h: qr.height };
  };

  // Returns which resize handles should be shown for each element type
  // For shapes, returns empty array as shapes use vertex handles instead
  const getResizeHandles = (
    type: ElementKind,
  ): Array<'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw'> => {
    switch (type) {
      case 'image':
        return ['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw']; // All handles
      case 'qr':
        return ['ne', 'nw', 'se', 'sw']; // Corner handles only (maintains square aspect ratio)
      case 'barcode':
        return ['n', 's']; // Vertical only (width is auto-calculated from content)
      case 'text':
        return ['e', 'w']; // Horizontal only (height is determined by text content)
      case 'shape':
        return []; // Shapes use vertex handles instead (see getShapeVertexHandles)
      default:
        return [];
    }
  };

  const getShapeVertexHandles = (el: ShapeElement): Array<'v1' | 'v2' | 'v3' | 'v4'> => {
    // SVG shapes don't use vertex handles - they use resize handles instead
    if (el.shapeType === 'svg') {
      return [];
    }
    if (el.shapeType === 'line') {
      return ['v1', 'v2'];
    }
    return ['v1', 'v2', 'v3', 'v4'];
  };

  const describeElement = (el: LabelElement) => {
    if (el.type === 'text') {
      const textEl = el as TextElement;
      if (typeof textEl.variableKey === 'string' && textEl.variableKey) {
        return friendlyVariableName(textEl.variableKey);
      }
      const summary = typeof textEl.text === 'string' && textEl.text ? textEl.text : 'Text';
      return summary.length > 24 ? `${summary.slice(0, 24)}…` : summary;
    }
    if (el.type === 'image') {
      const img = el as ImageElement;
      return typeof img.alt === 'string' && img.alt ? img.alt : 'Image';
    }
    if (el.type === 'barcode') {
      const bc = el as BarcodeElement;
      const summary = typeof bc.data === 'string' && bc.data ? bc.data : 'Barcode';
      return summary.length > 20 ? `${summary.slice(0, 20)}…` : summary;
    }
    if (el.type === 'shape') {
      const shape = el as ShapeElement;
      return shape.shapeType === 'line' ? 'Line' : 'Rectangle';
    }
    return 'QR code';
  };

  const recenterElement = (id: string) => {
    const el = template.elements.find((e) => e.id === id);
    if (!el) return;
    const { w, h } = getElementSize(el);
    const nextX = Math.max(0, (template.width - w) / 2);
    const nextY = Math.max(0, (template.height - h) / 2);
    updateElement(id, (current) => ({ ...current, x: nextX, y: nextY }));
    selectElement(id, false);
  };

  const filteredVariables = useMemo(() => {
    return filterLabelVariables(scopedLabelVariables, search);
  }, [search, scopedLabelVariables]);

  const addThemeVariable = () => {
    const name = newThemeName.trim() || 'Theme variable';
    const value = parseThemeVariableValue(newThemeValue || '', newThemeType);
    applyTemplate((prev: Template) => {
      const current = prev.themeVariables || [];
      const existingKeys = current.map((v) => v.key);
      const key = buildThemeVariableKey(name, existingKeys);
      const nextVar: ThemeVariable = {
        id: uid(),
        name,
        key,
        type: newThemeType,
        value,
        ...(newThemeType === 'select' && newThemeOptions.length > 0
          ? { options: newThemeOptions }
          : {}),
      };
      return { ...prev, themeVariables: [...current, nextVar] };
    });
    setNewThemeName('');
    setNewThemeValue('');
    setNewThemeType('string');
    setNewThemeOptions([]);
  };

  const updateThemeVariable = (id: string, updater: (v: ThemeVariable) => ThemeVariable) => {
    applyTemplate((prev: Template) => ({
      ...prev,
      themeVariables: (prev.themeVariables || []).map((tv) => (tv.id === id ? updater(tv) : tv)),
    }));
  };

  const renameThemeVariable = (id: string, name: string) => {
    applyTemplate((prev: Template) => {
      const current = prev.themeVariables || [];
      const target = current.find((v) => v.id === id);
      if (!target) return prev;
      const existingKeys = current.filter((v) => v.id !== id).map((v) => v.key);
      const nextKey = buildThemeVariableKey(name, existingKeys);
      const nextVariables = current.map((v) => (v.id === id ? { ...v, name, key: nextKey } : v));
      let nextElements = prev.elements;

      if (nextKey !== target.key) {
        nextElements = prev.elements.map((el) => {
          if (el.type !== 'text') return el;
          const textEl = el as TextElement;
          if (textEl.variableKey !== target.key) return el;
          const nextText = replaceVariableKeyInText(textEl.text, target.key, nextKey);
          return { ...textEl, variableKey: nextKey, text: nextText };
        });
      }

      return { ...prev, themeVariables: nextVariables, elements: nextElements };
    });
  };

  const changeThemeVariableType = (id: string, type: ThemeVariableType) => {
    updateThemeVariable(id, (v) => ({ ...v, type, value: parseThemeVariableValue(v.value, type) }));
  };

  const changeThemeVariableValue = (id: string, value: string | number | boolean) => {
    updateThemeVariable(id, (v) => ({ ...v, value: parseThemeVariableValue(value, v.type) }));
  };

  const removeThemeVariable = (id: string) => {
    applyTemplate((prev: Template) => {
      const current = prev.themeVariables || [];
      const target = current.find((v) => v.id === id);
      const nextThemeVars = current.filter((v) => v.id !== id);
      if (!target) return { ...prev, themeVariables: nextThemeVars };

      const nextElements = prev.elements.map((el) => {
        if (el.type !== 'text') return el;
        const textEl = el as TextElement;
        if (textEl.variableKey !== target.key) return el;
        const cleanedText = removeVariableKeyFromText(textEl.text, target.key);
        return { ...textEl, variableKey: undefined, text: cleanedText };
      });

      return { ...prev, themeVariables: nextThemeVars, elements: nextElements };
    });
  };

  const reorderThemeVariables = (fromIdx: number, toIdx: number) => {
    if (fromIdx === toIdx) return;
    applyTemplate((prev: Template) => {
      const arr = [...(prev.themeVariables || [])];
      const [moved] = arr.splice(fromIdx, 1);
      arr.splice(toIdx, 0, moved);
      return { ...prev, themeVariables: arr };
    });
  };

  const reorderThemeVariableOptions = (variableId: string, fromIdx: number, toIdx: number) => {
    if (fromIdx === toIdx) return;
    updateThemeVariable(variableId, (v) => {
      const opts = [...(v.options || [])];
      const [moved] = opts.splice(fromIdx, 1);
      opts.splice(toIdx, 0, moved);
      return { ...v, options: opts };
    });
  };

  const addTextCondition = (id: string) => {
    const fallback = conditionVariables[0];
    const fallbackType = fallback?.type || 'string';
    const fallbackKey = fallback?.key || 'theme.variable';
    const next: TextCondition = {
      id: uid(),
      variableKey: fallbackKey,
      operator: defaultOperatorForType(fallbackType),
      value: fallbackType === 'boolean' ? 'true' : '',
      text: '',
    };
    updateElement(id, (el) => {
      if (el.type !== 'text') return el;
      const current = (el as TextElement).conditions || [];
      return { ...el, conditions: [...current, next] } as TextElement;
    });
  };

  const updateTextCondition = (
    id: string,
    conditionId: string,
    updater: (c: TextCondition) => TextCondition,
  ) => {
    updateElement(id, (el) => {
      if (el.type !== 'text') return el;
      const current = (el as TextElement).conditions || [];
      return {
        ...el,
        conditions: current.map((c) => (c.id === conditionId ? updater(c) : c)),
      } as TextElement;
    });
  };

  const removeTextCondition = (id: string, conditionId: string) => {
    updateElement(id, (el) => {
      if (el.type !== 'text') return el;
      const current = (el as TextElement).conditions || [];
      return { ...el, conditions: current.filter((c) => c.id !== conditionId) } as TextElement;
    });
  };

  const addImageCondition = (id: string) => {
    const fallback = conditionVariables[0];
    const fallbackType = fallback?.type || 'string';
    const fallbackKey = fallback?.key || 'theme.variable';
    const next: ImageCondition = {
      id: uid(),
      variableKey: fallbackKey,
      operator: defaultOperatorForType(fallbackType),
      value: fallbackType === 'boolean' ? 'true' : '',
      url: '',
    };
    updateElement(id, (el) => {
      if (el.type !== 'image') return el;
      const current = (el as ImageElement).conditions || [];
      return { ...el, conditions: [...current, next] } as ImageElement;
    });
  };

  const updateImageCondition = (
    id: string,
    conditionId: string,
    updater: (c: ImageCondition) => ImageCondition,
  ) => {
    updateElement(id, (el) => {
      if (el.type !== 'image') return el;
      const current = (el as ImageElement).conditions || [];
      return {
        ...el,
        conditions: current.map((c) => (c.id === conditionId ? updater(c) : c)),
      } as ImageElement;
    });
  };

  const removeImageCondition = (id: string, conditionId: string) => {
    updateElement(id, (el) => {
      if (el.type !== 'image') return el;
      const current = (el as ImageElement).conditions || [];
      return { ...el, conditions: current.filter((c) => c.id !== conditionId) } as ImageElement;
    });
  };

   const addColorCondition = (id: string, field: string, defaultColor: string) => {
    const fallback = conditionVariables[0];
    const fallbackType = fallback?.type || 'string';
    const fallbackKey = fallback?.key || 'theme.variable';
    const next: ColorCondition = {
      id: uid(),
      variableKey: fallbackKey,
      operator: defaultOperatorForType(fallbackType),
      value: fallbackType === 'boolean' ? 'true' : '',
      color: defaultColor,
    };
    updateElement(id, (el) => {
      const current = (el as any)[field] || [];
      return { ...el, [field]: [...current, next] };
    });
  };

  const updateColorCondition = (
    id: string,
    field: string,
    conditionId: string,
    updater: (c: ColorCondition) => ColorCondition,
  ) => {
    updateElement(id, (el) => {
      const current: ColorCondition[] = (el as any)[field] || [];
      return { ...el, [field]: current.map((c) => (c.id === conditionId ? updater(c) : c)) };
    });
  };

  const removeColorCondition = (id: string, field: string, conditionId: string) => {
    updateElement(id, (el) => {
      const current: ColorCondition[] = (el as any)[field] || [];
      return { ...el, [field]: current.filter((c) => c.id !== conditionId) };
    });
  };

  const renderColorConditions = (
    elementId: string,
    field: string,
    conditions: ColorCondition[] | undefined,
    defaultColor: string,
    label: string,
  ) => (
    <div
      style={{
        display: 'grid',
        gap: 8,
        padding: 10,
        borderRadius: 8,
        border: `1px solid ${palette.border}`,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <span style={{ fontWeight: 700, fontSize: 13 }}>Conditional {label}</span>
        <button
          disabled={!conditionVariables.length}
          onClick={() => addColorCondition(elementId, field, defaultColor)}
          className="wysiwyg-chip"
          style={{ opacity: conditionVariables.length ? 1 : 0.5 }}
        >
          Add condition
        </button>
      </div>
      {!conditionVariables.length && (
        <div style={{ fontSize: 12, color: palette.muted }}>
          Add a variable to enable conditions.
        </div>
      )}
      {conditions?.map((cond) => {
        const type = conditionTypeByKey[cond.variableKey] || 'string';
        const operators = operatorsForType(type);
        const showValue =
          cond.operator !== 'truthy' &&
          cond.operator !== 'falsy' &&
          cond.operator !== 'isEmpty' &&
          cond.operator !== 'isNotEmpty';
        return (
          <div
            key={cond.id}
            style={{
              display: 'grid',
              gap: 6,
              padding: 8,
              borderRadius: 8,
              border: `1px solid ${palette.border}`,
              background: palette.cardBg,
            }}
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                gap: 8,
              }}
            >
              <label style={{ display: 'grid', gap: 4 }}>
                <span style={{ fontSize: 12, color: palette.muted }}>Variable</span>
                <select
                  value={cond.variableKey}
                  onChange={(e) => {
                    const nextKey = e.target.value;
                    const nextType = conditionTypeByKey[nextKey] || 'string';
                    const allowed = operatorsForType(nextType);
                    const nextOp = allowed.includes(cond.operator)
                      ? cond.operator
                      : defaultOperatorForType(nextType);
                    updateColorCondition(elementId, field, cond.id, (c) => ({
                      ...c,
                      variableKey: nextKey,
                      operator: nextOp,
                      value: nextType === 'boolean' ? 'true' : '',
                    }));
                  }}
                  style={{
                    padding: 8,
                    borderRadius: 8,
                    border: `1px solid ${palette.border}`,
                    background: palette.cardAlt,
                    color: palette.text,
                    width: '100%',
                    minWidth: 0,
                  }}
                >
                  {!conditionVariables.some((v) => v.key === cond.variableKey) && (
                    <option value={cond.variableKey}>
                      Custom ({friendlyVariableName(cond.variableKey)})
                    </option>
                  )}
                  {themeConditionVariables.length > 0 && (
                    <optgroup label="Theme variables">
                      {themeConditionVariables
                        .slice()
                        .sort((a, b) =>
                          (a.label || '').localeCompare(b.label || '', undefined, {
                            sensitivity: 'base',
                          }),
                        )
                        .map((v) => (
                          <option key={`theme-${v.key}`} value={v.key}>
                            {v.label}
                          </option>
                        ))}
                    </optgroup>
                  )}
                  {componentConditionVariables.length > 0 && (
                    <optgroup label="Component variables">
                      {componentConditionVariables
                        .slice()
                        .sort((a, b) =>
                          (a.label || '').localeCompare(b.label || '', undefined, {
                            sensitivity: 'base',
                          }),
                        )
                        .map((v) => (
                          <option key={`component-${v.key}`} value={v.key}>
                            {v.label}
                          </option>
                        ))}
                    </optgroup>
                  )}
                </select>
              </label>
              <label style={{ display: 'grid', gap: 4 }}>
                <span style={{ fontSize: 12, color: palette.muted }}>Operator</span>
                <select
                  value={cond.operator}
                  onChange={(e) =>
                    updateColorCondition(elementId, field, cond.id, (c) => ({
                      ...c,
                      operator: e.target.value as any,
                    }))
                  }
                  style={{
                    padding: 8,
                    borderRadius: 8,
                    border: `1px solid ${palette.border}`,
                    background: palette.cardAlt,
                    color: palette.text,
                    width: '100%',
                    minWidth: 0,
                  }}
                >
                  {operators
                    .slice()
                    .sort((a, b) =>
                      (operatorLabels[a] || '').localeCompare(operatorLabels[b] || '', undefined, {
                        sensitivity: 'base',
                      }),
                    )
                    .map((op) => (
                      <option key={op} value={op}>
                        {operatorLabels[op]}
                      </option>
                    ))}
                </select>
              </label>
            </div>
            {showValue && (
              <label style={{ display: 'grid', gap: 4 }}>
                <span style={{ fontSize: 12, color: palette.muted }}>Compare to</span>
                {type === 'boolean' ? (
                  <select
                    value={cond.value || 'true'}
                    onChange={(e) =>
                      updateColorCondition(elementId, field, cond.id, (c) => ({
                        ...c,
                        value: e.target.value,
                      }))
                    }
                    style={{
                      padding: 8,
                      borderRadius: 8,
                      border: `1px solid ${palette.border}`,
                      background: palette.cardAlt,
                      color: palette.text,
                    }}
                  >
                    <option value="true">True</option>
                    <option value="false">False</option>
                  </select>
                ) : (
                  <input
                    type={type === 'number' ? 'number' : 'text'}
                    value={cond.value ?? ''}
                    onChange={(e) =>
                      updateColorCondition(elementId, field, cond.id, (c) => ({
                        ...c,
                        value: e.target.value,
                      }))
                    }
                    style={{
                      padding: 8,
                      borderRadius: 8,
                      border: `1px solid ${palette.border}`,
                      background: palette.cardAlt,
                      color: palette.text,
                    }}
                  />
                )}
              </label>
            )}
            <label style={{ display: 'grid', gap: 4 }}>
              <span style={{ fontSize: 12, color: palette.muted }}>Color override</span>
              <input
                type="color"
                value={cond.color || defaultColor}
                onChange={(e) =>
                  updateColorCondition(elementId, field, cond.id, (c) => ({
                    ...c,
                    color: e.target.value,
                  }))
                }
                style={{
                  height: 36,
                  width: '100%',
                  maxWidth: 160,
                  background: palette.cardAlt,
                  border: `1px solid ${palette.border}`,
                  borderRadius: 8,
                }}
              />
            </label>
            <button
              onClick={() => removeColorCondition(elementId, field, cond.id)}
              className="wysiwyg-chip secondary"
              style={{ justifyContent: 'center' }}
            >
              Remove
            </button>
          </div>
        );
      })}
    </div>
  );

  const updateElement = (
    id: string,
    updater: (el: LabelElement) => LabelElement,
    options?: { skipHistory?: boolean },
  ) => {
    applyTemplate(
      (prev: Template) => ({
        ...prev,
        elements: prev.elements.map((el: LabelElement) => (el.id === id ? updater(el) : el)),
      }),
      options,
    );
  };

  const handleAssetSelected = useCallback(
    async (asset: AssetReference) => {
      if (!assetBrowserState) return;

      const { category, targetElementId, conditionId } = assetBrowserState;

      if (category === 'font') {
        if (api && organizationId && asset.blobName) {
          try {
            const getToken = () => localStorage.getItem('rid.jwt');
            const base64 = await fetchAssetContent(
              api as unknown as AssetApi,
              organizationId,
              asset.blobName,
              getToken,
            );

            if (base64) {
              injectNewAssetFontFace(asset, base64);

              newAssetFontsRef.current = { ...newAssetFontsRef.current, [asset.blobName]: base64 };
              setNewAssetFonts((prev) => ({ ...prev, [asset.blobName]: base64 }));
            } else {
              console.warn('[handleAssetSelected] Could not fetch font via proxy');
            }
          } catch (err) {
            console.warn('[handleAssetSelected] Failed to fetch font:', err);
          }
        }

        updateElement(targetElementId, (el) => ({
          ...el,
          fontAsset: asset,
          fontFamily: asset.displayName.replace(/\.[^.]+$/, ''), // Remove file extension for display
        }));
      } else if (category === 'image') {
        // Cache the image URL for preview
        if (api && organizationId && asset.blobName) {
          try {
            const url = await resolveAssetUrl(
              api as unknown as AssetApi,
              organizationId,
              asset.blobName,
            );
            if (url) {
              newAssetImagesRef.current = { ...newAssetImagesRef.current, [asset.blobName]: url };
              setNewAssetImages((prev) => ({ ...prev, [asset.blobName]: url }));
            }
          } catch (err) {
            console.warn('[handleAssetSelected] Failed to preload image:', err);
          }
        }

        if (conditionId) {
          updateImageCondition(targetElementId, conditionId, (c) => ({
            ...c,
            imageAsset: asset,
            url: '',
          }));
        } else {
          updateElement(targetElementId, (el) => ({
            ...el,
            imageAsset: asset,
            url: '',
          }));
        }
      }

      setAssetBrowserState(null);
    },
    [assetBrowserState, api, organizationId],
  );

  const tacAddResolution = resolveMarketTac(mockDataBundle.coa, getTacAdjustment(template, mockDataBundle.coa), tacCalculationEnabled);
  const tacAddAvailable = tacAddResolution.value !== null && (tacAddResolution.provenance === 'reported' || tacCalculationEnabled);
  const tacAddContext = JSON.stringify([template.id, tacPackageKey(mockDataBundle.coa), tacAddResolution, tacCalculationEnabled]);
  useEffect(() => {
    setPendingTacInsertion(null);
    setTacAddAcknowledged(false);
  }, [tacAddContext]);

  const addTextFromVariable = (variable: VariableOption, x: number, y: number, acknowledged = false) => {
    if (variable.key === TAC_PERCENT_KEY) {
      if (!tacAddAvailable) {
        showToastError('TAC is not reported and calculation is disabled or constituent data is unavailable.');
        return;
      }
      if (tacAddAvailable && tacAddResolution.provenance !== 'reported' && !acknowledged) {
        setTacAddAcknowledged(false);
        setPendingTacInsertion({ variable, x, y, context: tacAddContext });
        return;
      }
    }
    const friendlyName = variable.displayName ?? friendlyVariableName(variable.key);
    const textEl: TextElement = {
      id: uid(),
      type: 'text',
      x,
      y,
      width: 180,
      text: variable.defaultText ?? `${friendlyName}: {{${variable.key}}}`,
      variableKey: variable.key,
      ...(variable.key === TAC_PERCENT_KEY ? {
        tacCalculationEnabled,
        source: tacAddResolution.provenance === 'reported' ? 'metrc' as const
          : tacAddResolution.provenance === 'edited' ? 'edited' as const
          : tacAddResolution.provenance === 'calculated' ? 'calculated' as const : undefined,
      } : {}),
      fontSize: 16,
      fontWeight: 400,
      fontFamily: DEFAULT_FONT,
      color: palette.text,
      align: 'left',
      required: true,
      rotation: 0,
      zIndex: template.elements.length + 1,
    };
    applyTemplate((prev: Template) => {
      if (!variable.dateKind) return { ...prev, elements: [...prev.elements, textEl] };
      const dateElement: TextElement = {
        ...textEl,
        dateKind: variable.dateKind,
        dateFormat: DEFAULT_DATE_FORMAT,
      };
      return { ...prev, elements: [...prev.elements, dateElement] };
    });
    selectElement(textEl.id, false);
  };

  const addBlankText = () => {
    const textEl: TextElement = {
      id: uid(),
      type: 'text',
      x: 32,
      y: 32,
      width: 200,
      text: 'New text',
      fontSize: 18,
      fontWeight: 400,
      fontFamily: DEFAULT_FONT,
      color: palette.text,
      align: 'left',
      required: true,
      rotation: 0,
      zIndex: template.elements.length + 1,
    };
    applyTemplate((prev: Template) => ({ ...prev, elements: [...prev.elements, textEl] }));
    selectElement(textEl.id, false);
  };

  const addImage = () => {
    const img: ImageElement = {
      id: uid(),
      type: 'image',
      x: 48,
      y: 48,
      width: 140,
      height: 100,
      url: 'https://placehold.co/280x200/png',
      alt: 'Image',
      fit: 'contain',
      required: true,
      rotation: 0,
      opacity: 0.95,
      zIndex: template.elements.length + 1,
    };
    applyTemplate((prev: Template) => ({ ...prev, elements: [...prev.elements, img] }));
    selectElement(img.id, false);
  };

  const getQrElementMargin = (el: QrElement) => getQrMargin({
    value: el.data || QR_DESIGN_PAYLOAD,
    requestedSize: Math.min(el.width, el.height),
    dpi: template.dpi ?? 72,
    outputFormat: template.outputFormat,
    margin: el.margin,
    errorCorrectionLevel: el.errorCorrectionLevel ?? QR_DEFAULT_ERROR_CORRECTION,
  });

  // Accept the selection's full range; clamp each QR independently when applying it.
  const selectedQrMargins = selectedElements.flatMap((el) =>
    el.type === 'qr' ? [getQrElementMargin(el)] : [],
  );

  const addQr = () => {
    const dpi = template.dpi ?? 72;
    // Preserve the established new-component size; only lower the hard floor.
    const minimumSize = getQrDesignGeometry(0, dpi).moduleCount * getMinimumQrModuleSize(dpi);
    const size = getQrDesignGeometry(
      snapToGrid ? Math.max(minimumSize, snapValue(minimumSize, minorStep)) : minimumSize,
      dpi,
    ).size;
    if (hasQrElement) {
      showToastWarning('Only one Retail ID QR code can be added per template.');
      return;
    }

    const margin = getQrMargin({ value: QR_DESIGN_PAYLOAD, requestedSize: size, dpi, outputFormat: template.outputFormat }).value;
    const fgColor = '#213f39';
    const qr: QrElement = {
      id: uid(),
      type: 'qr',
      x: snapToGrid ? snapValue(64, minorStep) : 64,
      y: snapToGrid ? snapValue(64, minorStep) : 64,
      width: size,
      height: size,
      required: true,
      rotation: 0,
      zIndex: template.elements.length + 1,
      data: QR_DESIGN_PAYLOAD,
      fg: fgColor,
      bg: '#FFFFFF',
      margin,
      image: generateSignetLogoSvg(fgColor),
      imageWidth: 48,
      errorCorrectionLevel: QR_DEFAULT_ERROR_CORRECTION,
    };
    setPendingInsertions((prev) => [...prev, { elementIds: [qr.id], qrIds: [qr.id] }]);
    applyTemplate((prev: Template) => {
      if (prev.elements.some((element) => element.type === 'qr')) return prev;
      return { ...prev, elements: [...prev.elements, qr] };
    });
  };

  const addBarcode = () => {
    const barcode: BarcodeElement = {
      id: uid(),
      type: 'barcode',
      x: 64,
      y: 64,
      required: true,
      rotation: 0,
      zIndex: template.elements.length + 1,
      data: '123456789012',
      format: 'CODE128',
      lineColor: '#000000',
      background: '#FFFFFF',
      margin: 10,
      height: 50,
      barWidth: 2,
    };
    applyTemplate((prev: Template) => ({ ...prev, elements: [...prev.elements, barcode] }));
    selectElement(barcode.id, false);
  };

  const addShape = (shapeType: ShapeType = 'rectangle') => {
    const shape: ShapeElement = {
      id: uid(),
      type: 'shape',
      shapeType,
      x: 64,
      y: 64,
      required: false,
      rotation: 0,
      zIndex: template.elements.length + 1,
      v1: { x: 0, y: 0 },
      v2: shapeType === 'line' ? { x: 100, y: 0 } : { x: 80, y: 0 },
      ...(shapeType === 'rectangle'
        ? {
            v3: { x: 80, y: 60 },
            v4: { x: 0, y: 60 },
          }
        : {}),
      strokeColor: '#000000',
      strokeWidth: 2,
      ...(shapeType === 'rectangle' ? { fillColor: 'transparent' } : {}),
    };
    applyTemplate((prev: Template) => ({ ...prev, elements: [...prev.elements, shape] }));
    selectElement(shape.id, false);
  };

  // Parse SVG content and extract dimensions
  const parseSvgContent = (
    svgContent: string,
  ): { valid: boolean; width: number; height: number; sanitized: string } => {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(svgContent, 'image/svg+xml');
      const svg = doc.querySelector('svg');

      // Check for parse errors
      if (!svg || doc.querySelector('parsererror')) {
        return { valid: false, width: 100, height: 100, sanitized: '' };
      }

      // Sanitize: remove script tags and event handlers
      const scripts = svg.querySelectorAll('script');
      scripts.forEach((s) => s.remove());

      // Remove event handler attributes from all elements
      const allElements = svg.querySelectorAll('*');
      allElements.forEach((el) => {
        const attrs = Array.from(el.attributes);
        attrs.forEach((attr) => {
          if (attr.name.startsWith('on')) {
            el.removeAttribute(attr.name);
          }
        });
      });

      // Extract dimensions from viewBox or width/height attributes
      const viewBox = svg
        .getAttribute('viewBox')
        ?.split(/[\s,]+/)
        .map(Number);
      let width = parseFloat(svg.getAttribute('width') || '') || viewBox?.[2] || 100;
      let height = parseFloat(svg.getAttribute('height') || '') || viewBox?.[3] || 100;

      // Ensure xmlns is present
      if (!svg.hasAttribute('xmlns')) {
        svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
      }

      const sanitized = new XMLSerializer().serializeToString(svg);
      return { valid: true, width, height, sanitized };
    } catch {
      return { valid: false, width: 100, height: 100, sanitized: '' };
    }
  };

  // Prepare SVG content for rendering at specific dimensions
  // This ensures the SVG's width/height attributes match the target size
  const prepareSvgForRender = (svgContent: string, width: number, height: number): string => {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(svgContent, 'image/svg+xml');
      const svg = doc.querySelector('svg');
      if (!svg || doc.querySelector('parsererror')) {
        return svgContent;
      }
      // Always set width/height to match target dimensions
      svg.setAttribute('width', String(width));
      svg.setAttribute('height', String(height));
      if (!svg.hasAttribute('xmlns')) {
        svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
      }
      return new XMLSerializer().serializeToString(svg);
    } catch {
      return svgContent;
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    stopDragViewportAutoScroll();
    const themeVariablePayload = event.dataTransfer.getData('application/x-theme-variable');
    const variablePayload = event.dataTransfer.getData('application/x-variable');
    const elementPayload = event.dataTransfer.getData('application/x-element');
    const point = toCanvasPoint(canvasRef.current, event.clientX, event.clientY, effectiveZoom);
    const snapped = snapToGrid ? snapPoint(point, minorStep) : point;
    const x = snapped.x;
    const y = snapped.y;

    if (themeVariablePayload) {
      const variable: VariableOption = JSON.parse(themeVariablePayload);
      addTextFromVariable(variable, x, y);
      return;
    }

    if (variablePayload) {
      const variable: VariableOption = JSON.parse(variablePayload);
      addTextFromVariable(variable, x, y);
      return;
    }

    if (elementPayload) {
      const id = elementPayload;
      updateElement(id, (el) => ({ ...el, x, y }));
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleVariableDragStart = (
    event: React.DragEvent<HTMLDivElement>,
    variable: VariableOption,
  ) => {
    if (variable.key === TAC_PERCENT_KEY && !tacAddAvailable) {
      event.preventDefault();
      return;
    }
    event.dataTransfer.setData('application/x-variable', JSON.stringify(variable));
    event.dataTransfer.effectAllowed = 'copy';
    startDragViewportAutoScroll(event.clientX, event.clientY);
  };


  const handleElementMouseDown = (event: React.MouseEvent<HTMLDivElement>, el: LabelElement) => {
    event.stopPropagation();
    event.preventDefault(); // Prevent text selection while dragging

    const additive = event.shiftKey || event.ctrlKey || event.metaKey;

    // Determine the new selection based on modifier keys
    let newSelectedIds: Set<string>;
    if (!selectedIds.has(el.id)) {
      // Clicking on an unselected element
      if (additive) {
        // Add to selection
        newSelectedIds = new Set(selectedIds);
        newSelectedIds.add(el.id);
      } else {
        // Replace selection
        newSelectedIds = new Set([el.id]);
      }
      setSelectedIds(newSelectedIds);
    } else if (additive) {
      // Ctrl/Shift+click on already-selected toggles it off, don't start drag
      const next = new Set(selectedIds);
      next.delete(el.id);
      setSelectedIds(next);
      return;
    } else {
      // Clicking on already-selected without modifier - keep selection for multi-drag
      newSelectedIds = selectedIds;
    }

    pushHistory();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const mouseX = (event.clientX - rect.left) / effectiveZoom;
    const mouseY = (event.clientY - rect.top) / effectiveZoom;

    // Store offsets for ALL selected elements (for multi-drag)
    const offsets: Record<string, { offsetX: number; offsetY: number }> = {};
    for (const id of newSelectedIds) {
      const element = template.elements.find((e) => e.id === id);
      if (element) {
        offsets[id] = { offsetX: mouseX - element.x, offsetY: mouseY - element.y };
      }
    }

    setDragState({ offsets, primaryId: el.id });
  };

  const handleCanvasMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    // Only start marquee if clicking on the canvas itself (not an element)
    // Check if the click target is the canvas or its background layer
    const target = event.target as HTMLElement;
    const isCanvas = target === canvasRef.current || target.classList.contains('wysiwyg-canvas-bg');
    if (!isCanvas) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const x = (event.clientX - rect.left) / effectiveZoom;
    const y = (event.clientY - rect.top) / effectiveZoom;

    const additive = event.shiftKey || event.ctrlKey || event.metaKey;
    if (!additive) {
      clearSelection();
    }

    setMarqueeState({ startX: x, startY: y, currentX: x, currentY: y, additive });
  };

  const handleCanvasMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    if (vertexDragState) {
      const dx = (event.clientX - vertexDragState.startMouseX) / effectiveZoom;
      const dy = (event.clientY - vertexDragState.startMouseY) / effectiveZoom;
      const newX = snapToGrid
        ? snapValue(vertexDragState.startVertex.x + dx, minorStep)
        : vertexDragState.startVertex.x + dx;
      const newY = snapToGrid
        ? snapValue(vertexDragState.startVertex.y + dy, minorStep)
        : vertexDragState.startVertex.y + dy;
      updateElement(
        vertexDragState.id,
        (current) => ({
          ...current,
          [vertexDragState.vertexKey]: { x: newX, y: newY },
        }),
        { skipHistory: true },
      );
      return;
    }

    if (resizeState) {
      const dx = (event.clientX - resizeState.startX) / effectiveZoom;
      const dy = (event.clientY - resizeState.startY) / effectiveZoom;
      const el = template.elements.find((e) => e.id === resizeState.id);
      if (el) {
        applyResize(dx, dy, resizeState.handle, el);
      }
      return;
    }

    // Handle marquee selection dragging
    if (marqueeState) {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const x = (event.clientX - rect.left) / effectiveZoom;
      const y = (event.clientY - rect.top) / effectiveZoom;
      setMarqueeState((prev) => (prev ? { ...prev, currentX: x, currentY: y } : null));
      return;
    }

    if (!dragState) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const mouseX = (event.clientX - rect.left) / effectiveZoom;
    const mouseY = (event.clientY - rect.top) / effectiveZoom;

    // Update ALL selected elements (multi-drag)
    applyTemplate(
      (prev: Template) => {
        const primaryOffset = dragState.offsets[dragState.primaryId];
        const primaryTargetX = mouseX - primaryOffset.offsetX;
        const primaryTargetY = mouseY - primaryOffset.offsetY;
        const primarySnappedX = snapToGrid ? snapValue(primaryTargetX, minorStep) : primaryTargetX;
        const primarySnappedY = snapToGrid ? snapValue(primaryTargetY, minorStep) : primaryTargetY;
        const snapDeltaX = primarySnappedX - primaryTargetX;
        const snapDeltaY = primarySnappedY - primaryTargetY;

        return {
          ...prev,
          elements: prev.elements.map((el: LabelElement) => {
            const offset = dragState.offsets[el.id];
            if (!offset) return el;
            const targetX = mouseX - offset.offsetX;
            const targetY = mouseY - offset.offsetY;
            return {
              ...el,
              x: targetX + snapDeltaX,
              y: targetY + snapDeltaY,
            };
          }),
        };
      },
      { skipHistory: true },
    );
  };

  const handleCanvasMouseUp = () => {
    // Complete marquee selection
    if (marqueeState) {
      const { startX, startY, currentX, currentY, additive } = marqueeState;
      const minX = Math.min(startX, currentX);
      const maxX = Math.max(startX, currentX);
      const minY = Math.min(startY, currentY);
      const maxY = Math.max(startY, currentY);

      // Find all elements that intersect the marquee rectangle
      const intersecting = template.elements.filter((el) => {
        const size = getElementSize(el);
        const elRight = el.x + size.w;
        const elBottom = el.y + size.h;
        // Check intersection
        return el.x < maxX && elRight > minX && el.y < maxY && elBottom > minY;
      });

      if (additive) {
        // Add to existing selection
        setSelectedIds((prev) => {
          const next = new Set(prev);
          intersecting.forEach((el) => next.add(el.id));
          return next;
        });
      } else {
        setSelectedIds(new Set(intersecting.map((el) => el.id)));
      }
      setMarqueeState(null);
    }

    setDragState(null);
    setResizeState(null);
    setVertexDragState(null);
  };
  const handleCanvasMouseLeave = () => {
    // Complete marquee on leave as well
    if (marqueeState) {
      const { startX, startY, currentX, currentY, additive } = marqueeState;
      const minX = Math.min(startX, currentX);
      const maxX = Math.max(startX, currentX);
      const minY = Math.min(startY, currentY);
      const maxY = Math.max(startY, currentY);

      const intersecting = template.elements.filter((el) => {
        const size = getElementSize(el);
        const elRight = el.x + size.w;
        const elBottom = el.y + size.h;
        return el.x < maxX && elRight > minX && el.y < maxY && elBottom > minY;
      });

      if (additive) {
        setSelectedIds((prev) => {
          const next = new Set(prev);
          intersecting.forEach((el) => next.add(el.id));
          return next;
        });
      } else {
        setSelectedIds(new Set(intersecting.map((el) => el.id)));
      }
      setMarqueeState(null);
    }

    setDragState(null);
    setResizeState(null);
    setVertexDragState(null);
  };

  const removeElements = (ids: Set<string>) => {
    applyTemplate((prev: Template) => ({
      ...prev,
      elements: prev.elements.filter((el: LabelElement) => !ids.has(el.id)),
    }));
    clearSelection();
  };

  const removeElement = (id: string) => {
    removeElements(new Set([id]));
  };

  const undoLast = () => {
    if (!historyRef.current.length) return;
    const prev = historyRef.current.pop()!;
    redoRef.current = [...redoRef.current, templateRef.current];
    setTemplate(prev);
    clearSelection();
  };

  const redoLast = () => {
    if (!redoRef.current.length) return;
    const next = redoRef.current.pop()!;
    historyRef.current = [...historyRef.current, templateRef.current];
    setTemplate(next);
    clearSelection();
  };

  const copySelectedElements = () => {
    if (selectedIds.size === 0) return;
    const elementsToCopy = template.elements.filter((el: LabelElement) => selectedIds.has(el.id));
    clipboardRef.current = JSON.parse(JSON.stringify(elementsToCopy));
  };

  const pasteElements = () => {
    if (clipboardRef.current.length === 0) return;
    const newElements = clipboardRef.current.map((el: LabelElement) => ({
      ...JSON.parse(JSON.stringify(el)),
      id: uid(),
      x: el.x + 10,
      y: el.y + 10,
    }));
    setPendingInsertions((prev) => [
      ...prev,
      {
        elementIds: newElements.map((element) => element.id),
        qrIds: newElements.filter((element) => element.type === 'qr').map((element) => element.id),
      },
    ]);
    applyTemplate((prev: Template) => {
      let hasQr = prev.elements.some((element) => element.type === 'qr');
      const pastedElements = newElements.filter((element) => {
        if (element.type !== 'qr') return true;
        if (hasQr) return false;
        hasQr = true;
        return true;
      });
      if (pastedElements.length === 0) return prev;
      return { ...prev, elements: [...prev.elements, ...pastedElements] };
    });
  };

  const loadTemplateFromString = (json?: string) => {
    try {
      if (!json) throw new Error('No JSON provided');
      const parsed: Template = JSON.parse(json);
      const unit = parsed.realUnit || 'in';
      const importDpi = resolveTemplateDpi(parsed);
      const widthPx = parsed.realWidth
        ? Number(realToPixels(parsed.realWidth, unit, importDpi).toFixed(1))
        : parsed.width;
      const heightPx = parsed.realHeight
        ? Number(realToPixels(parsed.realHeight, unit, importDpi).toFixed(1))
        : parsed.height;
      const normalized = normalizeTemplate({ ...parsed, width: widthPx, height: heightPx });
      const desiredLayoutId =
        normalized.defaultLayoutId ||
        (normalized.customLayout ? 'custom' : DEFAULT_PREVIEW_LAYOUT_ID);
      const availableIds = availablePreviewLayouts.map((l) => l.id);
      const hasDesired = availableIds.includes(desiredLayoutId);
      const fallbackLayoutId = hasDesired
        ? desiredLayoutId
        : availableIds[0] || DEFAULT_PREVIEW_LAYOUT_ID;
      const finalized = { ...normalized, defaultLayoutId: fallbackLayoutId };
      setPreviewSizeId(fallbackLayoutId);
      // Restore custom layout values if present
      if (finalized.customLayout) {
        const cl = finalized.customLayout;
        setCustomUnit(cl.unit);
        setCustomPageWidth(cl.pageWidth);
        setCustomPageHeight(cl.pageHeight);
        setCustomLabelWidth(cl.labelWidth);
        setCustomLabelHeight(cl.labelHeight);
        setCustomMarginTop(cl.marginTop);
        setCustomMarginRight(cl.marginRight);
        setCustomMarginBottom(cl.marginBottom);
        setCustomMarginLeft(cl.marginLeft);
        setCustomSpacingX(cl.spacingX);
        setCustomSpacingY(cl.spacingY);
        setCustomColumns(cl.columns);
        setCustomRows(cl.rows);
        if (cl.format) setCustomFormat(cl.format);
        if (cl.shape) setCustomShape(cl.shape);
        setCustomLabelRotation(cl.labelRotation ?? 0);
      }
      setPreviewTopOffset(finalized.topOffset ?? 0);
      setPreviewLeftOffset(finalized.leftOffset ?? 0);
      setPageRotation(finalized.pageRotation ?? 0);
      const restoredLabelCount = getDefaultLabelCount(finalized.defaultLabelCount);
      setLabelCount(restoredLabelCount);
      setLabelCountInput(restoredLabelCount);
      applyTemplate(() => finalized);
      clearSelection();
      if (onLoad) onLoad(finalized);
    } catch (err) {
      console.error('Invalid template JSON');
    }
  };

  const loadTemplate = () => {
    const byName = localStorage.getItem(storageKey(template.name));
    const legacy = localStorage.getItem('label-template');
    loadTemplateFromString(importValue || byName || legacy || '');
  };

  const saveTemplate = () => {
    const normalizedBrandIds = normalizeBrandIds(activeBrandIds);
    const availableBrandIds = normalizedAvailableBrands.map((brand) => brand.id);
    const selectedBrandIdSet = new Set(normalizedBrandIds);
    const includesAllBrands = allBrands || (
      availableBrandIds.length > 0 &&
      normalizedBrandIds.length === availableBrandIds.length &&
      availableBrandIds.every((id) => selectedBrandIdSet.has(id))
    );
    const normalized = normalizeTemplate({
      ...template,
      allBrands: includesAllBrands,
      brandIds: includesAllBrands ? [] : normalizedBrandIds,
    });
    if (getTemplateDimensionValidationError(normalized)) return;
    if (onSave) onSave(normalizeQrMarginsForSave(withTacSources(normalized, mockDataBundle.coa)), activeMarketIds, normalized.brandIds || []);
  };

  const handleTemplateFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || '');
      setImportValue(text);
      loadTemplateFromString(text);
      if (fileInputRef.current) fileInputRef.current.value = '';
    };
    reader.readAsText(file);
  };

  // Image upload disabled - legacy images loaded from backend
  // const handleImageFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   const file = e.target.files?.[0];
  //   if (!file || !selected || selected.type !== 'image') {
  //     if (imageUploadInputRef.current) imageUploadInputRef.current.value = '';
  //     return;
  //   }
  //   const reader = new FileReader();
  //   reader.onload = () => {
  //     const dataUrl = typeof reader.result === 'string' ? reader.result : '';
  //     if (dataUrl) {
  //       updateElement(selected.id, (el) => ({ ...el, url: dataUrl }));
  //     }
  //     if (imageUploadInputRef.current) imageUploadInputRef.current.value = '';
  //   };
  //   reader.readAsDataURL(file);
  // };

  const bringToFront = (id: string) => {
    const maxZ = Math.max(0, ...template.elements.map((el: LabelElement) => el.zIndex || 0));
    updateElement(id, (el: LabelElement) => ({ ...el, zIndex: maxZ + 1 }));
  };

  const startResize = (
    event: React.MouseEvent<HTMLDivElement>,
    el: LabelElement,
    handle: 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw',
  ) => {
    event.stopPropagation();
    event.preventDefault(); // Prevent text selection while resizing
    pushHistory();
    const { w: startWidth, h: startHeight } = getElementSize(el);
    const startFontSize = el.type === 'text' ? (el as TextElement).fontSize : undefined;
    const startInnerTextHeight = el.type === 'text' ? Math.max(4, startHeight - 16) : undefined;
    setResizeState({
      id: el.id,
      handle,
      startX: event.clientX,
      startY: event.clientY,
      startWidth,
      startHeight,
      startLeft: el.x,
      startTop: el.y,
      startFontSize,
      startInnerTextHeight,
      startQrMargin: el.type === 'qr' ? (el as QrElement).margin : undefined,
    });
    selectElement(el.id, false);
  };

  const startVertexDrag = (
    event: React.MouseEvent<HTMLDivElement>,
    el: ShapeElement,
    vertexKey: 'v1' | 'v2' | 'v3' | 'v4',
  ) => {
    event.stopPropagation();
    event.preventDefault();
    pushHistory();
    const vertex = el[vertexKey] as ShapeVertex;
    if (!vertex) return;
    setVertexDragState({
      id: el.id,
      vertexKey,
      startMouseX: event.clientX,
      startMouseY: event.clientY,
      startVertex: { ...vertex },
    });
    selectElement(el.id, false);
  };

  const applyResize = (dx: number, dy: number, handle: ResizeState['handle'], el: LabelElement) => {
    // Handle SVG shapes - optionally maintain aspect ratio
    if (el.type === 'shape' && (el as ShapeElement).shapeType === 'svg') {
      const shape = el as ShapeElement;
      const startWidth = shape.width || 100;
      const startHeight = shape.height || 100;
      const maintainAspect = shape.maintainAspectRatio !== false; // default true
      const aspectRatio = startWidth / startHeight;

      let newWidth = startWidth;
      let newHeight = startHeight;
      let newX = el.x;
      let newY = el.y;

      // Calculate new size based on handle position
      if (handle.includes('e')) newWidth = Math.max(20, resizeState!.startWidth + dx);
      if (handle.includes('s')) newHeight = Math.max(20, resizeState!.startHeight + dy);
      if (handle.includes('w')) {
        newWidth = Math.max(20, resizeState!.startWidth - dx);
        newX = resizeState!.startLeft + dx;
      }
      if (handle.includes('n')) {
        newHeight = Math.max(20, resizeState!.startHeight - dy);
        newY = resizeState!.startTop + dy;
      }

      if (maintainAspect) {
        // Maintain aspect ratio - use the dimension that changed the most
        const widthDelta = Math.abs(newWidth - startWidth);
        const heightDelta = Math.abs(newHeight - startHeight);

        if (widthDelta >= heightDelta) {
          // Width changed more, adjust height to match
          newHeight = Math.round(newWidth / aspectRatio);
        } else {
          // Height changed more, adjust width to match
          newWidth = Math.round(newHeight * aspectRatio);
        }
      }

      updateElement(
        el.id,
        (current) => ({
          ...current,
          x: newX,
          y: newY,
          width: newWidth,
          height: newHeight,
          v2: { x: newWidth, y: 0 },
        }),
        { skipHistory: true },
      );
      return;
    }

    if (el.type === 'image' || el.type === 'qr' || el.type === 'barcode') {
      let newWidth =
        el.type === 'qr'
          ? (el as QrElement).width
          : el.type === 'barcode'
            ? 150
            : (el as ImageElement).width;
      let newHeight =
        el.type === 'qr'
          ? (el as QrElement).height
          : el.type === 'barcode'
            ? (el as BarcodeElement).height
            : (el as ImageElement).height;
      let newX = el.x;
      let newY = el.y;

      const resizeMinimum = el.type === 'qr' ? 0 : 20;
      if (handle.includes('e')) newWidth = Math.max(resizeMinimum, resizeState!.startWidth + dx);
      if (handle.includes('s')) newHeight = Math.max(resizeMinimum, resizeState!.startHeight + dy);
      if (handle.includes('w')) {
        newWidth = Math.max(resizeMinimum, resizeState!.startWidth - dx);
        newX = resizeState!.startLeft + dx;
      }
      if (handle.includes('n')) {
        newHeight = Math.max(resizeMinimum, resizeState!.startHeight - dy);
        newY = resizeState!.startTop + dy;
      }

      if (el.type === 'qr') {
        const requestedWidth = snapToGrid ? snapValue(newWidth, minorStep) : newWidth;
        const requestedHeight = snapToGrid ? snapValue(newHeight, minorStep) : newHeight;
        // QR codes are always square - use the minimum of width/height
        const resized = getResizedQrDesignGeometry({
          value: (el as QrElement).data || QR_DESIGN_PAYLOAD,
          startLeft: resizeState!.startLeft,
          startTop: resizeState!.startTop,
          startWidth: resizeState!.startWidth,
          startHeight: resizeState!.startHeight,
          requestedWidth,
          requestedHeight,
          resizeWest: handle.includes('w'),
          resizeNorth: handle.includes('n'),
          dpi: template.dpi ?? 72,
          margin: resizeState!.startQrMargin ?? 0,
          outputFormat: template.outputFormat,
          errorCorrectionLevel: (el as QrElement).errorCorrectionLevel ?? QR_DEFAULT_ERROR_CORRECTION,
        });
        updateElement(
          el.id,
          (current) => ({
            ...current,
            x: resized.x,
            y: resized.y,
            width: resized.size,
            height: resized.size,
            margin: resized.margin,
          }),
          { skipHistory: true },
        );
      } else if (el.type === 'barcode') {
        // Barcode: only height is adjustable (width is determined by content and barWidth)
        updateElement(el.id, (current) => ({ ...current, x: newX, y: newY, height: newHeight }), {
          skipHistory: true,
        });
      } else {
        updateElement(
          el.id,
          (current) => ({ ...current, x: newX, y: newY, width: newWidth, height: newHeight }),
          { skipHistory: true },
        );
      }
      return;
    }

    // Text: horizontal resize adjusts width only; font size remains constant
    let newWidth = (el as TextElement).width;
    let newX = el.x;

    if (handle.includes('e')) newWidth = Math.max(40, resizeState!.startWidth + dx);
    if (handle.includes('w')) {
      newWidth = Math.max(40, resizeState!.startWidth - dx);
      newX = resizeState!.startLeft + dx;
    }

    updateElement(el.id, (current) => ({ ...current, x: newX, width: newWidth }), {
      skipHistory: true,
    });
  };

  type ResizeState = typeof resizeState extends infer T
    ? T extends (infer U)[]
      ? U
      : NonNullable<T>
    : never;

  const setCanvasUnit = (nextUnit: 'in' | 'cm') => {
    applyTemplate((prev) => {
      const fromUnit = prev.realUnit || 'in';
      const dpi = resolveTemplateDpi(prev);
      if (!prev.realWidth || !prev.realHeight) {
        return { ...prev, realUnit: nextUnit };
      }
      const width = convertUnits(prev.realWidth, fromUnit, nextUnit);
      const height = convertUnits(prev.realHeight, fromUnit, nextUnit);
      return {
        ...prev,
        realUnit: nextUnit,
        realWidth: Number(width.toFixed(3)),
        realHeight: Number(height.toFixed(3)),
        width: realToPixels(width, nextUnit, dpi),
        height: realToPixels(height, nextUnit, dpi),
      };
    });
  };

  const setCanvasRealSize = (realWidth?: number, realHeight?: number) => {
    applyTemplate((prev) => {
      const unit = prev.realUnit || 'in';
      const dpi = resolveTemplateDpi(prev);
      const nextWidth = realWidth ?? prev.realWidth;
      const nextHeight = realHeight ?? prev.realHeight;
      return {
        ...prev,
        realWidth: nextWidth,
        realHeight: nextHeight,
        width: nextWidth ? realToPixels(nextWidth, unit, dpi) : prev.width,
        height: nextHeight ? realToPixels(nextHeight, unit, dpi) : prev.height,
      };
    });
  };

  const isEditableTarget = (target: EventTarget | null) => {
    if (!target || !(target instanceof HTMLElement)) return false;
    const tag = target.tagName.toLowerCase();
    if (tag === 'input' || tag === 'textarea' || tag === 'select' || target.isContentEditable)
      return true;
    return false;
  };

  React.useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const inField = isEditableTarget(target);

      if (!inField && (event.key === 'Delete' || event.key === 'Backspace')) {
        if (selectedIds.size > 0) {
          event.preventDefault();
          removeElements(selectedIds);
        }
      }
      const isUndo =
        (event.ctrlKey || event.metaKey) && !event.shiftKey && event.key.toLowerCase() === 'z';
      const isRedo =
        ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key.toLowerCase() === 'z') ||
        ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'y');
      const isCopy = (event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'c';
      const isPaste = (event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'v';
      if (isUndo) {
        event.preventDefault();
        undoLast();
      } else if (isRedo) {
        event.preventDefault();
        redoLast();
      } else if (!inField && isCopy) {
        event.preventDefault();
        copySelectedElements();
      } else if (!inField && isPaste) {
        event.preventDefault();
        pasteElements();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [selectedIds]);

  // Invalidate immediately on input changes, not when the next debounce expires.
  // Export owns its own snapshot and must not be cancelled by preview effects.
  useLayoutEffect(() => {
    if (canvasTab === 'preview' && caps.supportsPageLayout && pdfOperationRef.current !== 'export') {
      setIsGeneratingPreview(true);
      const timeoutId = setTimeout(() => {
        generatePdfPreview();
      }, 300);
      return () => {
        clearTimeout(timeoutId);
        if (pdfOperationRef.current === 'preview') {
          ++labelDataRequestIdRef.current;
          pdfOperationRef.current = null;
        }
      };
    }
  }, [
    canvasTab,
    caps.supportsPageLayout,
    template,
    labelCountInput,
    previewSizeId,
    availablePreviewLayouts,
    previewTopOffset,
    previewLeftOffset,
    pageRotation,
    qrIndexOffset,
    selectedCoaId,
    mockDataBundle,
    organizationId,
  ]);

  // Design input changes must not schedule synchronous state writes from the
  // preview layout effect. Clear its feedback once when leaving preview.
  useEffect(() => {
    if (canvasTab !== 'preview') setIsGeneratingPreview(false);
  }, [canvasTab]);

  // Release the previous URL only after React has replaced its iframe source.
  useEffect(() => () => revokePdfUrl(pdfPreviewUrl), [pdfPreviewUrl]);

  useLayoutEffect(() => {
    setPdfPreviewUrl(null);
    setPdfError(null);
  }, [template.id]);

  useLayoutEffect(() => () => {
    ++labelDataRequestIdRef.current;
    pdfOperationRef.current = null;
  }, []);

  return (
    <div
      className="wysiwyg-editor-root"
      onPointerDownCapture={captureStartingPointSnapshot}
      onMouseDownCapture={captureStartingPointSnapshot}
      onKeyDownCapture={captureStartingPointSnapshot}
      onChangeCapture={captureStartingPointSnapshot}
      onClickCapture={captureStartingPointSnapshot}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        padding: 16,
        background: palette.pageBg,
        color: palette.text,
        minHeight: '100vh',
        width: '100%',
        boxSizing: 'border-box',
        overflowX: 'hidden',
      }}
    >
      <style>{`
        .no-scrollbar { scrollbar-width: none; -ms-overflow-style: none; }
        .no-scrollbar::-webkit-scrollbar { display: none; }
        @media (max-width: 1100px) {
          .wysiwyg-editor-root { --wysiwyg-editor-columns: minmax(0, 1fr); }
          .wysiwyg-editor-grid > :nth-child(2) { grid-row: 1; }
        }
      `}</style>

      {pdfError && <div role="alert">{pdfError}{pdfPreviewUrl && ' Showing the previous preview.'}</div>}

      <div
        className="card"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div
            style={{
              display: 'inline-flex',
              gap: 8,
              alignItems: 'center',
              flexWrap: 'wrap',
              padding: '4px 8px',
              borderRadius: 10,
              border: `1px solid ${palette.border}`,
              background: palette.cardAlt,
            }}
          >
            <CanvasDimensionField
              axis="Width"
              unit={templateUnit}
              value={canvasWidth}
              invalid={templateWidthIsInvalid}
              describedBy={dimensionValidationError ? dimensionsErrorId : undefined}
              onChange={(next) => setCanvasRealSize(next, undefined)}
            />
            <CanvasDimensionField
              axis="Height"
              unit={templateUnit}
              value={canvasHeight}
              invalid={templateHeightIsInvalid}
              describedBy={dimensionValidationError ? dimensionsErrorId : undefined}
              onChange={(next) => setCanvasRealSize(undefined, next)}
            />
            <label style={{ display: 'grid', gap: 2 }}>
              <span style={{ fontSize: 11, color: palette.muted }}>Units</span>
              <select
                value={template.realUnit || 'in'}
                onChange={(e) => setCanvasUnit(e.target.value as 'in' | 'cm')}
                style={{
                  padding: '6px 8px',
                  borderRadius: 8,
                  border: `1px solid ${palette.border}`,
                  background: palette.cardBg,
                  color: palette.text,
                }}
              >
                <option value="in">Inches</option>
                <option value="cm">Centimeters</option>
              </select>
            </label>
            <div style={{ fontSize: 12, color: palette.muted }}>
              {`${template.width.toFixed(2)} px × ${template.height.toFixed(2)} px`}
            </div>
            {dimensionValidationError && (
              <span
                id={dimensionsErrorId}
                role="alert"
                style={{ color: 'var(--danger, #b42318)', fontSize: 12, fontWeight: 600 }}
              >
                {dimensionValidationError}
              </span>
            )}
          </div>
          <button
            type="button"
            title={dimensionValidationError || 'Save template and download JSON'}
            onClick={saveTemplate}
            disabled={Boolean(dimensionValidationError)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '8px 12px',
              borderRadius: 10,
              border: `1px solid ${palette.border}`,
              background: palette.accent,
              color: 'var(--panel)',
              fontWeight: 700,
              cursor: dimensionValidationError ? 'not-allowed' : 'pointer',
              opacity: dimensionValidationError ? 0.6 : 1,
            }}
          >
            Save Template
          </button>
          <div ref={packageDropdownRef} style={{ position: 'relative' }}>
            <button
              type="button"
              title="Select COA data to display"
              onClick={() => setIsPackageDropdownOpen((v) => !v)}
              disabled={isLoadingPackages}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                padding: '8px 12px',
                borderRadius: 10,
                border: `1px solid ${palette.border}`,
                background: palette.cardBg,
                color: palette.text,
                cursor: isLoadingPackages ? 'wait' : 'pointer',
                fontSize: 14,
                opacity: isLoadingPackages ? 0.7 : 1,
                minWidth: 200,
                textAlign: 'left',
              }}
            >
              {isLoadingCoa ? (
                <span style={{ fontWeight: 700 }}>Loading COA...</span>
              ) : selectedCoaId === 'none' ? (
                <span style={{ fontWeight: 700 }}>Select Package...</span>
              ) : (
                (() => {
                  const selectedPkg = allPackageCoaOptions.find((p) => p.id === selectedCoaId)
                    || selectedPackageOption;
                  return (
                    <>
                      <span style={{ fontWeight: 700 }}>{selectedPkg?.label || 'Package'}</span>
                      {selectedPkg?.coaData.productName && (
                        <span className="muted" style={{ fontSize: 12 }}>
                          {selectedPkg.coaData.productName}
                        </span>
                      )}
                      {selectedPkg && (selectedPkg.facilityName || selectedPkg.facilityLicense) && (
                        <span className="muted" style={{ fontSize: 12 }}>
                          {[selectedPkg.facilityName, selectedPkg.facilityLicense].filter(Boolean).join(' · ')}
                        </span>
                      )}
                    </>
                  );
                })()
              )}
            </button>

            {isPackageDropdownOpen && (
              <div
                className="card"
                onMouseDown={(e) => e.preventDefault()}
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  marginTop: 6,
                  zIndex: 20,
                  padding: 6,
                  maxHeight: 360,
                  overflowY: 'auto',
                  minWidth: 280,
                  background: palette.cardBg,
                  border: `1px solid ${palette.border}`,
                  borderRadius: 12,
                  boxShadow: '0 10px 30px rgba(0,0,0,0.12)',
                }}
              >
                {/* Search input */}
                <input
                  type="text"
                  placeholder="Search package ID (min 3 chars)..."
                  value={packageSearchTerm}
                  onChange={(e) => setPackageSearchTerm(e.target.value)}
                  onMouseDown={(e) => e.stopPropagation()}
                  autoFocus
                  style={{
                    width: '100%',
                    padding: '8px 10px',
                    borderRadius: 8,
                    border: `1px solid ${palette.border}`,
                    background: palette.cardBg,
                    color: palette.text,
                    fontSize: 13,
                    marginBottom: 6,
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />

                {/* No COA option */}
                <button
                  type="button"
                  onMouseDown={() => handleCoaChange('none')}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    border: 'none',
                    background: selectedCoaId === 'none' ? 'rgba(0,0,0,0.05)' : 'transparent',
                    padding: '8px 10px',
                    borderRadius: 8,
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ fontWeight: 700, color: palette.text }}>No COA</div>
                </button>

                {(isLoadingPackages || isSearchingPackages) && (
                  <div className="muted" style={{ padding: 8, fontSize: 12 }}>
                    {isSearchingPackages ? 'Searching packages...' : 'Loading packages...'}
                  </div>
                )}

                {visiblePackageCoaOptions.length === 0 &&
                  !isLoadingPackages &&
                  !isSearchingPackages &&
                  api &&
                  organizationId &&
                  activeMarketIds?.length && (
                    <div className="muted" style={{ padding: 8, fontSize: 12 }}>
                      {packageSearchTerm.length > 0 && packageSearchTerm.length < 3
                        ? 'Type at least 3 characters to search.'
                        : 'No packages found'}
                    </div>
                  )}

                {visiblePackageCoaOptions.map((pkg) => (
                  <button
                    key={pkg.id}
                    type="button"
                    onMouseDown={() => handleCoaChange(pkg.id)}
                    style={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      border: 'none',
                      background: selectedCoaId === pkg.id ? 'rgba(0,0,0,0.05)' : 'transparent',
                      padding: '8px 10px',
                      borderRadius: 8,
                      cursor: 'pointer',
                    }}
                  >
                    <div style={{ fontWeight: 700, color: palette.text }}>{pkg.label}</div>
                    <div className="muted" style={{ fontSize: 12 }}>
                      {pkg.coaData.productName || 'Package'}
                    </div>
                    {(pkg.facilityName || pkg.facilityLicense) && (
                      <div className="muted" style={{ fontSize: 12 }}>
                        {[pkg.facilityName, pkg.facilityLicense].filter(Boolean).join(' · ')}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <button
            title={showOutlines ? 'Hide outlines' : 'Show outlines'}
            onClick={() => setShowOutlines((v) => !v)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '8px 10px',
              borderRadius: 10,
              border: `1px solid ${palette.border}`,
              background: showOutlines ? palette.accent : palette.cardBg,
              color: showOutlines ? 'var(--panel)' : palette.muted,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            {showOutlines ? 'Outlines On' : 'Outlines Off'}
          </button>
          <button
            title={showRequiredBadges ? 'Hide required badges' : 'Show required badges'}
            onClick={() => setShowRequiredBadges((v) => !v)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '8px 10px',
              borderRadius: 10,
              border: `1px solid ${palette.border}`,
              background: showRequiredBadges ? palette.accent : palette.cardBg,
              color: showRequiredBadges ? 'var(--panel)' : palette.muted,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            {showRequiredBadges ? 'Badges On' : 'Badges Off'}
          </button>
          <button
            title={showRulers ? 'Hide rulers' : 'Show rulers'}
            onClick={() => setShowRulers((v) => !v)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '8px 10px',
              borderRadius: 10,
              border: `1px solid ${palette.border}`,
              background: showRulers ? palette.accent : palette.cardBg,
              color: showRulers ? 'var(--panel)' : palette.muted,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            {showRulers ? 'Rulers On' : 'Rulers Off'}
          </button>
          <button
            title={snapToGrid ? 'Disable grid snapping' : 'Enable grid snapping'}
            onClick={() => setSnapToGrid((v) => !v)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '8px 10px',
              borderRadius: 10,
              border: `1px solid ${palette.border}`,
              background: snapToGrid ? palette.accent : palette.cardBg,
              color: snapToGrid ? 'var(--panel)' : palette.muted,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            {snapToGrid ? 'Snap On' : 'Snap Off'}
          </button>
        </div>
      </div>
      {isLegacyTemplate(template) && !legacyWarningDismissed && (
        <div className="alert warning" role="alert" style={{ marginBottom: 0 }}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
            <path d="M8.982 1.566a1.13 1.13 0 0 0-1.964 0L.165 13.233c-.457.778.091 1.767.982 1.767h13.706c.891 0 1.439-.99.982-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z" />
          </svg>
          <span style={{ flex: 1 }}>
            This template was migrated from a previous version of Retail ID and has custom logic.{' '}
            <a href="#" className="migration-warning-link" style={{ color: 'inherit' }}>
              Learn More &gt;
            </a>
          </span>
          <button
            type="button"
            onClick={() => setLegacyWarningDismissed(true)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 4,
              color: 'inherit',
              display: 'flex',
              alignItems: 'center',
            }}
            aria-label="Dismiss warning"
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
              <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
            </svg>
          </button>
        </div>
      )}
      <div
        className="wysiwyg-editor-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: 'var(--wysiwyg-editor-columns, 360px minmax(0, 1fr) 380px)',
          gap: 16,
          width: '100%',
          boxSizing: 'border-box',
          alignItems: 'start',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
          <div className="card" style={{ padding: 12, marginBottom: 6 }}>
            <div style={{ fontSize: 12, color: palette.muted }}>Template</div>
            <input
              data-testid="label-editor-template-name"
              value={template.name}
              onChange={(e) => applyTemplate((prev) => ({ ...prev, name: e.target.value }))}
              style={{
                padding: '8px 10px',
                borderRadius: 8,
                border: `1px solid ${palette.border}`,
                background: palette.cardBg,
                color: palette.text,
                fontWeight: 700,
                width: '100%',
              }}
            />
            <div style={{ marginTop: 8 }}>
              <span className="wysiwyg-chip secondary" style={{ cursor: 'default' }}>
                {`${formatDef.displayName} ${template.dpi ?? formatDef.defaultDpi} DPI`}
              </span>
            </div>
            <label style={{ display: 'block', marginTop: 10, fontSize: 12, color: palette.muted }}>
              Shelf life (calendar days)
              <input
                type="number"
                min="0"
                step="1"
                value={template.shelfLifeDays ?? ''}
                onChange={(event) => {
                  const value = event.target.value;
                  if (value !== '' && !/^\d+$/.test(value)) return;
                  applyTemplate((prev) => ({
                    ...prev,
                    shelfLifeDays: value === '' ? undefined : Number(value),
                  }));
                }}
                style={{
                  display: 'block',
                  marginTop: 4,
                  padding: '8px 10px',
                  borderRadius: 8,
                  border: `1px solid ${palette.border}`,
                  background: palette.cardBg,
                  color: palette.text,
                  width: '100%',
                }}
              />
            </label>
            <div style={{ marginTop: 10 }}>
              <MarketsSelector
                options={(availableMarkets || []).map((m) => ({
                  id: m.id,
                  name: m.name,
                  code: m.code || '',
                  selected: (activeMarketIds || []).includes(m.id),
                  active: true,
                }))}
                onChange={(next) =>
                  setActiveMarketIds((next || []).filter((n) => n.selected).map((n) => n.id))
                }
                label="Markets"
                defaultOpen={false}
              />
            </div>
            <div style={{ marginTop: 10 }}>
              <MarketsSelector
                options={normalizedAvailableBrands.map((brand) => ({
                  id: brand.id,
                  name: brand.name,
                  code: '',
                  selected: allBrands || activeBrandIds.includes(brand.id),
                  active: true,
                }))}
                onChange={(next, action) => {
                  const nextBrandIds = normalizeBrandIds(
                    (next || []).filter((brand) => brand.selected).map((brand) => brand.id),
                  );
                  if (action === 'select-all' || action === 'select-none') {
                    setActiveBrandIds(nextBrandIds);
                    setAllBrands(action === 'select-all' && normalizedAvailableBrands.length > 0);
                    return;
                  }
                  const nextBrandIdSet = new Set(nextBrandIds);
                  const availableBrandIdSet = new Set(normalizedAvailableBrands.map((brand) => brand.id));
                  const mergedBrandIds = activeBrandIds.filter(
                    (id) => !availableBrandIdSet.has(id) || nextBrandIdSet.has(id),
                  );
                  nextBrandIds.forEach((id) => {
                    if (!mergedBrandIds.includes(id)) mergedBrandIds.push(id);
                  });
                  setActiveBrandIds(mergedBrandIds);
                  setAllBrands(
                    availableBrandIdSet.size > 0 &&
                    mergedBrandIds.length === availableBrandIdSet.size &&
                    mergedBrandIds.every((id) => availableBrandIdSet.has(id)),
                  );
                }}
                label="Brands"
                defaultOpen={false}
                testId="template-brands"
              />
            </div>
          </div>
          <div
            className="card"
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
              minWidth: 0,
              overflow: 'hidden',
              alignSelf: 'stretch',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h3 style={{ margin: 0, fontSize: 16, letterSpacing: 0.2 }}>Components</h3>
            </div>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12 }}>
              <input type="checkbox" checked={tacCalculationEnabled}
                disabled={!tacMarketAllowsCalculation(mockDataBundle.coa)}
                onChange={event => {
                  setTacCalculationEnabled(event.target.checked);
                  setPendingTacInsertion(null);
                  setTacAddAcknowledged(false);
                }} />
              Allow Retail ID to calculate TAC when not reported
            </label>
            {!tacMarketAllowsCalculation(mockDataBundle.coa) && <p role="note">TAC calculation is not enabled for this package market.</p>}
            <FullScreenConfirmOverlay
              show={pendingTacInsertion !== null}
              title="This TAC is not lab-reported"
              testId="tac-add-gate"
              confirmLabel={tacAddResolution.provenance === 'edited' ? 'Add as edited' : 'Add as calculated'}
              confirmDisabled={!tacAddAcknowledged || pendingTacInsertion?.context !== tacAddContext || !tacAddAvailable}
              cancelLabel="Cancel"
              onCancel={() => { setPendingTacInsertion(null); setTacAddAcknowledged(false); }}
              onConfirm={() => {
                if (!pendingTacInsertion || !tacAddAcknowledged || pendingTacInsertion.context !== tacAddContext || !tacAddAvailable) return;
                addTextFromVariable(pendingTacInsertion.variable, pendingTacInsertion.x, pendingTacInsertion.y, true);
                setPendingTacInsertion(null);
                setTacAddAcknowledged(false);
              }}
              message={<div>
                <p>{tacAddResolution.value === null ? 'TAC unavailable' : `${Number(tacAddResolution.value.toPrecision(12))}%`}</p>
                <p>Source: {tacAddResolution.provenance === 'edited' ? 'Edited' : 'Calculated by Retail ID'}</p>
                {pendingTacInsertion?.context !== tacAddContext && <p role="alert">The package or TAC inputs changed. Cancel and add TAC again to review the current value.</p>}
                <label><input type="checkbox"
                  checked={tacAddAcknowledged && pendingTacInsertion?.context === tacAddContext}
                  disabled={pendingTacInsertion?.context !== tacAddContext}
                  onChange={event => setTacAddAcknowledged(event.target.checked)} />
                  I understand this TAC is not lab-reported.
                </label>
              </div>}
            />
            <input
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 10px',
                borderRadius: 10,
                border: `1px solid ${palette.border}`,
                background: palette.cardAlt,
                color: palette.text,
              }}
            />
            <div
              style={{
                display: 'grid',
                gap: 8,
                maxHeight: '70vh',
                overflow: 'auto',
                paddingRight: 4,
              }}
            >
              {filteredVariables.map((variable) => (
                <div
                  key={variable.key}
                  data-testid={variable.dateKind ? `date-component-${variable.dateKind}` : undefined}
                  draggable={variable.key !== TAC_PERCENT_KEY || tacAddAvailable}
                  onDragStart={(e) => handleVariableDragStart(e, variable)}
                  onDragEnd={stopDragViewportAutoScroll}
                  title={variable.description}
                  style={{
                    padding: '10px 12px',
                    borderRadius: 12,
                    border: `1px solid ${palette.border}`,
                    background: palette.cardAlt,
                    cursor: 'grab',
                  }}
                >
                  <div style={{ fontWeight: 700, fontSize: 13, color: palette.accent }}>
                    {variable.displayName ?? friendlyVariableName(variable.key)}
                  </div>
                  <div style={{ fontSize: 12, color: palette.text }}>{variable.metrcName}</div>
                  <div style={{ fontSize: 11, color: palette.muted }}>{variable.description}</div>
                  {variable.key === TAC_PERCENT_KEY && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span data-testid="tac-component-availability" style={{ fontSize: 12 }}>
                        {!tacAddAvailable ? 'Not reported'
                          : `Source: ${tacAddResolution.provenance === 'reported' ? 'Reported by Metrc'
                            : tacAddResolution.provenance === 'edited' ? 'Edited' : 'Calculated by Retail ID'}`}
                      </span>
                      <button type="button" className="wysiwyg-chip" aria-label="Add TAC"
                        disabled={!tacAddAvailable}
                        onClick={() => addTextFromVariable(variable, 32, 32)}>+</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div
          className="card"
          data-testid="label-editor-center"
          style={{
            background: palette.cardAlt,
            position: 'relative',
            minWidth: 0,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          <div
            style={{
              display: 'flex',
              gap: 8,
              borderBottom: `1px solid ${palette.border}`,
              paddingBottom: 8,
              flexWrap: 'wrap',
            }}
          >
            {['design', 'preview'].map((tab) => (
              <button
                key={tab}
                onClick={() => setCanvasTab(tab as 'design' | 'preview')}
                className={`wysiwyg-chip ${canvasTab === tab ? '' : 'secondary'}`}
                style={{
                  background: canvasTab === tab ? palette.accent : palette.cardBg,
                  color: canvasTab === tab ? 'var(--panel)' : palette.muted,
                }}
              >
                {tab === 'design' ? 'Design' : 'Print Options'}
              </button>
            ))}
          </div>

          {canvasTab === 'design' ? (
            <>
              <header
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 12,
                }}
              >
                <div>
                  <div style={{ fontSize: 12, color: palette.muted }}>Canvas</div>
                  <div style={{ fontWeight: 700, color: palette.text }}>{template.name}</div>
                  {onChangeStartingPoint && (
                    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, fontSize: 12, color: palette.muted }}>
                      <span>{provenance ? `Based on ${provenance.market} \u00b7 ${provenance.category}` : 'Blank template'}</span>
                      <button
                        type="button"
                        className="subtle-btn"
                        onClick={() => onChangeStartingPoint(
                          startingPointSnapshotRef.current !== undefined
                          && startingPointSnapshotRef.current !== getStartingPointSnapshot(),
                        )}
                      >
                        Change
                      </button>
                    </div>
                  )}
                  {template.realWidth && template.realHeight && (
                    <div style={{ fontSize: 12, color: palette.muted }}>
                      Real size: {template.realWidth} {template.realUnit || 'in'} ×{' '}
                      {template.realHeight} {template.realUnit || 'in'}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <label
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      fontSize: 12,
                      color: palette.text,
                    }}
                  >
                    <span style={{ color: palette.muted }}>Zoom</span>
                    <select
                      value={designZoom}
                      onChange={(e) => setDesignZoom(Number(e.target.value))}
                      style={{
                        padding: '6px 8px',
                        borderRadius: 8,
                        border: `1px solid ${palette.border}`,
                        background: palette.cardBg,
                        color: palette.text,
                      }}
                    >
                      {[0.5, 0.75, 1, 1.25, 1.5, 2, 2.5, 3, 4, 5].map((z) => (
                        <option key={z} value={z}>
                          {Math.round(z * 100)}%
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              </header>
              <div
                style={{ display: 'inline-flex', flexDirection: 'column', alignSelf: 'flex-start', maxWidth: '100%', minWidth: 0 }}
              >
                <div
                  style={{
                    display: 'flex',
                    gap: 4,
                    padding: '8px 12px',
                    background: palette.cardBg,
                    border: `1px solid ${palette.border}`,
                    borderBottom: 'none',
                    borderRadius: '8px 8px 0 0',
                  }}
                >
                  <IconButton title="Add Text" ariaLabel="Add text element" onClick={addBlankText}>
                    <TextElementIcon />
                  </IconButton>
                  <IconButton title="Add Image" ariaLabel="Add image element" onClick={addImage}>
                    <ImageElementIcon />
                  </IconButton>
                  <IconButton
                    title="Add QR Code"
                    ariaLabel="Add QR code element"
                    onClick={addQr}
                  >
                    <QrElementIcon />
                  </IconButton>
                  <IconButton
                    title="Add Barcode"
                    ariaLabel="Add barcode element"
                    onClick={addBarcode}
                  >
                    <BarcodeElementIcon />
                  </IconButton>
                  <IconButton
                    title="Add Shape"
                    ariaLabel="Add shape element"
                    onClick={() => addShape('rectangle')}
                  >
                    <ShapeElementIcon />
                  </IconButton>
                </div>
                <div
                  data-testid="label-editor-canvas-viewport"
                  style={{
                    position: 'relative',
                    overflow: 'auto',
                    width: (template.width + effectiveRulerSize) * effectiveZoom,
                    maxWidth: '100%',
                    boxSizing: 'border-box',
                    height: (template.height + effectiveRulerSize) * effectiveZoom,
                    border: `1px solid ${palette.border}`,
                    borderRadius: '0 8px 8px 8px',
                  }}
                >
                  <div
                    style={{
                      position: 'relative',
                      width: template.width + effectiveRulerSize,
                      height: template.height + effectiveRulerSize,
                      transform: `scale(${effectiveZoom})`,
                      transformOrigin: 'top left',
                    }}
                  >
                    {showRulers && (
                      <>
                        <div
                          style={{
                            position: 'absolute',
                            left: effectiveRulerSize,
                            top: 0,
                            height: effectiveRulerSize,
                            width: template.width,
                            background: palette.cardBg,
                            borderBottom: `1px solid ${palette.border}`,
                            borderLeft: `1px solid ${palette.border}`,
                          }}
                        >
                          {horizontalTicks}
                          {horizontalLabels}
                        </div>
                        <div
                          style={{
                            position: 'absolute',
                            left: 0,
                            top: effectiveRulerSize,
                            width: effectiveRulerSize,
                            height: template.height,
                            background: palette.cardBg,
                            borderRight: `1px solid ${palette.border}`,
                            borderTop: `1px solid ${palette.border}`,
                          }}
                        >
                          {verticalTicks}
                          {verticalLabels}
                        </div>
                        <div
                          style={{
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            width: effectiveRulerSize,
                            height: effectiveRulerSize,
                            background: palette.cardBg,
                            borderRight: `1px solid ${palette.border}`,
                            borderBottom: `1px solid ${palette.border}`,
                          }}
                        />
                      </>
                    )}
                    <div
                      ref={canvasRef}
                      data-testid="label-editor-canvas"
                      aria-label="Label canvas"
                      className="wysiwyg-canvas-area"
                      onDrop={handleDrop}
                      onDragOver={handleDragOver}
                      onMouseDown={handleCanvasMouseDown}
                      onMouseMove={handleCanvasMouseMove}
                      onMouseUp={handleCanvasMouseUp}
                      onMouseLeave={handleCanvasMouseLeave}
                      style={{
                        position: 'absolute',
                        left: effectiveRulerSize,
                        top: effectiveRulerSize,
                        width: template.width,
                        height: template.height,
                        background: template.background,
                        borderRadius: 0,
                        border: `1px dashed ${palette.border}`,
                        overflow: 'hidden',
                        userSelect: dragState || resizeState ? 'none' : undefined,
                      }}
                    >
                      <div
                        style={{
                          position: 'absolute',
                          inset: 0,
                          backgroundImage: `radial-gradient(circle at 1px 1px, ${palette.border} 1px, transparent 0), radial-gradient(circle at 20px 20px, rgba(31,40,57,0.06) 1px, transparent 0)`,
                          backgroundSize: '20px 20px',
                          pointerEvents: 'none',
                        }}
                      />
                      {template.elements
                        .slice()
                        .sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0))
                        .map((el) => {
                          const size = getElementSize(el);
                          const shapeOffset =
                            el.type === 'shape' && 'offsetX' in size
                              ? { x: (size as any).offsetX || 0, y: (size as any).offsetY || 0 }
                              : { x: 0, y: 0 };
                          return (
                            <div
                              key={el.id}
                              aria-label={describeElement(el)}
                              draggable={false}
                              onMouseDown={(e) => handleElementMouseDown(e, el)}
                              onClick={(e) => e.stopPropagation()}
                              style={{
                                position: 'absolute',
                                left: el.x + shapeOffset.x,
                                top: el.y + shapeOffset.y,
                                width: size.w,
                                height: size.h,
                                border: selectedIds.has(el.id)
                                  ? `1px solid ${palette.accent}`
                                  : showOutlines
                                    ? `1px solid ${palette.border}`
                                    : '1px solid transparent',
                                borderRadius: 0,
                                padding: 0,
                                background:
                                  el.type === 'text' && showOutlines
                                    ? 'rgba(0,185,130,0.08)'
                                    : 'transparent',
                                cursor: 'grab',
                                transform: `rotate(${el.rotation || 0}deg)`,
                                transformOrigin: el.type === 'barcode' ? 'top left' : undefined,
                                userSelect: 'none',
                              }}
                            >
                              {el.type === 'text' ? (
                                (() => {
                                  const textScaleX = (el as TextElement).scaleX ?? 1;
                                  const textScaleY = (el as TextElement).scaleY ?? 1;
                                  const hasScale = textScaleX !== 1 || textScaleY !== 1;
                                  return (
                                <div
                                  style={{
                                    fontSize: (el as TextElement).fontSize,
                                    fontWeight: (el as TextElement).fontWeight,
                                    fontStyle: (el as TextElement).fontStyle,
                                    textDecoration: (el as TextElement).textDecoration,
                                    fontFamily: resolveEffectiveFontFamily(el as TextElement),
                                    color: resolveColorCondition((el as TextElement).colorConditions) || (el as TextElement).color,
                                    textAlign: (el as TextElement).align,
                                    lineHeight: (el as TextElement).lineHeight ?? LINE_HEIGHT,
                                    whiteSpace: 'pre-wrap',
                                    letterSpacing: (el as TextElement).letterSpacing
                                      ? `${(el as TextElement).letterSpacing}em`
                                      : undefined,
                                    textTransform: (el as TextElement).textTransform || undefined,
                                    ...(hasScale
                                      ? {
                                          transform: `scale(${textScaleX}, ${textScaleY})`,
                                          transformOrigin: 'top left',
                                          width: `${100 / textScaleX}%`,
                                        }
                                      : {}),
                                  }}
                                >
                                  {caps.displayFont
                                    ? wrapTextForDesigner(
                                        formatTextContent(el as TextElement),
                                        (el as TextElement).width,
                                        (el as TextElement).fontSize,
                                        (el as TextElement).fontWeight,
                                        resolveEffectiveFontFamily(el as TextElement),
                                        (el as TextElement).scaleX,
                                        (el as TextElement).fontStyle,
                                      )
                                    : formatTextContent(el as TextElement)}
                                </div>
                                  );
                                })()
                              ) : el.type === 'image' ? (
                                <img
                                  src={resolveImageUrl(el as ImageElement)}
                                  alt={(el as ImageElement).alt || 'image element'}
                                  style={{
                                    width: '100%',
                                    height: (el as ImageElement).height,
                                    objectFit: (el as ImageElement).fit,
                                    display: 'block',
                                    borderRadius: 12,
                                    opacity: (el as ImageElement).opacity,
                                    pointerEvents: 'none',
                                  }}
                                />
                              ) : el.type === 'qr' ? (
                                (() => {
                                  const qrEl = el as QrElement;
                                  const designKey = getDesignQrKey(qrEl);
                                  const qrSrc =
                                    designQrCacheRef.current[designKey] || transparentPx;
                                  return (
                                    <img
                                      src={qrSrc}
                                      alt="QR code"
                                      style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'contain',
                                        display: 'block',
                                        pointerEvents: 'none',
                                      }}
                                    />
                                  );
                                })()
                              ) : el.type === 'barcode' ? (
                                (() => {
                                  const bcEl = el as BarcodeElement;
                                  const resolvedValue = resolveBarcodeData(bcEl);
                                  const bcKey = getBarcodeKey(resolvedValue, bcEl);
                                  if (!barcodeCacheRef.current[bcKey]) {
                                    barcodeCacheRef.current[bcKey] = generateBarcodeDataUrl({
                                      value: resolvedValue || ' ',
                                      format: bcEl.format,
                                      barWidth: bcEl.barWidth,
                                      height: bcEl.height,
                                      margin: bcEl.margin,
                                      lineColor: resolveColorCondition(bcEl.lineColorConditions) || bcEl.lineColor,
                                      background: resolveColorCondition(bcEl.backgroundConditions) || bcEl.background,
                                      scaleFactor: 1, // Designer uses 1x scale for accurate WYSIWYG
                                    });
                                  }
                                  return (
                                    <img
                                      src={barcodeCacheRef.current[bcKey].dataUrl}
                                      alt="Barcode"
                                      style={{ display: 'block', pointerEvents: 'none' }}
                                    />
                                  );
                                })()
                              ) : el.type === 'shape' ? (
                                (() => {
                                  const shape = el as ShapeElement;
                                  const { w, h } = getElementSize(el);

                                  // SVG shapes render the raw SVG content or a placeholder
                                  if (shape.shapeType === 'svg') {
                                    if (shape.svgContent) {
                                      // Prepare SVG with correct dimensions for consistent rendering
                                      const preparedSvg = prepareSvgForRender(
                                        shape.svgContent,
                                        w,
                                        h,
                                      );
                                      return (
                                        <img
                                          src={`data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(preparedSvg)))}`}
                                          width={w}
                                          height={h}
                                          style={{ display: 'block', pointerEvents: 'none' }}
                                          alt="SVG shape"
                                        />
                                      );
                                    }
                                    // Empty SVG - show placeholder
                                    return (
                                      <div
                                        style={{
                                          width: w,
                                          height: h,
                                          border: `2px dashed ${palette.muted}`,
                                          borderRadius: 4,
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          background: `repeating-linear-gradient(45deg, transparent, transparent 5px, ${palette.border} 5px, ${palette.border} 10px)`,
                                          opacity: 0.5,
                                        }}
                                      >
                                        <span
                                          style={{
                                            fontSize: 11,
                                            color: palette.muted,
                                            background: palette.cardBg,
                                            padding: '2px 6px',
                                            borderRadius: 4,
                                          }}
                                        >
                                          SVG
                                        </span>
                                      </div>
                                    );
                                  }

                                  // Line and rectangle shapes use vertex-based rendering
                                  const vertices = [shape.v1, shape.v2];
                                  if (shape.v3) vertices.push(shape.v3);
                                  if (shape.v4) vertices.push(shape.v4);
                                  const minX = Math.min(...vertices.map((v) => v.x));
                                  const minY = Math.min(...vertices.map((v) => v.y));

                                  return (
                                    <svg
                                      width={w}
                                      height={h}
                                      style={{
                                        display: 'block',
                                        overflow: 'visible',
                                        pointerEvents: 'none',
                                      }}
                                    >
                                      {shape.shapeType === 'line' ? (
                                        <line
                                          x1={shape.v1.x - minX}
                                          y1={shape.v1.y - minY}
                                          x2={shape.v2.x - minX}
                                          y2={shape.v2.y - minY}
                                          stroke={resolveColorCondition(shape.strokeColorConditions) || shape.strokeColor}
                                          strokeWidth={shape.strokeWidth}
                                          strokeLinecap="round"
                                        />
                                      ) : (
                                        <polygon
                                          points={`${shape.v1.x - minX},${shape.v1.y - minY} ${shape.v2.x - minX},${shape.v2.y - minY} ${shape.v3!.x - minX},${shape.v3!.y - minY} ${shape.v4!.x - minX},${shape.v4!.y - minY}`}
                                          stroke={resolveColorCondition(shape.strokeColorConditions) || shape.strokeColor}
                                          strokeWidth={shape.strokeWidth}
                                          fill={resolveColorCondition(shape.fillColorConditions) || shape.fillColor || 'none'}
                                          strokeLinejoin="round"
                                        />
                                      )}
                                    </svg>
                                  );
                                })()
                              ) : null}
                              {showRequiredBadges && el.required && (
                                <div
                                  style={{
                                    position: 'absolute',
                                    top: -8,
                                    right: -8,
                                    padding: '2px 6px',
                                    borderRadius: 10,
                                    background: palette.accentDark,
                                    color: palette.cardBg,
                                    fontFamily: 'Arial, sans-serif',
                                    fontWeight: 700,
                                    fontSize: 12,
                                    transform: `scale(${badgeScale})`,
                                    transformOrigin: 'top right',
                                  }}
                                >
                                  required
                                </div>
                              )}
                              {showRequiredBadges &&
                                selectedCoaId !== 'none' &&
                                el.type === 'text' &&
                                hasUndefinedVariables(formatTextContent(el as TextElement)) && (
                                  <div
                                    style={{
                                      position: 'absolute',
                                      top: -8,
                                      left: -8,
                                      padding: '2px 6px',
                                      borderRadius: 10,
                                      background: '#d97706',
                                      color: palette.cardBg,
                                      fontFamily: 'Arial, sans-serif',
                                      fontWeight: 700,
                                      fontSize: 12,
                                      transform: `scale(${badgeScale})`,
                                      transformOrigin: 'top left',
                                    }}
                                  >
                                    undefined
                                  </div>
                                )}
                              {selectedIds.has(el.id) && (
                                <>
                                  {/* Resize handles - SVG shapes get corner handles for aspect-ratio-locked resizing, or all handles if unlocked */}
                                  {(el.type === 'shape' && (el as ShapeElement).shapeType === 'svg'
                                    ? (el as ShapeElement).maintainAspectRatio !== false
                                      ? (['ne', 'nw', 'se', 'sw'] as const) // aspect locked: corners only
                                      : (['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw'] as const) // aspect unlocked: all handles
                                    : getResizeHandles(el.type)
                                  ).map((handle) => {
                                    const size = 10;
                                    const half = size / 2;
                                    const styles: React.CSSProperties = {
                                      position: 'absolute',
                                      width: size,
                                      height: size,
                                      background: palette.cardBg,
                                      border: `1px solid ${palette.accent}`,
                                      borderRadius: 4,
                                      cursor:
                                        handle === 'n'
                                          ? 'ns-resize'
                                          : handle === 's'
                                            ? 'ns-resize'
                                            : handle === 'e'
                                              ? 'ew-resize'
                                              : handle === 'w'
                                                ? 'ew-resize'
                                                : handle === 'ne'
                                                  ? 'nesw-resize'
                                                  : handle === 'sw'
                                                    ? 'nesw-resize'
                                                    : handle === 'nw'
                                                      ? 'nwse-resize'
                                                      : 'nwse-resize',
                                    };
                                    if (handle.includes('n')) styles.top = -half;
                                    if (handle.includes('s')) styles.bottom = -half;
                                    if (handle.includes('e')) styles.right = -half;
                                    if (handle.includes('w')) styles.left = -half;
                                    if (!handle.includes('n') && !handle.includes('s'))
                                      styles.top = `calc(50% - ${half}px)`;
                                    if (!handle.includes('e') && !handle.includes('w'))
                                      styles.left = `calc(50% - ${half}px)`;
                                    return (
                                      <div
                                        key={handle}
                                        aria-label={`Resize ${handle}`}
                                        onMouseDown={(e) =>
                                          startResize(e, el, handle as ResizeState['handle'])
                                        }
                                        style={styles}
                                      />
                                    );
                                  })}
                                  {/* Vertex handles for shape elements */}
                                  {el.type === 'shape' &&
                                    (() => {
                                      const shape = el as ShapeElement;
                                      const vertices = getShapeVertexHandles(shape);
                                      const allVertices = [shape.v1, shape.v2];
                                      if (shape.v3) allVertices.push(shape.v3);
                                      if (shape.v4) allVertices.push(shape.v4);
                                      const minX = Math.min(...allVertices.map((v) => v.x));
                                      const minY = Math.min(...allVertices.map((v) => v.y));

                                      return vertices.map((vertexKey) => {
                                        const vertex = shape[vertexKey] as ShapeVertex;
                                        if (!vertex) return null;
                                        const size = 10;
                                        const half = size / 2;
                                        const left = vertex.x - minX - half;
                                        const top = vertex.y - minY - half;
                                        return (
                                          <div
                                            key={vertexKey}
                                            onMouseDown={(e) =>
                                              startVertexDrag(e, el as ShapeElement, vertexKey)
                                            }
                                            style={{
                                              position: 'absolute',
                                              width: size,
                                              height: size,
                                              left,
                                              top,
                                              background: '#6366f1',
                                              border: `1px solid ${palette.cardBg}`,
                                              borderRadius: '50%',
                                              cursor: 'move',
                                            }}
                                          />
                                        );
                                      });
                                    })()}
                                </>
                              )}
                            </div>
                          );
                        })}
                      {dragState &&
                        (() => {
                          const dragging = template.elements.find(
                            (e) => e.id === dragState.primaryId,
                          );
                          if (!dragging) return null;
                          const { w, h } = getElementSize(dragging);
                          const x = dragging.x;
                          const y = dragging.y;
                          return (
                            <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
                              <div
                                style={{
                                  position: 'absolute',
                                  left: 0,
                                  top: y,
                                  width: x,
                                  height: 1,
                                  background: palette.accent,
                                  opacity: 0.75,
                                }}
                              />
                              <div
                                style={{
                                  position: 'absolute',
                                  left: x,
                                  top: 0,
                                  width: 1,
                                  height: y,
                                  background: palette.accent,
                                  opacity: 0.75,
                                }}
                              />
                              <div
                                style={{
                                  position: 'absolute',
                                  left: x,
                                  top: y,
                                  width: w,
                                  height: h,
                                  pointerEvents: 'none',
                                }}
                              >
                                <div
                                  style={{
                                    position: 'absolute',
                                    left: 0,
                                    top: -22,
                                    background: palette.cardBg,
                                    border: `1px solid ${palette.border}`,
                                    borderRadius: 8,
                                    padding: '4px 8px',
                                    fontSize: 11,
                                    color: palette.text,
                                    boxShadow: '0 4px 12px rgba(31,40,57,0.12)',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  X: {Math.round(x)} px ({formatRulerLabel(x / pxPerUnit)}{' '}
                                  {rulerUnit}) · Y: {Math.round(y)} px (
                                  {formatRulerLabel(y / pxPerUnit)} {rulerUnit})
                                </div>
                              </div>
                            </div>
                          );
                        })()}
                      {/* Marquee selection rectangle */}
                      {marqueeState && (
                        <div
                          style={{
                            position: 'absolute',
                            left: Math.min(marqueeState.startX, marqueeState.currentX),
                            top: Math.min(marqueeState.startY, marqueeState.currentY),
                            width: Math.abs(marqueeState.currentX - marqueeState.startX),
                            height: Math.abs(marqueeState.currentY - marqueeState.startY),
                            border: `1px dashed ${palette.accent}`,
                            background: `${palette.accent}22`,
                            pointerEvents: 'none',
                            zIndex: 9999,
                          }}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            renderPreview()
          )}
        </div>

        <div
          data-testid="label-editor-properties"
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            minWidth: 0,
            alignSelf: 'start',
          }}
        >
          <div
            className="card"
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
              minWidth: 0,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 8,
                flexWrap: 'wrap',
              }}
            >
              <h3
                style={{ margin: 0, fontSize: 16, display: 'flex', alignItems: 'center', gap: 8 }}
              >
                Properties
                {selectedIds.size > 1 && (
                  <span style={{ fontSize: 12, color: palette.muted, fontWeight: 400 }}>
                    ({selectedIds.size} elements)
                  </span>
                )}
              </h3>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button
                  onClick={() => setShowPropertiesPane((open) => !open)}
                  className="wysiwyg-chip secondary"
                >
                  {showPropertiesPane ? 'Collapse' : 'Expand'}
                </button>
                {selectedIds.size > 0 && (
                  <button
                    onClick={() => removeElements(selectedIds)}
                    className="wysiwyg-chip delete"
                  >
                    Delete{selectedIds.size > 1 ? ` (${selectedIds.size})` : ''}
                  </button>
                )}
              </div>
            </div>
            {showPropertiesPane ? (
              selected ? (
                <div style={{ display: 'grid', gap: 8, minWidth: 0 }}>
                  <div
                    style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, minWidth: 0 }}
                  >
                    <label style={{ display: 'grid', gap: 4 }}>
                      <span style={{ fontSize: 12, color: palette.muted }}>X</span>
                      <input
                        type="number"
                        value={getCommonValue(selectedElements, (el) => el.x) ?? ''}
                        placeholder={
                          selectedIds.size > 1 &&
                          getCommonValue(selectedElements, (el) => el.x) === undefined
                            ? ''
                            : undefined
                        }
                        onChange={(e) =>
                          updateSelectedElements((el) => ({ ...el, x: Number(e.target.value) }))
                        }
                        style={{
                          padding: 8,
                          borderRadius: 8,
                          border: `1px solid ${palette.border}`,
                          background: palette.cardAlt,
                          color: palette.text,
                          width: '100%',
                        }}
                      />
                    </label>
                    <label style={{ display: 'grid', gap: 4 }}>
                      <span style={{ fontSize: 12, color: palette.muted }}>Y</span>
                      <input
                        type="number"
                        value={getCommonValue(selectedElements, (el) => el.y) ?? ''}
                        placeholder={
                          selectedIds.size > 1 &&
                          getCommonValue(selectedElements, (el) => el.y) === undefined
                            ? ''
                            : undefined
                        }
                        onChange={(e) =>
                          updateSelectedElements((el) => ({ ...el, y: Number(e.target.value) }))
                        }
                        style={{
                          padding: 8,
                          borderRadius: 8,
                          border: `1px solid ${palette.border}`,
                          background: palette.cardAlt,
                          color: palette.text,
                          width: '100%',
                        }}
                      />
                    </label>
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="checkbox"
                      checked={getCommonValue(selectedElements, (el) => el.required) ?? false}
                      onChange={(e) =>
                        updateSelectedElements((el) => ({ ...el, required: e.target.checked }))
                      }
                    />
                    <span>Required</span>
                  </label>
                  <label style={{ display: 'grid', gap: 4, minWidth: 0 }}>
                    <span style={{ fontSize: 12, color: palette.muted }}>Z-index</span>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', minWidth: 0 }}>
                      <input
                        type="number"
                        value={getCommonValue(selectedElements, (el) => el.zIndex) ?? ''}
                        onChange={(e) =>
                          updateSelectedElements((el) => ({
                            ...el,
                            zIndex: Number(e.target.value),
                          }))
                        }
                        style={{
                          flex: 1,
                          padding: 8,
                          borderRadius: 8,
                          border: `1px solid ${palette.border}`,
                          background: palette.cardAlt,
                          color: palette.text,
                          minWidth: 0,
                        }}
                      />
                      <button
                        onClick={() => {
                          selectedElements.forEach((el) => bringToFront(el.id));
                        }}
                        className="wysiwyg-chip"
                        style={{ flexShrink: 0 }}
                      >
                        Bring front
                      </button>
                    </div>
                  </label>
                  {selected.type !== 'shape' && (
                    <label style={{ display: 'grid', gap: 4 }}>
                      <span style={{ fontSize: 12, color: palette.muted }}>Rotation (degrees)</span>
                      {template.outputFormat === 'zpl' && selected.type === 'text' ? (
                        <select
                          aria-label="Rotation (degrees)"
                          value={getCommonValue(selectedElements, (el) => el.rotation || 0) ?? ''}
                          onChange={(e) =>
                            updateSelectedElements((el) => ({
                              ...el,
                              rotation: Number(e.target.value),
                            }))
                          }
                          style={{
                            padding: 8,
                            borderRadius: 8,
                            border: `1px solid ${palette.border}`,
                            background: palette.cardAlt,
                            color: palette.text,
                            width: '100%',
                          }}
                        >
                          <option value={0}>0</option>
                          <option value={90}>90</option>
                          <option value={180}>180</option>
                          <option value={270}>270</option>
                        </select>
                      ) : (
                        <input
                          aria-label="Rotation (degrees)"
                          type="number"
                          step={1}
                          value={getCommonValue(selectedElements, (el) => el.rotation || 0) ?? ''}
                          onChange={(e) =>
                            updateSelectedElements((el) => ({
                              ...el,
                              rotation: Number(e.target.value),
                            }))
                          }
                          style={{
                            padding: 8,
                            borderRadius: 8,
                            border: `1px solid ${palette.border}`,
                            background: palette.cardAlt,
                            color: palette.text,
                            width: '100%',
                          }}
                        />
                      )}
                    </label>
                  )}
                  {selected.type === 'text' && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <label style={{ display: 'grid', gap: 4 }}>
                        <span style={{ fontSize: 12, color: palette.muted }}>Scale X</span>
                        <input
                          type="number"
                          step={0.05}
                          min={0.1}
                          max={3}
                          value={
                            getCommonValueOfType<TextElement, number>(
                              'text',
                              (el) => el.scaleX ?? 1,
                            ) ?? ''
                          }
                          onChange={(e) => {
                            const val = parseFloat(e.target.value);
                            updateSelectedOfType<TextElement>('text', (el) => ({
                              ...el,
                              scaleX: isNaN(val) ? 1 : Math.max(0.1, Math.min(3, val)),
                            }));
                          }}
                          placeholder="1"
                          style={{
                            padding: 8,
                            borderRadius: 8,
                            border: `1px solid ${palette.border}`,
                            background: palette.cardAlt,
                            color: palette.text,
                            width: '100%',
                          }}
                        />
                      </label>
                      <label style={{ display: 'grid', gap: 4 }}>
                        <span style={{ fontSize: 12, color: palette.muted }}>Scale Y</span>
                        <input
                          type="number"
                          step={0.05}
                          min={0.1}
                          max={3}
                          value={
                            getCommonValueOfType<TextElement, number>(
                              'text',
                              (el) => el.scaleY ?? 1,
                            ) ?? ''
                          }
                          onChange={(e) => {
                            const val = parseFloat(e.target.value);
                            updateSelectedOfType<TextElement>('text', (el) => ({
                              ...el,
                              scaleY: isNaN(val) ? 1 : Math.max(0.1, Math.min(3, val)),
                            }));
                          }}
                          placeholder="1"
                          style={{
                            padding: 8,
                            borderRadius: 8,
                            border: `1px solid ${palette.border}`,
                            background: palette.cardAlt,
                            color: palette.text,
                            width: '100%',
                          }}
                        />
                      </label>
                    </div>
                  )}
                  {selected.type === 'text' ? (
                    <>
                      {elementHasTacContext(selected) && (
                        <>
                        {selected.tacCalculationEnabled !== true && (
                          <p role="note">This TAC element is Metrc-only. Remove it and re-add TAC from Components to enable Retail ID calculation.</p>
                        )}
                        <TacValueEditor
                          calculationEnabled={selected.tacCalculationEnabled === true}
                          coa={mockDataBundle.coa}
                          adjustment={getTacAdjustment(template, mockDataBundle.coa)}
                          onChange={selectedPackageOption && tacPackageKey(mockDataBundle.coa) ? tacAdjustment => {
                            const packageKey = tacPackageKey(mockDataBundle.coa)!;
                            applyTemplate(previous => withTacSources({
                              ...previous, tacAdjustments: { ...previous.tacAdjustments, [packageKey]: tacAdjustment },
                            }, mockDataBundle.coa));
                          } : undefined}
                        />
                        </>
                      )}
                      <label style={{ display: 'grid', gap: 4, minWidth: 0 }}>
                        <span style={{ fontSize: 12, color: palette.muted }}>Variable binding</span>
                        <select
                          value={(selected as TextElement).variableKey || ''}
                          onChange={(e) =>
                            updateElement(selected.id, (el) => {
                              const key = e.target.value;
                              if (!key) return { ...el, variableKey: undefined } as LabelElement;
                              const friendly = friendlyVariableName(key);
                              const variable = scopedLabelVariables.find(option => option.key === key);
                              return {
                                ...el,
                                variableKey: key,
                                text: variable?.defaultText ?? `${friendly}: {{${key}}}`,
                              } as LabelElement;
                            })
                          }
                          style={{
                            padding: 8,
                            borderRadius: 8,
                            border: `1px solid ${palette.border}`,
                            background: palette.cardAlt,
                            color: palette.text,
                            width: '100%',
                            maxWidth: '100%',
                          }}
                        >
                          <option value="">Manual text</option>
                          {selectedTextBinding && !scopedLabelVariables.some(variable => variable.key === selectedTextBinding) && (
                            <option value={selectedTextBinding}>
                              Custom ({labelVariables.find(variable => variable.key === selectedTextBinding)?.displayName ?? friendlyVariableName(selectedTextBinding)})
                            </option>
                          )}
                          {scopedLabelVariables
                            .slice()
                            .sort((a, b) =>
                              friendlyVariableName(a.key).localeCompare(
                                friendlyVariableName(b.key),
                                undefined,
                                { sensitivity: 'base' },
                              ),
                            )
                            .map((v) => (
                              <option key={v.key} value={v.key}>
                                {v.displayName ?? friendlyVariableName(v.key)}
                              </option>
                            ))}
                        </select>
                      </label>
                      {hasDateVariablesInText && (
                        <label style={{ display: 'grid', gap: 4, minWidth: 0 }}>
                          <span style={{ fontSize: 12, color: palette.muted }}>Date format</span>
                          <select
                            value={(selected as TextElement).dateFormat || DEFAULT_DATE_FORMAT}
                            onChange={(e) =>
                              updateElement(selected.id, (el) => ({
                                ...el,
                                dateFormat: e.target.value,
                              }))
                            }
                            style={{
                              padding: 8,
                              borderRadius: 8,
                              border: `1px solid ${palette.border}`,
                              background: palette.cardAlt,
                              color: palette.text,
                              width: '100%',
                              maxWidth: '100%',
                            }}
                          >
                            {DATE_FORMAT_PRESETS.slice()
                              .sort((a, b) =>
                                (a.label || '').localeCompare(b.label || '', undefined, {
                                  sensitivity: 'base',
                                }),
                              )
                              .map((preset) => (
                                <option key={preset.value} value={preset.value}>
                                  {preset.label}
                                </option>
                              ))}
                          </select>
                        </label>
                      )}
                      {/* Number Format UI */}
                      {hasNumberVariablesInText && (
                        <div style={{ display: 'grid', gap: 4, minWidth: 0 }}>
                          <label style={{ display: 'grid', gap: 4, minWidth: 0 }}>
                            <span style={{ fontSize: 12, color: palette.muted }}>
                              Decimal places
                            </span>
                            <select
                              value={
                                (selected as TextElement).numberDecimalPlaces ??
                                DEFAULT_NUMBER_DECIMAL_PLACES
                              }
                              onChange={(e) =>
                                updateElement(selected.id, (el) => ({
                                  ...el,
                                  numberDecimalPlaces: Number(e.target.value),
                                }))
                              }
                              style={{
                                padding: 8,
                                borderRadius: 8,
                                border: `1px solid ${palette.border}`,
                                background: palette.cardAlt,
                                color: palette.text,
                                width: '100%',
                                maxWidth: '100%',
                              }}
                            >
                              {NUMBER_DECIMAL_PRESETS.slice()
                                .sort((a, b) =>
                                  (a.label || '').localeCompare(b.label || '', undefined, {
                                    sensitivity: 'base',
                                  }),
                                )
                                .map((preset) => (
                                  <option key={preset.value} value={preset.value}>
                                    {preset.label}
                                  </option>
                                ))}
                            </select>
                          </label>
                          <label style={{ display: 'grid', gap: 4, minWidth: 0 }}>
                            <span style={{ fontSize: 12, color: palette.muted }}>
                              Rounding method
                            </span>
                            <select
                              value={
                                (selected as TextElement).numberFormatMethod ??
                                DEFAULT_NUMBER_METHOD
                              }
                              onChange={(e) =>
                                updateElement(selected.id, (el) => ({
                                  ...el,
                                  numberFormatMethod: e.target.value as 'round' | 'truncate',
                                }))
                              }
                              style={{
                                padding: 8,
                                borderRadius: 8,
                                border: `1px solid ${palette.border}`,
                                background: palette.cardAlt,
                                color: palette.text,
                                width: '100%',
                                maxWidth: '100%',
                              }}
                            >
                              {NUMBER_METHOD_PRESETS.slice()
                                .sort((a, b) =>
                                  (a.label || '').localeCompare(b.label || '', undefined, {
                                    sensitivity: 'base',
                                  }),
                                )
                                .map((preset) => (
                                  <option key={preset.value} value={preset.value}>
                                    {preset.label}
                                  </option>
                                ))}
                            </select>
                          </label>
                        </div>
                      )}
                      <label style={{ display: 'grid', gap: 4, minWidth: 0 }}>
                        <span style={{ fontSize: 12, color: palette.muted }}>Text</span>
                        <textarea
                          value={(selected as TextElement).text}
                          onChange={(e) =>
                            updateElement(selected.id, (el) => ({ ...el, text: e.target.value }))
                          }
                          rows={3}
                          style={{
                            padding: 8,
                            borderRadius: 8,
                            border: `1px solid ${palette.border}`,
                            background: palette.cardAlt,
                            color: palette.text,
                            resize: 'vertical',
                            width: '100%',
                            maxWidth: '100%',
                          }}
                        />
                      </label>
                      {/* Object Variable Drill-Down UI */}
                      {objectVariablesInText.length > 0 && (
                        <div
                          style={{
                            display: 'grid',
                            gap: 6,
                            padding: 10,
                            borderRadius: 10,
                            border: `1px solid ${palette.border}`,
                            background: palette.cardAlt,
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              gap: 8,
                            }}
                          >
                            <span style={{ fontWeight: 700, fontSize: 13 }}>Object Variables</span>
                          </div>
                          <div style={{ fontSize: 12, color: palette.muted }}>
                            These variables resolve to objects. Click a property to drill down.
                          </div>
                          {objectVariablesInText.map(({ variableKey, drillPaths, currentPath }) => (
                            <div
                              key={variableKey}
                              style={{
                                padding: 8,
                                borderRadius: 8,
                                border: `1px solid ${palette.border}`,
                                background: palette.cardBg,
                              }}
                            >
                              <div
                                style={{
                                  fontWeight: 600,
                                  marginBottom: 6,
                                  fontSize: 12,
                                  fontFamily: 'monospace',
                                }}
                              >
                                {'{{' + variableKey + (currentPath ? '.' + currentPath : '') + '}}'}
                              </div>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                {drillPaths.map(({ path, value }) => {
                                  const isSelected = currentPath === path;
                                  return (
                                    <button
                                      key={path}
                                      onClick={() => {
                                        const currentText = (selected as TextElement).text || '';
                                        // Build the pattern to match: either the base variable or a drilled-down version
                                        const escaped = variableKey.replace(
                                          /[.*+?^${}()|[\]\\]/g,
                                          '\\$&',
                                        );
                                        // Match {{variableKey}} or {{variableKey.anything}}
                                        const pattern = new RegExp(
                                          `\\{\\{\\s*${escaped}(?:\\.[^{}]+)?\\s*\\}\\}`,
                                          'g',
                                        );
                                        const newText = currentText.replace(
                                          pattern,
                                          `{{${variableKey}.${path}}}`,
                                        );
                                        updateElement(selected.id, (el) => ({
                                          ...el,
                                          text: newText,
                                        }));
                                      }}
                                      className="wysiwyg-chip"
                                      style={{
                                        fontSize: 11,
                                        padding: '4px 8px',
                                        background: isSelected ? palette.accent : undefined,
                                        color: isSelected ? '#fff' : undefined,
                                      }}
                                      title={`Example value: ${JSON.stringify(value)}`}
                                    >
                                      .{path}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      <div
                        style={{
                          display: 'grid',
                          gap: 6,
                          padding: 10,
                          borderRadius: 10,
                          border: `1px solid ${palette.border}`,
                          background: palette.cardAlt,
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 8,
                          }}
                        >
                          <span style={{ fontWeight: 700, fontSize: 13 }}>Conditional content</span>
                          <button
                            disabled={!conditionVariables.length}
                            onClick={() => addTextCondition(selected.id)}
                            className="wysiwyg-chip"
                            style={{ opacity: conditionVariables.length ? 1 : 0.5 }}
                          >
                            Add condition
                          </button>
                        </div>
                        {!conditionVariables.length && (
                          <div style={{ fontSize: 12, color: palette.muted }}>
                            Add a variable to enable conditions.
                          </div>
                        )}
                        {(selected as TextElement).conditions?.map((cond) => {
                          const type = conditionTypeByKey[cond.variableKey] || 'string';
                          const operators = operatorsForType(type);
                          const showValue =
                            cond.operator !== 'truthy' &&
                            cond.operator !== 'falsy' &&
                            cond.operator !== 'isEmpty' &&
                            cond.operator !== 'isNotEmpty';
                          return (
                            <div
                              key={cond.id}
                              style={{
                                display: 'grid',
                                gap: 6,
                                padding: 8,
                                borderRadius: 8,
                                border: `1px solid ${palette.border}`,
                                background: palette.cardBg,
                              }}
                            >
                              <div
                                style={{
                                  display: 'grid',
                                  gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                                  gap: 8,
                                }}
                              >
                                <label style={{ display: 'grid', gap: 4 }}>
                                  <span style={{ fontSize: 12, color: palette.muted }}>
                                    Variable
                                  </span>
                                  <select
                                    value={cond.variableKey}
                                    onChange={(e) => {
                                      const nextKey = e.target.value;
                                      const nextType = conditionTypeByKey[nextKey] || 'string';
                                      const allowed = operatorsForType(nextType);
                                      const nextOp = allowed.includes(cond.operator)
                                        ? cond.operator
                                        : defaultOperatorForType(nextType);
                                      updateTextCondition(selected.id, cond.id, (c) => ({
                                        ...c,
                                        variableKey: nextKey,
                                        operator: nextOp,
                                        value: nextType === 'boolean' ? 'true' : '',
                                      }));
                                    }}
                                    style={{
                                      padding: 8,
                                      borderRadius: 8,
                                      border: `1px solid ${palette.border}`,
                                      background: palette.cardAlt,
                                      color: palette.text,
                                      width: '100%',
                                      minWidth: 0,
                                    }}
                                  >
                                    {!conditionVariables.some(
                                      (v) => v.key === cond.variableKey,
                                    ) && (
                                      <option value={cond.variableKey}>
                                        Custom ({friendlyVariableName(cond.variableKey)})
                                      </option>
                                    )}
                                    {themeConditionVariables.length > 0 && (
                                      <optgroup label="Theme variables">
                                        {themeConditionVariables
                                          .slice()
                                          .sort((a, b) =>
                                            (a.label || '').localeCompare(
                                              b.label || '',
                                              undefined,
                                              { sensitivity: 'base' },
                                            ),
                                          )
                                          .map((v) => (
                                            <option key={`theme-${v.key}`} value={v.key}>
                                              {v.label}
                                            </option>
                                          ))}
                                      </optgroup>
                                    )}
                                    {componentConditionVariables.length > 0 && (
                                      <optgroup label="Component variables">
                                        {componentConditionVariables
                                          .slice()
                                          .sort((a, b) =>
                                            (a.label || '').localeCompare(
                                              b.label || '',
                                              undefined,
                                              { sensitivity: 'base' },
                                            ),
                                          )
                                          .map((v) => (
                                            <option key={`component-${v.key}`} value={v.key}>
                                              {v.label}
                                            </option>
                                          ))}
                                      </optgroup>
                                    )}
                                  </select>
                                </label>
                                <label style={{ display: 'grid', gap: 4 }}>
                                  <span style={{ fontSize: 12, color: palette.muted }}>
                                    Operator
                                  </span>
                                  <select
                                    value={cond.operator}
                                    onChange={(e) =>
                                      updateTextCondition(selected.id, cond.id, (c) => ({
                                        ...c,
                                        operator: e.target.value as any,
                                      }))
                                    }
                                    style={{
                                      padding: 8,
                                      borderRadius: 8,
                                      border: `1px solid ${palette.border}`,
                                      background: palette.cardAlt,
                                      color: palette.text,
                                      width: '100%',
                                      minWidth: 0,
                                    }}
                                  >
                                    {operators
                                      .slice()
                                      .sort((a, b) =>
                                        (operatorLabels[a] || '').localeCompare(
                                          operatorLabels[b] || '',
                                          undefined,
                                          { sensitivity: 'base' },
                                        ),
                                      )
                                      .map((op) => (
                                        <option key={op} value={op}>
                                          {operatorLabels[op]}
                                        </option>
                                      ))}
                                  </select>
                                </label>
                              </div>
                              {showValue && (
                                <label style={{ display: 'grid', gap: 4 }}>
                                  <span style={{ fontSize: 12, color: palette.muted }}>
                                    Compare to
                                  </span>
                                  {type === 'boolean' ? (
                                    <select
                                      value={cond.value || 'true'}
                                      onChange={(e) =>
                                        updateTextCondition(selected.id, cond.id, (c) => ({
                                          ...c,
                                          value: e.target.value,
                                        }))
                                      }
                                      style={{
                                        padding: 8,
                                        borderRadius: 8,
                                        border: `1px solid ${palette.border}`,
                                        background: palette.cardAlt,
                                        color: palette.text,
                                      }}
                                    >
                                      <option value="true">True</option>
                                      <option value="false">False</option>
                                    </select>
                                  ) : (
                                    <input
                                      type={type === 'number' ? 'number' : 'text'}
                                      value={cond.value ?? ''}
                                      onChange={(e) =>
                                        updateTextCondition(selected.id, cond.id, (c) => ({
                                          ...c,
                                          value: e.target.value,
                                        }))
                                      }
                                      style={{
                                        padding: 8,
                                        borderRadius: 8,
                                        border: `1px solid ${palette.border}`,
                                        background: palette.cardAlt,
                                        color: palette.text,
                                      }}
                                    />
                                  )}
                                </label>
                              )}
                              <label style={{ display: 'grid', gap: 4 }}>
                                <span style={{ fontSize: 12, color: palette.muted }}>
                                  Text override
                                </span>
                                <textarea
                                  value={cond.text ?? ''}
                                  onChange={(e) =>
                                    updateTextCondition(selected.id, cond.id, (c) => ({
                                      ...c,
                                      text: e.target.value,
                                    }))
                                  }
                                  rows={2}
                                  style={{
                                    padding: 8,
                                    borderRadius: 8,
                                    border: `1px solid ${palette.border}`,
                                    background: palette.cardAlt,
                                    color: palette.text,
                                    resize: 'vertical',
                                  }}
                                />
                              </label>
                              <button
                                onClick={() => removeTextCondition(selected.id, cond.id)}
                                className="wysiwyg-chip secondary"
                                style={{ justifyContent: 'center' }}
                              >
                                Remove
                              </button>
                            </div>
                          );
                        })}
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                        <label style={{ display: 'grid', gap: 4 }}>
                          <span style={{ fontSize: 12, color: palette.muted }}>Font size</span>
                          <input
                            type="number"
                            min={MIN_FONT_SIZE}
                            value={
                              getCommonValueOfType<TextElement, number>(
                                'text',
                                (el) => el.fontSize,
                              ) ?? ''
                            }
                            onChange={(e) => {
                              const next = Math.max(MIN_FONT_SIZE, Number(e.target.value));
                              updateSelectedOfType<TextElement>('text', (el) => ({
                                ...el,
                                fontSize: next,
                              }));
                            }}
                            style={{
                              padding: 8,
                              borderRadius: 8,
                              border: `1px solid ${palette.border}`,
                              background: palette.cardAlt,
                              color: palette.text,
                              width: '100%',
                            }}
                          />
                        </label>
                        <label style={{ display: 'grid', gap: 4 }}>
                          <span style={{ fontSize: 12, color: palette.muted }}>Width</span>
                          <input
                            type="number"
                            value={
                              getCommonValueOfType<TextElement, number>('text', (el) => el.width) ??
                              ''
                            }
                            onChange={(e) =>
                              updateSelectedOfType<TextElement>('text', (el) => ({
                                ...el,
                                width: Number(e.target.value),
                              }))
                            }
                            style={{
                              padding: 8,
                              borderRadius: 8,
                              border: `1px solid ${palette.border}`,
                              background: palette.cardAlt,
                              color: palette.text,
                              width: '100%',
                            }}
                          />
                        </label>
                      </div>
                      <div role="group" aria-label="Font style" style={{ display: 'grid', gap: 4, minWidth: 0 }}>
                        <span style={{ fontSize: 12, color: palette.muted }}>Font style</span>
                        <div style={{ display: 'flex', gap: 4, padding: '4px 0' }}>
                          {(['Bold', 'Italic', 'Underline'] as const).map((format) => {
                            const isEnabled = (el: TextElement) =>
                              format === 'Bold' ? el.fontWeight === 700
                                : format === 'Italic' ? el.fontStyle === 'italic'
                                  : el.textDecoration === 'underline';
                            const checked = getCommonValueOfType<TextElement, boolean>('text', isEnabled);
                            return (
                              <button
                                  key={format}
                                  type="button"
                                  aria-label={format}
                                  title={format}
                                  aria-pressed={checked === undefined ? 'mixed' : checked}
                                  onClick={() => {
                                    const enabled = checked !== true;
                                    updateSelectedOfType<TextElement>('text', (el) => ({
                                      ...el,
                                      fontStyle: el.fontStyle ?? 'normal',
                                      textDecoration: el.textDecoration ?? 'none',
                                      ...(format === 'Bold' ? { fontWeight: enabled ? 700 : 400 }
                                        : format === 'Italic' ? { fontStyle: enabled ? 'italic' as const : 'normal' as const }
                                          : { textDecoration: enabled ? 'underline' as const : 'none' as const }),
                                    }));
                                  }}
                                  style={{
                                    width: 36,
                                    height: 32,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: 0,
                                    borderRadius: 4,
                                    border: `1px ${checked === undefined ? 'dashed' : 'solid'} ${checked === false ? palette.border : palette.accent}`,
                                    background: checked === true ? palette.accent : palette.cardAlt,
                                    color: checked === true ? '#ffffff' : palette.text,
                                    fontSize: 16,
                                  }}
                                >
                                <span aria-hidden="true" style={{
                                  fontWeight: format === 'Bold' ? 700 : undefined,
                                  fontStyle: format === 'Italic' ? 'italic' : undefined,
                                  textDecoration: format === 'Underline' ? 'underline' : undefined,
                                }}>{format === 'Bold' ? 'B' : format === 'Italic' ? 'I' : 'U'}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      <label style={{ display: 'grid', gap: 4, minWidth: 0 }}>
                        <span style={{ fontSize: 12, color: palette.muted }}>Case</span>
                        <select
                          value={
                            getCommonValueOfType<TextElement, string>(
                              'text',
                              (el) => el.textTransform || 'none',
                            ) ?? 'none'
                          }
                          onChange={(e) =>
                            updateSelectedOfType<TextElement>('text', (el) => ({
                              ...el,
                              textTransform: e.target.value as TextElement['textTransform'],
                            }))
                          }
                          style={{
                            padding: 8,
                            borderRadius: 8,
                            border: `1px solid ${palette.border}`,
                            background: palette.cardAlt,
                            color: palette.text,
                            width: '100%',
                            maxWidth: '100%',
                          }}
                        >
                          <option value="none">Default</option>
                          <option value="lowercase">lowercase</option>
                          <option value="uppercase">UPPERCASE</option>
                        </select>
                      </label>
                      {caps.supportsLetterSpacing && (
                      <label style={{ display: 'grid', gap: 4 }}>
                        <span style={{ fontSize: 12, color: palette.muted }}>
                          Letter spacing (em)
                        </span>
                        <input
                          type="number"
                          step={0.01}
                          value={
                            getCommonValueOfType<TextElement, number>(
                              'text',
                              (el) => el.letterSpacing ?? 0,
                            ) ?? ''
                          }
                          onChange={(e) => {
                            const val = parseFloat(e.target.value);
                            updateSelectedOfType<TextElement>('text', (el) => ({
                              ...el,
                              letterSpacing: isNaN(val) ? 0 : val,
                            }));
                          }}
                          placeholder="0"
                          style={{
                            padding: 8,
                            borderRadius: 8,
                            border: `1px solid ${palette.border}`,
                            background: palette.cardAlt,
                            color: palette.text,
                            width: '100%',
                          }}
                        />
                      </label>
                      )}
                      <label style={{ display: 'grid', gap: 4 }}>
                        <span style={{ fontSize: 12, color: palette.muted }}>Line spacing</span>
                        <input
                          type="number"
                          step={0.1}
                          min={0.5}
                          max={3}
                          value={
                            getCommonValueOfType<TextElement, number>(
                              'text',
                              (el) => el.lineHeight ?? 1,
                            ) ?? ''
                          }
                          onChange={(e) => {
                            const val = parseFloat(e.target.value);
                            updateSelectedOfType<TextElement>('text', (el) => ({
                              ...el,
                              lineHeight: isNaN(val) ? 1 : val,
                            }));
                          }}
                          placeholder="1"
                          style={{
                            padding: 8,
                            borderRadius: 8,
                            border: `1px solid ${palette.border}`,
                            background: palette.cardAlt,
                            color: palette.text,
                            width: '100%',
                          }}
                        />
                      </label>
                      <label style={{ display: 'grid', gap: 4, minWidth: 0 }}>
                        <span style={{ fontSize: 12, color: palette.muted }}>Text alignment</span>
                        <select
                          value={
                            getCommonValueOfType<TextElement, string>(
                              'text',
                              (el) => el.align || 'left',
                            ) ?? ''
                          }
                          onChange={(e) =>
                            updateSelectedOfType<TextElement>('text', (el) => ({
                              ...el,
                              align: e.target.value as TextElement['align'],
                            }))
                          }
                          style={{
                            padding: 8,
                            borderRadius: 8,
                            border: `1px solid ${palette.border}`,
                            background: palette.cardAlt,
                            color: palette.text,
                            width: '100%',
                            maxWidth: '100%',
                          }}
                        >
                          <option value="left">Left</option>
                          <option value="center">Center</option>
                          <option value="right">Right</option>
                        </select>
                      </label>
                      {caps.supportsCustomFonts && (
                      <label style={{ display: 'grid', gap: 4, minWidth: 0 }}>
                        <span style={{ fontSize: 12, color: palette.muted }}>Font family</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span
                            style={{
                              padding: 8,
                              borderRadius: 8,
                              border: `1px solid ${palette.border}`,
                              background: palette.cardAlt,
                              color: palette.text,
                              flex: 1,
                              fontSize: 13,
                            }}
                          >
                            {(selected as TextElement).fontAsset?.displayName
                              ? (selected as TextElement).fontAsset!.displayName.replace(
                                  /\.[^.]+$/,
                                  '',
                                )
                              : (selected as TextElement)['font-asset']?.['mongo-id']
                                ? 'Legacy Font'
                                : 'Roboto Condensed'}
                          </span>
                          <button
                            onClick={() => {
                              // Apply font to all selected text elements
                              setAssetBrowserState({
                                isOpen: true,
                                category: 'font',
                                targetElementId: selected.id,
                              });
                            }}
                            className="wysiwyg-chip secondary"
                          >
                            Choose Font
                          </button>
                        </div>
                      </label>
                      )}
                      {template.outputFormat === 'zpl' && (
                        <label style={{ display: 'grid', gap: 4, minWidth: 0 }}>
                          <span style={{ fontSize: 12, color: palette.muted }}>
                            Zebra resident font
                          </span>
                          <select
                            aria-label="Zebra resident font"
                            data-testid="label-editor-zpl-font"
                            value={
                              getCommonValueOfType<TextElement, string>(
                                'text',
                                (el) => resolveZplFontFamily(el.fontFamily),
                              ) ?? ''
                            }
                            onChange={(e) =>
                              updateSelectedOfType<TextElement>('text', (el) => ({
                                ...el,
                                fontFamily: e.target.value,
                                fontAsset: undefined,
                                'font-asset': undefined,
                              }))
                            }
                            style={{
                              padding: 8,
                              borderRadius: 8,
                              border: `1px solid ${palette.border}`,
                              background: palette.cardAlt,
                              color: palette.text,
                              width: '100%',
                              maxWidth: '100%',
                            }}
                          >
                            {ZPL_FONT_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </label>
                      )}
                      {caps.supportsColor && (
                      <>
                      <label style={{ display: 'grid', gap: 4, minWidth: 0 }}>
                        <span style={{ fontSize: 12, color: palette.muted }}>Color</span>
                          <input
                            type="color"
                            value={
                              getCommonValueOfType<TextElement, string>('text', (el) => el.color) ??
                              '#000000'
                            }
                            onChange={(e) =>
                              updateSelectedOfType<TextElement>('text', (el) => ({
                                ...el,
                                color: e.target.value,
                              }))
                            }
                            style={{
                              height: 36,
                              width: '100%',
                              maxWidth: 160,
                              background: palette.cardAlt,
                              border: `1px solid ${palette.border}`,
                              borderRadius: 8,
                            }}
                          />
                      </label>
                      {renderColorConditions(
                        selected.id,
                        'colorConditions',
                        (selected as TextElement).colorConditions,
                        (selected as TextElement).color || '#000000',
                        'color',
                      )}
                      </>
                      )}
                    </>
                  ) : selected.type === 'image' ? (
                    <>
                      {caps.imageNote && (
                        <div style={{
                          padding: '8px 12px',
                          borderRadius: 8,
                          background: '#fff3e0',
                          border: '1px solid #ffe0b2',
                          fontSize: 12,
                          color: '#e65100',
                          marginBottom: 8,
                        }}>
                          {caps.imageNote}
                        </div>
                      )}
                      <div style={{ display: 'grid', gap: 4, minWidth: 0 }}>
                        <span style={{ fontSize: 12, color: palette.muted }}>Image Source</span>
                        {(() => {
                          const imgEl = selected as ImageElement & {
                            'image-asset'?: { 'mongo-id': string };
                          };
                          const legacyMongoId = imgEl['image-asset']?.['mongo-id'];
                          if (imgEl.imageAsset?.displayName) {
                            return (
                              <div
                                style={{
                                  padding: 8,
                                  borderRadius: 8,
                                  border: `1px solid ${palette.border}`,
                                  background: palette.cardAlt,
                                  color: palette.text,
                                  fontSize: 13,
                                }}
                              >
                                {imgEl.imageAsset.displayName}
                              </div>
                            );
                          }
                          if (legacyMongoId) {
                            return (
                              <div
                                style={{
                                  padding: 8,
                                  borderRadius: 8,
                                  border: `1px solid ${palette.border}`,
                                  background: palette.cardAlt,
                                  color: palette.text,
                                  fontSize: 13,
                                }}
                              >
                                <span style={{ color: palette.muted }}>Legacy image: </span>
                                <span style={{ fontFamily: 'monospace', fontSize: 11 }}>
                                  {legacyMongoId.slice(0, 8)}...
                                </span>
                              </div>
                            );
                          }
                          if (imgEl.url && imgEl.url.startsWith('data:')) {
                            return (
                              <div
                                style={{
                                  padding: 8,
                                  borderRadius: 8,
                                  border: `1px solid ${palette.border}`,
                                  background: palette.cardAlt,
                                  color: palette.text,
                                  fontSize: 13,
                                }}
                              >
                                Embedded image (data URL)
                              </div>
                            );
                          }
                          return (
                            <div
                              style={{
                                padding: 8,
                                borderRadius: 8,
                                border: `1px solid ${palette.border}`,
                                background: palette.cardAlt,
                                color: palette.muted,
                                fontSize: 13,
                                fontStyle: 'italic',
                              }}
                            >
                              No image loaded
                            </div>
                          );
                        })()}
                      </div>
                      <div
                        style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}
                      >
                        <button
                          onClick={() => {
                            setAssetBrowserState({
                              isOpen: true,
                              category: 'image',
                              targetElementId: selected.id,
                            });
                          }}
                          className="wysiwyg-chip secondary"
                        >
                          Choose Image
                        </button>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                        <label style={{ display: 'grid', gap: 4 }}>
                          <span style={{ fontSize: 12, color: palette.muted }}>Width</span>
                          <input
                            type="number"
                            value={
                              getCommonValueOfType<ImageElement, number>(
                                'image',
                                (el) => el.width,
                              ) ?? ''
                            }
                            onChange={(e) =>
                              updateSelectedOfType<ImageElement>('image', (el) => ({
                                ...el,
                                width: Number(e.target.value),
                              }))
                            }
                            style={{
                              padding: 8,
                              borderRadius: 8,
                              border: `1px solid ${palette.border}`,
                              background: palette.cardAlt,
                              color: palette.text,
                              width: '100%',
                            }}
                          />
                        </label>
                        <label style={{ display: 'grid', gap: 4 }}>
                          <span style={{ fontSize: 12, color: palette.muted }}>Height</span>
                          <input
                            type="number"
                            value={
                              getCommonValueOfType<ImageElement, number>(
                                'image',
                                (el) => el.height,
                              ) ?? ''
                            }
                            onChange={(e) =>
                              updateSelectedOfType<ImageElement>('image', (el) => ({
                                ...el,
                                height: Number(e.target.value),
                              }))
                            }
                            style={{
                              padding: 8,
                              borderRadius: 8,
                              border: `1px solid ${palette.border}`,
                              background: palette.cardAlt,
                              color: palette.text,
                              width: '100%',
                            }}
                          />
                        </label>
                      </div>
                      <div
                        style={{
                          display: 'grid',
                          gap: 6,
                          padding: 10,
                          borderRadius: 10,
                          border: `1px solid ${palette.border}`,
                          background: palette.cardAlt,
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 8,
                          }}
                        >
                          <span style={{ fontWeight: 700, fontSize: 13 }}>Conditional image</span>
                          <button
                            disabled={!conditionVariables.length}
                            onClick={() => addImageCondition(selected.id)}
                            className="wysiwyg-chip"
                            style={{ opacity: conditionVariables.length ? 1 : 0.5 }}
                          >
                            Add condition
                          </button>
                        </div>
                        {!conditionVariables.length && (
                          <div style={{ fontSize: 12, color: palette.muted }}>
                            Add a variable to enable conditions.
                          </div>
                        )}
                        {(selected as ImageElement).conditions?.map((cond) => {
                          const type = conditionTypeByKey[cond.variableKey] || 'string';
                          const operators = operatorsForType(type);
                          const showValue =
                            cond.operator !== 'truthy' &&
                            cond.operator !== 'falsy' &&
                            cond.operator !== 'isEmpty' &&
                            cond.operator !== 'isNotEmpty';
                          return (
                            <div
                              key={cond.id}
                              style={{
                                display: 'grid',
                                gap: 6,
                                padding: 8,
                                borderRadius: 8,
                                border: `1px solid ${palette.border}`,
                                background: palette.cardBg,
                              }}
                            >
                              <div
                                style={{
                                  display: 'grid',
                                  gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                                  gap: 8,
                                }}
                              >
                                <label style={{ display: 'grid', gap: 4 }}>
                                  <span style={{ fontSize: 12, color: palette.muted }}>
                                    Variable
                                  </span>
                                  <select
                                    value={cond.variableKey}
                                    onChange={(e) => {
                                      const nextKey = e.target.value;
                                      const nextType = conditionTypeByKey[nextKey] || 'string';
                                      const allowed = operatorsForType(nextType);
                                      const nextOp = allowed.includes(cond.operator)
                                        ? cond.operator
                                        : defaultOperatorForType(nextType);
                                      updateImageCondition(selected.id, cond.id, (c) => ({
                                        ...c,
                                        variableKey: nextKey,
                                        operator: nextOp,
                                        value: nextType === 'boolean' ? 'true' : '',
                                      }));
                                    }}
                                    style={{
                                      padding: 8,
                                      borderRadius: 8,
                                      border: `1px solid ${palette.border}`,
                                      background: palette.cardAlt,
                                      color: palette.text,
                                      width: '100%',
                                      minWidth: 0,
                                    }}
                                  >
                                    {!conditionVariables.some(
                                      (v) => v.key === cond.variableKey,
                                    ) && (
                                      <option value={cond.variableKey}>
                                        Custom ({friendlyVariableName(cond.variableKey)})
                                      </option>
                                    )}
                                    {themeConditionVariables.length > 0 && (
                                      <optgroup label="Theme variables">
                                        {themeConditionVariables
                                          .slice()
                                          .sort((a, b) =>
                                            (a.label || '').localeCompare(
                                              b.label || '',
                                              undefined,
                                              { sensitivity: 'base' },
                                            ),
                                          )
                                          .map((v) => (
                                            <option key={`theme-${v.key}`} value={v.key}>
                                              {v.label}
                                            </option>
                                          ))}
                                      </optgroup>
                                    )}
                                    {componentConditionVariables.length > 0 && (
                                      <optgroup label="Component variables">
                                        {componentConditionVariables
                                          .slice()
                                          .sort((a, b) =>
                                            (a.label || '').localeCompare(
                                              b.label || '',
                                              undefined,
                                              { sensitivity: 'base' },
                                            ),
                                          )
                                          .map((v) => (
                                            <option key={`component-${v.key}`} value={v.key}>
                                              {v.label}
                                            </option>
                                          ))}
                                      </optgroup>
                                    )}
                                  </select>
                                </label>
                                <label style={{ display: 'grid', gap: 4 }}>
                                  <span style={{ fontSize: 12, color: palette.muted }}>
                                    Operator
                                  </span>
                                  <select
                                    value={cond.operator}
                                    onChange={(e) =>
                                      updateImageCondition(selected.id, cond.id, (c) => ({
                                        ...c,
                                        operator: e.target.value as any,
                                      }))
                                    }
                                    style={{
                                      padding: 8,
                                      borderRadius: 8,
                                      border: `1px solid ${palette.border}`,
                                      background: palette.cardAlt,
                                      color: palette.text,
                                      width: '100%',
                                      minWidth: 0,
                                    }}
                                  >
                                    {operators
                                      .slice()
                                      .sort((a, b) =>
                                        (operatorLabels[a] || '').localeCompare(
                                          operatorLabels[b] || '',
                                          undefined,
                                          { sensitivity: 'base' },
                                        ),
                                      )
                                      .map((op) => (
                                        <option key={op} value={op}>
                                          {operatorLabels[op]}
                                        </option>
                                      ))}
                                  </select>
                                </label>
                              </div>
                              {showValue && (
                                <label style={{ display: 'grid', gap: 4 }}>
                                  <span style={{ fontSize: 12, color: palette.muted }}>
                                    Compare to
                                  </span>
                                  {type === 'boolean' ? (
                                    <select
                                      value={cond.value || 'true'}
                                      onChange={(e) =>
                                        updateImageCondition(selected.id, cond.id, (c) => ({
                                          ...c,
                                          value: e.target.value,
                                        }))
                                      }
                                      style={{
                                        padding: 8,
                                        borderRadius: 8,
                                        border: `1px solid ${palette.border}`,
                                        background: palette.cardAlt,
                                        color: palette.text,
                                      }}
                                    >
                                      <option value="true">True</option>
                                      <option value="false">False</option>
                                    </select>
                                  ) : (
                                    <input
                                      type={type === 'number' ? 'number' : 'text'}
                                      value={cond.value ?? ''}
                                      onChange={(e) =>
                                        updateImageCondition(selected.id, cond.id, (c) => ({
                                          ...c,
                                          value: e.target.value,
                                        }))
                                      }
                                      style={{
                                        padding: 8,
                                        borderRadius: 8,
                                        border: `1px solid ${palette.border}`,
                                        background: palette.cardAlt,
                                        color: palette.text,
                                      }}
                                    />
                                  )}
                                </label>
                              )}
                              <div style={{ display: 'grid', gap: 4 }}>
                                <span style={{ fontSize: 12, color: palette.muted }}>
                                  Conditional Image
                                </span>
                                {cond.imageAsset?.displayName ? (
                                  <div
                                    style={{
                                      padding: 8,
                                      borderRadius: 8,
                                      border: `1px solid ${palette.border}`,
                                      background: palette.cardAlt,
                                      color: palette.text,
                                      fontSize: 13,
                                    }}
                                  >
                                    {cond.imageAsset.displayName}
                                  </div>
                                ) : (
                                  <div
                                    style={{
                                      padding: 8,
                                      borderRadius: 8,
                                      border: `1px solid ${palette.border}`,
                                      background: palette.cardAlt,
                                      color: palette.muted,
                                      fontSize: 13,
                                      fontStyle: 'italic',
                                    }}
                                  >
                                    No image selected
                                  </div>
                                )}
                                <button
                                  onClick={() => {
                                    setAssetBrowserState({
                                      isOpen: true,
                                      category: 'image',
                                      targetElementId: selected.id,
                                      conditionId: cond.id,
                                    });
                                  }}
                                  className="wysiwyg-chip secondary"
                                >
                                  Choose Image
                                </button>
                              </div>
                              <button
                                onClick={() => removeImageCondition(selected.id, cond.id)}
                                className="wysiwyg-chip secondary"
                                style={{ justifyContent: 'center' }}
                              >
                                Remove
                              </button>
                            </div>
                          );
                        })}
                      </div>
                      <label style={{ display: 'grid', gap: 4, minWidth: 0 }}>
                        <span style={{ fontSize: 12, color: palette.muted }}>Fit</span>
                        <select
                          value={(selected as ImageElement).fit}
                          onChange={(e) =>
                            updateElement(selected.id, (el) => ({
                              ...el,
                              fit: e.target.value as ImageElement['fit'],
                            }))
                          }
                          style={{
                            padding: 8,
                            borderRadius: 8,
                            border: `1px solid ${palette.border}`,
                            background: palette.cardAlt,
                            color: palette.text,
                            width: '100%',
                            maxWidth: '100%',
                          }}
                        >
                          <option value="contain">Contain</option>
                          <option value="cover">Cover</option>
                        </select>
                      </label>
                      {caps.supportsOpacity && (
                      <label style={{ display: 'grid', gap: 4, minWidth: 0 }}>
                        <span style={{ fontSize: 12, color: palette.muted }}>Opacity</span>
                        <input
                          type="range"
                          min={0.1}
                          max={1}
                          step={0.05}
                          value={(selected as ImageElement).opacity}
                          onChange={(e) =>
                            updateElement(selected.id, (el) => ({
                              ...el,
                              opacity: Number(e.target.value),
                            }))
                          }
                          style={{ width: '100%' }}
                        />
                      </label>
                      )}
                    </>
                  ) : selected.type === 'qr' ? (
                    <>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                        <label style={{ display: 'grid', gap: 4 }}>
                          <span style={{ fontSize: 12, color: palette.muted }}>Size (px)</span>
                          <QrSizeInput
                            key={selected.id}
                            min={getQrTemplateGeometry({
                              value: (selected as QrElement).data || QR_DESIGN_PAYLOAD,
                              requestedSize: 0,
                              dpi: template.dpi ?? 72,
                              outputFormat: template.outputFormat,
                              margin: 0,
                              errorCorrectionLevel: (selected as QrElement).errorCorrectionLevel ?? QR_DEFAULT_ERROR_CORRECTION,
                            }).minimumSize}
                            value={
                              getCommonValueOfType<QrElement, number>('qr', (el) => el.width) ?? ''
                            }
                            onChange={(requestedSize) => {
                              updateSelectedOfType<QrElement>('qr', (el) => ({
                                ...el,
                                ...(() => {
                                  const size = getQrTemplateGeometry({
                                    value: el.data || QR_DESIGN_PAYLOAD,
                                    requestedSize,
                                    dpi: template.dpi ?? 72,
                                    outputFormat: template.outputFormat,
                                    margin: 0,
                                    errorCorrectionLevel: el.errorCorrectionLevel ?? QR_DEFAULT_ERROR_CORRECTION,
                                  }).size;
                                  const margin = getResizedQrMargin({
                                    value: el.data || QR_DESIGN_PAYLOAD,
                                    requestedSize: size,
                                    dpi: template.dpi ?? 72,
                                    outputFormat: template.outputFormat,
                                    margin: el.margin,
                                    errorCorrectionLevel: el.errorCorrectionLevel ?? QR_DEFAULT_ERROR_CORRECTION,
                                  }, el.width);
                                  return { width: size, height: size, margin };
                                })(),
                              }));
                            }}
                            style={{
                              padding: 8,
                              borderRadius: 8,
                              border: `1px solid ${palette.border}`,
                              background: palette.cardAlt,
                              color: palette.text,
                              width: '100%',
                            }}
                          />
                        </label>
                        <label style={{ display: 'grid', gap: 4 }}>
                          <span style={{ fontSize: 12, color: palette.muted }}>Margin (px)</span>
                          <QrSizeInput
                            key={selected.id}
                            min={Math.min(...selectedQrMargins.map((margin) => margin.minimum))}
                            max={Math.max(...selectedQrMargins.map((margin) => margin.maximum))}
                            step="any"
                            value={
                              getCommonValueOfType<QrElement, number>('qr', (el) => getQrElementMargin(el).value) ?? ''
                            }
                            onChange={(margin) => {
                              updateSelectedOfType<QrElement>('qr', (el) => ({
                                ...el,
                                margin: getQrElementMargin({ ...el, margin }).value,
                              }));
                            }}
                            style={{
                              padding: 8,
                              borderRadius: 8,
                              border: `1px solid ${palette.border}`,
                              background: palette.cardAlt,
                              color: palette.text,
                              width: '100%',
                            }}
                          />
                        </label>
                      </div>
                      {hasUndersizedSelectedQr && (
                        <div className="alert warning" role="alert" style={{ marginBottom: 0 }}>
                          QRs this size may suffer from lower reliability. Many 2D scanners have a
                          minimum size requirement. Also, be sure to create enough space around the
                          Retail ID so it is scannable.
                        </div>
                      )}
                      {caps.supportsColor && (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                        <label style={{ display: 'grid', gap: 4 }}>
                          <span style={{ fontSize: 12, color: palette.muted }}>Foreground</span>
                          <input
                            type="color"
                            value={
                              getCommonValueOfType<QrElement, string>('qr', (el) => el.fg) ??
                              '#000000'
                            }
                            onChange={(e) =>
                              updateSelectedOfType<QrElement>('qr', (el) => ({
                                ...el,
                                fg: e.target.value,
                                image: generateSignetLogoSvg(e.target.value),
                              }))
                            }
                            style={{
                              height: 36,
                              width: '100%',
                              maxWidth: 160,
                              background: palette.cardAlt,
                              border: `1px solid ${palette.border}`,
                              borderRadius: 8,
                            }}
                          />
                        </label>
                        <label style={{ display: 'grid', gap: 4 }}>
                          <span style={{ fontSize: 12, color: palette.muted }}>Background</span>
                          <input
                            type="color"
                            value={
                              getCommonValueOfType<QrElement, string>('qr', (el) => el.bg) ??
                              '#FFFFFF'
                            }
                            onChange={(e) =>
                              updateSelectedOfType<QrElement>('qr', (el) => ({
                                ...el,
                                bg: e.target.value,
                              }))
                            }
                            style={{
                              height: 36,
                              width: '100%',
                              maxWidth: 160,
                              background: palette.cardAlt,
                              border: `1px solid ${palette.border}`,
                              borderRadius: 8,
                            }}
                          />
                        </label>
                      </div>
                      )}
                      {caps.supportsColor && renderColorConditions(
                        selected.id,
                        'fgConditions',
                        (selected as QrElement).fgConditions,
                        (selected as QrElement).fg || '#000000',
                        'foreground',
                      )}
                      {caps.supportsColor && renderColorConditions(
                        selected.id,
                        'bgConditions',
                        (selected as QrElement).bgConditions,
                        (selected as QrElement).bg || '#FFFFFF',
                        'background',
                      )}
                    </>
                  ) : selected.type === 'barcode' ? (
                    <>
                      <label style={{ display: 'grid', gap: 4, minWidth: 0 }}>
                        <span style={{ fontSize: 12, color: palette.muted }}>Data source</span>
                        <select
                          value={(selected as BarcodeElement).dataSource || 'static'}
                          onChange={(e) =>
                            updateElement(selected.id, (el) => ({
                              ...el,
                              dataSource: e.target.value as BarcodeDataSource,
                            }))
                          }
                          style={{
                            padding: 8,
                            borderRadius: 8,
                            border: `1px solid ${palette.border}`,
                            background: palette.cardAlt,
                            color: palette.text,
                            width: '100%',
                          }}
                        >
                          <option value="static">Static or Theme Variable Value</option>
                          <option value="productUpc">Pull from Product UPC</option>
                        </select>
                      </label>
                      <label style={{ display: 'grid', gap: 4, minWidth: 0 }}>
                        <span style={{ fontSize: 12, color: palette.muted }}>
                          {(selected as BarcodeElement).dataSource === 'productUpc'
                            ? 'Default Value When Product is Missing UPC'
                            : 'Barcode data'}
                        </span>
                        <textarea
                          value={(selected as BarcodeElement).data}
                          onChange={(e) =>
                            updateElement(selected.id, (el) => ({ ...el, data: e.target.value }))
                          }
                          rows={2}
                          style={{
                            padding: 8,
                            borderRadius: 8,
                            border: `1px solid ${palette.border}`,
                            background: palette.cardAlt,
                            color: palette.text,
                            resize: 'vertical',
                            width: '100%',
                            maxWidth: '100%',
                          }}
                        />
                      </label>
                      <label style={{ display: 'grid', gap: 4, minWidth: 0 }}>
                        <span style={{ fontSize: 12, color: palette.muted }}>Format</span>
                        <select
                          value={(selected as BarcodeElement).format}
                          onChange={(e) =>
                            updateElement(selected.id, (el) => ({
                              ...el,
                              format: e.target.value as BarcodeFormat,
                            }))
                          }
                          style={{
                            padding: 8,
                            borderRadius: 8,
                            border: `1px solid ${palette.border}`,
                            background: palette.cardAlt,
                            color: palette.text,
                            width: '100%',
                          }}
                        >
                          <option value="CODE128">CODE128</option>
                          <option value="CODE128A">CODE128A</option>
                          <option value="CODE128B">CODE128B</option>
                          <option value="CODE128C">CODE128C</option>
                          <option value="EAN13">EAN13</option>
                          <option value="EAN8">EAN8</option>
                          <option value="EAN5">EAN5</option>
                          <option value="EAN2">EAN2</option>
                          <option value="UPC">UPC</option>
                          <option value="UPCE">UPCE</option>
                          <option value="CODE39">CODE39</option>
                          <option value="ITF14">ITF14</option>
                          <option value="ITF">ITF</option>
                          <option value="MSI">MSI</option>
                          <option value="MSI10">MSI10</option>
                          <option value="MSI11">MSI11</option>
                          <option value="MSI1010">MSI1010</option>
                          <option value="MSI1110">MSI1110</option>
                          <option value="pharmacode">Pharmacode</option>
                          <option value="codabar">Codabar</option>
                        </select>
                      </label>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                        <label style={{ display: 'grid', gap: 4 }}>
                          <span style={{ fontSize: 12, color: palette.muted }}>Bar width</span>
                          <input
                            type="number"
                            min={0}
                            step={0.1}
                            value={
                              getCommonValueOfType<BarcodeElement, number>(
                                'barcode',
                                (el) => el.barWidth,
                              ) ?? ''
                            }
                            onChange={(e) =>
                              updateSelectedOfType<BarcodeElement>('barcode', (el) => ({
                                ...el,
                                barWidth: Math.max(0, Number(e.target.value)),
                              }))
                            }
                            style={{
                              padding: 8,
                              borderRadius: 8,
                              border: `1px solid ${palette.border}`,
                              background: palette.cardAlt,
                              color: palette.text,
                              width: '100%',
                            }}
                          />
                        </label>
                        <label style={{ display: 'grid', gap: 4 }}>
                          <span style={{ fontSize: 12, color: palette.muted }}>Height (px)</span>
                          <input
                            type="number"
                            min={0}
                            value={
                              getCommonValueOfType<BarcodeElement, number>(
                                'barcode',
                                (el) => el.height,
                              ) ?? ''
                            }
                            onChange={(e) =>
                              updateSelectedOfType<BarcodeElement>('barcode', (el) => ({
                                ...el,
                                height: Math.max(0, Number(e.target.value)),
                              }))
                            }
                            style={{
                              padding: 8,
                              borderRadius: 8,
                              border: `1px solid ${palette.border}`,
                              background: palette.cardAlt,
                              color: palette.text,
                              width: '100%',
                            }}
                          />
                        </label>
                        <label style={{ display: 'grid', gap: 4 }}>
                          <span style={{ fontSize: 12, color: palette.muted }}>Margin (px)</span>
                          <input
                            type="number"
                            min={0}
                            value={
                              getCommonValueOfType<BarcodeElement, number>(
                                'barcode',
                                (el) => el.margin,
                              ) ?? ''
                            }
                            onChange={(e) =>
                              updateSelectedOfType<BarcodeElement>('barcode', (el) => ({
                                ...el,
                                margin: Math.max(0, Number(e.target.value)),
                              }))
                            }
                            style={{
                              padding: 8,
                              borderRadius: 8,
                              border: `1px solid ${palette.border}`,
                              background: palette.cardAlt,
                              color: palette.text,
                              width: '100%',
                            }}
                          />
                        </label>
                      </div>
                      {caps.supportsColor && (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                        <label style={{ display: 'grid', gap: 4 }}>
                          <span style={{ fontSize: 12, color: palette.muted }}>Line color</span>
                          <input
                            type="color"
                            value={
                              getCommonValueOfType<BarcodeElement, string>(
                                'barcode',
                                (el) => el.lineColor,
                              ) ?? '#000000'
                            }
                            onChange={(e) =>
                              updateSelectedOfType<BarcodeElement>('barcode', (el) => ({
                                ...el,
                                lineColor: e.target.value,
                              }))
                            }
                            style={{
                              height: 36,
                              width: '100%',
                              maxWidth: 160,
                              background: palette.cardAlt,
                              border: `1px solid ${palette.border}`,
                              borderRadius: 8,
                            }}
                          />
                        </label>
                        <label style={{ display: 'grid', gap: 4 }}>
                          <span style={{ fontSize: 12, color: palette.muted }}>Background</span>
                          <input
                            type="color"
                            value={
                              getCommonValueOfType<BarcodeElement, string>(
                                'barcode',
                                (el) => el.background,
                              ) ?? '#FFFFFF'
                            }
                            onChange={(e) =>
                              updateSelectedOfType<BarcodeElement>('barcode', (el) => ({
                                ...el,
                                background: e.target.value,
                              }))
                            }
                            style={{
                              height: 36,
                              width: '100%',
                              maxWidth: 160,
                              background: palette.cardAlt,
                              border: `1px solid ${palette.border}`,
                              borderRadius: 8,
                            }}
                          />
                        </label>
                      </div>
                      )}
                      {caps.supportsColor && renderColorConditions(
                        selected.id,
                        'lineColorConditions',
                        (selected as BarcodeElement).lineColorConditions,
                        (selected as BarcodeElement).lineColor || '#000000',
                        'line color',
                      )}
                      {caps.supportsColor && renderColorConditions(
                        selected.id,
                        'backgroundConditions',
                        (selected as BarcodeElement).backgroundConditions,
                        (selected as BarcodeElement).background || '#FFFFFF',
                        'background',
                      )}
                      <div style={{ fontSize: 12, color: palette.muted }}>
                        Supports variable tokens like {'{{variableKey}}'}. Bar width is a multiplier
                        (1-4).
                      </div>
                    </>
                  ) : selected.type === 'shape' ? (
                    <>
                      <label style={{ display: 'grid', gap: 4 }}>
                        <span style={{ fontSize: 12, color: palette.muted }}>Shape type</span>
                        <select
                          value={(selected as ShapeElement).shapeType}
                          onChange={(e) => {
                            const newShapeType = e.target.value as ShapeType;
                            const shape = selected as ShapeElement;

                            // Switching TO svg from line/rectangle
                            if (newShapeType === 'svg' && shape.shapeType !== 'svg') {
                              updateElement(selected.id, (el) => ({
                                ...el,
                                shapeType: newShapeType,
                                svgContent: '',
                                width: 100,
                                height: 100,
                                v1: { x: 0, y: 0 },
                                v2: { x: 100, y: 0 },
                                v3: undefined,
                                v4: undefined,
                                fillColor: undefined,
                              }));
                              // Switching FROM svg to line/rectangle
                            } else if (shape.shapeType === 'svg' && newShapeType !== 'svg') {
                              if (newShapeType === 'line') {
                                updateElement(selected.id, (el) => ({
                                  ...el,
                                  shapeType: newShapeType,
                                  v1: { x: 0, y: 0 },
                                  v2: { x: 100, y: 0 },
                                  v3: undefined,
                                  v4: undefined,
                                  fillColor: undefined,
                                  svgContent: undefined,
                                  width: undefined,
                                  height: undefined,
                                }));
                              } else {
                                updateElement(selected.id, (el) => ({
                                  ...el,
                                  shapeType: newShapeType,
                                  v1: { x: 0, y: 0 },
                                  v2: { x: 80, y: 0 },
                                  v3: { x: 80, y: 60 },
                                  v4: { x: 0, y: 60 },
                                  fillColor: 'transparent',
                                  svgContent: undefined,
                                  width: undefined,
                                  height: undefined,
                                }));
                              }
                            } else if (newShapeType === 'line' && shape.shapeType === 'rectangle') {
                              updateElement(selected.id, (el) => ({
                                ...el,
                                shapeType: newShapeType,
                                v3: undefined,
                                v4: undefined,
                                fillColor: undefined,
                              }));
                            } else if (newShapeType === 'rectangle' && shape.shapeType === 'line') {
                              const dx = shape.v2.x - shape.v1.x;
                              const dy = shape.v2.y - shape.v1.y;
                              const len = Math.sqrt(dx * dx + dy * dy) || 1;
                              const perpX = (-dy / len) * 40;
                              const perpY = (dx / len) * 40;
                              updateElement(selected.id, (el) => ({
                                ...el,
                                shapeType: newShapeType,
                                v3: { x: shape.v2.x + perpX, y: shape.v2.y + perpY },
                                v4: { x: shape.v1.x + perpX, y: shape.v1.y + perpY },
                                fillColor: 'transparent',
                              }));
                            }
                          }}
                          style={{
                            padding: 8,
                            borderRadius: 8,
                            border: `1px solid ${palette.border}`,
                            background: palette.cardAlt,
                            color: palette.text,
                            width: '100%',
                          }}
                        >
                          <option value="line">Line</option>
                          <option value="rectangle">Rectangle</option>
                          {caps.supportsSvgShapes && <option value="svg">SVG (inline)</option>}
                        </select>
                      </label>

                      {/* SVG-specific controls */}
                      {(selected as ShapeElement).shapeType === 'svg' && (
                        <>
                          <label style={{ display: 'grid', gap: 4 }}>
                            <span style={{ fontSize: 12, color: palette.muted }}>SVG Content</span>
                            <textarea
                              value={(selected as ShapeElement).svgContent || ''}
                              onChange={(e) => {
                                const parsed = parseSvgContent(e.target.value);
                                if (parsed.valid) {
                                  // Auto-set width/height from SVG's intrinsic dimensions
                                  updateElement(selected.id, (el) => ({
                                    ...el,
                                    svgContent: parsed.sanitized,
                                    width: parsed.width,
                                    height: parsed.height,
                                    v2: { x: parsed.width, y: 0 },
                                  }));
                                } else {
                                  // Invalid SVG - just store the raw content for editing
                                  updateElement(selected.id, (el) => ({
                                    ...el,
                                    svgContent: e.target.value,
                                  }));
                                }
                              }}
                              rows={6}
                              placeholder="Paste SVG code here..."
                              style={{
                                padding: 8,
                                borderRadius: 8,
                                border: `1px solid ${palette.border}`,
                                background: palette.cardAlt,
                                color: palette.text,
                                width: '100%',
                                fontFamily: 'monospace',
                                fontSize: 11,
                                resize: 'vertical',
                              }}
                            />
                          </label>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                            <label style={{ display: 'grid', gap: 4 }}>
                              <span style={{ fontSize: 12, color: palette.muted }}>Width</span>
                              <input
                                type="number"
                                min={1}
                                value={(selected as ShapeElement).width || 100}
                                onChange={(e) => {
                                  const shape = selected as ShapeElement;
                                  const newWidth = Math.max(1, Number(e.target.value));
                                  const maintainAspect = shape.maintainAspectRatio !== false; // default true
                                  if (maintainAspect) {
                                    const aspectRatio =
                                      (shape.width || 100) / (shape.height || 100);
                                    const newHeight = Math.round(newWidth / aspectRatio);
                                    updateElement(selected.id, (el) => ({
                                      ...el,
                                      width: newWidth,
                                      height: newHeight,
                                      v2: { x: newWidth, y: 0 },
                                    }));
                                  } else {
                                    updateElement(selected.id, (el) => ({
                                      ...el,
                                      width: newWidth,
                                      v2: { x: newWidth, y: 0 },
                                    }));
                                  }
                                }}
                                style={{
                                  padding: 8,
                                  borderRadius: 8,
                                  border: `1px solid ${palette.border}`,
                                  background: palette.cardAlt,
                                  color: palette.text,
                                  width: '100%',
                                }}
                              />
                            </label>
                            <label style={{ display: 'grid', gap: 4 }}>
                              <span style={{ fontSize: 12, color: palette.muted }}>Height</span>
                              <input
                                type="number"
                                min={1}
                                value={(selected as ShapeElement).height || 100}
                                onChange={(e) => {
                                  const shape = selected as ShapeElement;
                                  const newHeight = Math.max(1, Number(e.target.value));
                                  const maintainAspect = shape.maintainAspectRatio !== false; // default true
                                  if (maintainAspect) {
                                    const aspectRatio =
                                      (shape.width || 100) / (shape.height || 100);
                                    const newWidth = Math.round(newHeight * aspectRatio);
                                    updateElement(selected.id, (el) => ({
                                      ...el,
                                      width: newWidth,
                                      height: newHeight,
                                      v2: { x: newWidth, y: 0 },
                                    }));
                                  } else {
                                    updateElement(selected.id, (el) => ({
                                      ...el,
                                      height: newHeight,
                                    }));
                                  }
                                }}
                                style={{
                                  padding: 8,
                                  borderRadius: 8,
                                  border: `1px solid ${palette.border}`,
                                  background: palette.cardAlt,
                                  color: palette.text,
                                  width: '100%',
                                }}
                              />
                            </label>
                          </div>
                          <label
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              cursor: 'pointer',
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={(selected as ShapeElement).maintainAspectRatio !== false}
                              onChange={(e) => {
                                updateElement(selected.id, (el) => ({
                                  ...el,
                                  maintainAspectRatio: e.target.checked,
                                }));
                              }}
                              style={{ width: 16, height: 16 }}
                            />
                            <span style={{ fontSize: 12, color: palette.muted }}>
                              Maintain aspect ratio
                            </span>
                          </label>
                          <div style={{ fontSize: 12, color: palette.muted }}>
                            Inline SVG element. Paste valid SVG code above.
                          </div>
                        </>
                      )}

                      {/* Line/Rectangle controls */}
                      {(selected as ShapeElement).shapeType !== 'svg' && (
                        <>
                          {/* Vertex coordinates */}
                          <div style={{ fontSize: 12, color: palette.muted, marginTop: 8 }}>
                            Vertices (relative to position)
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                            <label style={{ display: 'grid', gap: 4 }}>
                              <span style={{ fontSize: 12, color: palette.muted }}>V1 X</span>
                              <input
                                type="number"
                                value={(selected as ShapeElement).v1.x}
                                onChange={(e) =>
                                  updateElement(selected.id, (el) => ({
                                    ...el,
                                    v1: { ...(el as ShapeElement).v1, x: Number(e.target.value) },
                                  }))
                                }
                                style={{
                                  padding: 8,
                                  borderRadius: 8,
                                  border: `1px solid ${palette.border}`,
                                  background: palette.cardAlt,
                                  color: palette.text,
                                  width: '100%',
                                }}
                              />
                            </label>
                            <label style={{ display: 'grid', gap: 4 }}>
                              <span style={{ fontSize: 12, color: palette.muted }}>V1 Y</span>
                              <input
                                type="number"
                                value={(selected as ShapeElement).v1.y}
                                onChange={(e) =>
                                  updateElement(selected.id, (el) => ({
                                    ...el,
                                    v1: { ...(el as ShapeElement).v1, y: Number(e.target.value) },
                                  }))
                                }
                                style={{
                                  padding: 8,
                                  borderRadius: 8,
                                  border: `1px solid ${palette.border}`,
                                  background: palette.cardAlt,
                                  color: palette.text,
                                  width: '100%',
                                }}
                              />
                            </label>
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                            <label style={{ display: 'grid', gap: 4 }}>
                              <span style={{ fontSize: 12, color: palette.muted }}>V2 X</span>
                              <input
                                type="number"
                                value={(selected as ShapeElement).v2.x}
                                onChange={(e) =>
                                  updateElement(selected.id, (el) => ({
                                    ...el,
                                    v2: { ...(el as ShapeElement).v2, x: Number(e.target.value) },
                                  }))
                                }
                                style={{
                                  padding: 8,
                                  borderRadius: 8,
                                  border: `1px solid ${palette.border}`,
                                  background: palette.cardAlt,
                                  color: palette.text,
                                  width: '100%',
                                }}
                              />
                            </label>
                            <label style={{ display: 'grid', gap: 4 }}>
                              <span style={{ fontSize: 12, color: palette.muted }}>V2 Y</span>
                              <input
                                type="number"
                                value={(selected as ShapeElement).v2.y}
                                onChange={(e) =>
                                  updateElement(selected.id, (el) => ({
                                    ...el,
                                    v2: { ...(el as ShapeElement).v2, y: Number(e.target.value) },
                                  }))
                                }
                                style={{
                                  padding: 8,
                                  borderRadius: 8,
                                  border: `1px solid ${palette.border}`,
                                  background: palette.cardAlt,
                                  color: palette.text,
                                  width: '100%',
                                }}
                              />
                            </label>
                          </div>
                          {(selected as ShapeElement).shapeType === 'rectangle' && (
                            <>
                              <div
                                style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}
                              >
                                <label style={{ display: 'grid', gap: 4 }}>
                                  <span style={{ fontSize: 12, color: palette.muted }}>V3 X</span>
                                  <input
                                    type="number"
                                    value={(selected as ShapeElement).v3?.x ?? 0}
                                    onChange={(e) =>
                                      updateElement(selected.id, (el) => ({
                                        ...el,
                                        v3: {
                                          ...((el as ShapeElement).v3 ?? { x: 0, y: 0 }),
                                          x: Number(e.target.value),
                                        },
                                      }))
                                    }
                                    style={{
                                      padding: 8,
                                      borderRadius: 8,
                                      border: `1px solid ${palette.border}`,
                                      background: palette.cardAlt,
                                      color: palette.text,
                                      width: '100%',
                                    }}
                                  />
                                </label>
                                <label style={{ display: 'grid', gap: 4 }}>
                                  <span style={{ fontSize: 12, color: palette.muted }}>V3 Y</span>
                                  <input
                                    type="number"
                                    value={(selected as ShapeElement).v3?.y ?? 0}
                                    onChange={(e) =>
                                      updateElement(selected.id, (el) => ({
                                        ...el,
                                        v3: {
                                          ...((el as ShapeElement).v3 ?? { x: 0, y: 0 }),
                                          y: Number(e.target.value),
                                        },
                                      }))
                                    }
                                    style={{
                                      padding: 8,
                                      borderRadius: 8,
                                      border: `1px solid ${palette.border}`,
                                      background: palette.cardAlt,
                                      color: palette.text,
                                      width: '100%',
                                    }}
                                  />
                                </label>
                              </div>
                              <div
                                style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}
                              >
                                <label style={{ display: 'grid', gap: 4 }}>
                                  <span style={{ fontSize: 12, color: palette.muted }}>V4 X</span>
                                  <input
                                    type="number"
                                    value={(selected as ShapeElement).v4?.x ?? 0}
                                    onChange={(e) =>
                                      updateElement(selected.id, (el) => ({
                                        ...el,
                                        v4: {
                                          ...((el as ShapeElement).v4 ?? { x: 0, y: 0 }),
                                          x: Number(e.target.value),
                                        },
                                      }))
                                    }
                                    style={{
                                      padding: 8,
                                      borderRadius: 8,
                                      border: `1px solid ${palette.border}`,
                                      background: palette.cardAlt,
                                      color: palette.text,
                                      width: '100%',
                                    }}
                                  />
                                </label>
                                <label style={{ display: 'grid', gap: 4 }}>
                                  <span style={{ fontSize: 12, color: palette.muted }}>V4 Y</span>
                                  <input
                                    type="number"
                                    value={(selected as ShapeElement).v4?.y ?? 0}
                                    onChange={(e) =>
                                      updateElement(selected.id, (el) => ({
                                        ...el,
                                        v4: {
                                          ...((el as ShapeElement).v4 ?? { x: 0, y: 0 }),
                                          y: Number(e.target.value),
                                        },
                                      }))
                                    }
                                    style={{
                                      padding: 8,
                                      borderRadius: 8,
                                      border: `1px solid ${palette.border}`,
                                      background: palette.cardAlt,
                                      color: palette.text,
                                      width: '100%',
                                    }}
                                  />
                                </label>
                              </div>
                            </>
                          )}

                          {/* Styling */}
                          <div style={{ fontSize: 12, color: palette.muted, marginTop: 8 }}>
                            Styling
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                            {caps.supportsColor && (
                            <label style={{ display: 'grid', gap: 4 }}>
                              <span style={{ fontSize: 12, color: palette.muted }}>
                                Stroke color
                              </span>
                              <input
                                type="color"
                                value={
                                  getCommonValueOfType<ShapeElement, string>(
                                    'shape',
                                    (el) => el.strokeColor,
                                  ) ?? '#000000'
                                }
                                onChange={(e) =>
                                  updateSelectedOfType<ShapeElement>('shape', (el) => ({
                                    ...el,
                                    strokeColor: e.target.value,
                                  }))
                                }
                                style={{
                                  height: 36,
                                  width: '100%',
                                  maxWidth: 160,
                                  background: palette.cardAlt,
                                  border: `1px solid ${palette.border}`,
                                  borderRadius: 8,
                                }}
                              />
                            </label>
                            )}
                            <label style={{ display: 'grid', gap: 4 }}>
                              <span style={{ fontSize: 12, color: palette.muted }}>
                                Stroke width
                              </span>
                              <input
                                type="number"
                                min={1}
                                max={20}
                                value={
                                  getCommonValueOfType<ShapeElement, number>(
                                    'shape',
                                    (el) => el.strokeWidth,
                                  ) ?? ''
                                }
                                onChange={(e) =>
                                  updateSelectedOfType<ShapeElement>('shape', (el) => ({
                                    ...el,
                                    strokeWidth: Math.max(1, Number(e.target.value)),
                                  }))
                                }
                                style={{
                                  padding: 8,
                                  borderRadius: 8,
                                  border: `1px solid ${palette.border}`,
                                  background: palette.cardAlt,
                                  color: palette.text,
                                  width: '100%',
                                }}
                              />
                            </label>
                          </div>
                          {(selected as ShapeElement).shapeType === 'rectangle' && caps.supportsShapeFill && (
                            <label style={{ display: 'grid', gap: 4 }}>
                              <span style={{ fontSize: 12, color: palette.muted }}>Fill color</span>
                              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                <input
                                  type="color"
                                  value={(() => {
                                    const common = getCommonValueOfType<
                                      ShapeElement,
                                      string | undefined
                                    >('shape', (el) => el.fillColor);
                                    return common === 'transparent' || !common ? '#ffffff' : common;
                                  })()}
                                  onChange={(e) =>
                                    updateSelectedOfType<ShapeElement>('shape', (el) => ({
                                      ...el,
                                      fillColor: e.target.value,
                                    }))
                                  }
                                  style={{
                                    height: 36,
                                    width: 60,
                                    background: palette.cardAlt,
                                    border: `1px solid ${palette.border}`,
                                    borderRadius: 8,
                                  }}
                                />
                                <label
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 4,
                                    fontSize: 12,
                                    color: palette.muted,
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={
                                      getCommonValueOfType<ShapeElement, boolean>(
                                        'shape',
                                        (el) => el.fillColor === 'transparent' || !el.fillColor,
                                      ) ?? false
                                    }
                                    onChange={(e) =>
                                      updateSelectedOfType<ShapeElement>('shape', (el) => ({
                                        ...el,
                                        fillColor: e.target.checked ? 'transparent' : '#ffffff',
                                      }))
                                    }
                                  />
                                  Transparent
                                </label>
                              </div>
                            </label>
                          )}
                          {caps.supportsColor && renderColorConditions(
                            selected.id,
                            'strokeColorConditions',
                            (selected as ShapeElement).strokeColorConditions,
                            (selected as ShapeElement).strokeColor || '#000000',
                            'stroke color',
                          )}
                          {caps.supportsColor && (selected as ShapeElement).shapeType === 'rectangle' &&
                            renderColorConditions(
                              selected.id,
                              'fillColorConditions',
                              (selected as ShapeElement).fillColorConditions,
                              (selected as ShapeElement).fillColor || '#ffffff',
                              'fill color',
                            )}
                          <div style={{ fontSize: 12, color: palette.muted }}>
                            {(selected as ShapeElement).shapeType === 'line'
                              ? 'Line defined by 2 vertices. Drag vertex handles to reposition.'
                              : 'Rectangle defined by 4 vertices. Drag vertex handles to reshape.'}
                          </div>
                        </>
                      )}
                    </>
                  ) : null}
                </div>
              ) : (
                <div style={{ color: palette.muted, fontSize: 14 }}>
                  Select or drop an element to edit its properties.
                </div>
              )
            ) : (
              <div style={{ color: palette.muted, fontSize: 13 }}>Properties collapsed.</div>
            )}
          </div>

          <div
            className="card"
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
              minWidth: 0,
              overflow: 'hidden',
              alignSelf: 'stretch',
              minHeight: 0,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
                flexWrap: 'wrap',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <h3 style={{ margin: 0, fontSize: 16, letterSpacing: 0.2 }}>Added Components</h3>
                <span className="wysiwyg-chip">{template.elements.length} active</span>
              </div>
              <button
                onClick={() => setShowAddedPane((open) => !open)}
                className="wysiwyg-chip secondary"
              >
                {showAddedPane ? 'Collapse' : 'Expand'}
              </button>
            </div>
            {showAddedPane && (
              <div
                style={{
                  display: 'grid',
                  gap: 8,
                  overflow: 'auto',
                  paddingRight: 4,
                  maxHeight: '34vh',
                }}
              >
                {template.elements.length === 0 && (
                  <div style={{ color: palette.muted, fontSize: 13 }}>
                    No components on canvas yet.
                  </div>
                )}
                {template.elements.map((el) => (
                  <div
                    key={el.id}
                    onClick={(e) => {
                      const additive = e.shiftKey || e.ctrlKey || e.metaKey;
                      selectElement(el.id, additive);
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 8,
                      padding: '10px 12px',
                      borderRadius: 12,
                      border: selectedIds.has(el.id)
                        ? `2px solid ${palette.accent}`
                        : `1px solid ${palette.border}`,
                      background: selectedIds.has(el.id) ? palette.accent + '15' : palette.cardAlt,
                      cursor: 'pointer',
                      transition: 'border-color 0.15s, background 0.15s',
                    }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
                      <div
                        style={{
                          fontWeight: 700,
                          fontSize: 13,
                          color: palette.text,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {describeElement(el)}
                      </div>
                      <div
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          fontSize: 12,
                          color: palette.muted,
                        }}
                      >
                        <span
                          style={{
                            padding: '2px 8px',
                            borderRadius: 8,
                            background: palette.cardBg,
                            border: `1px solid ${palette.border}`,
                            color: palette.text,
                          }}
                        >
                          {el.type === 'text'
                            ? 'Text'
                            : el.type === 'image'
                              ? 'Image'
                              : el.type === 'qr'
                                ? 'QR'
                                : el.type === 'barcode'
                                  ? 'Barcode'
                                  : el.type === 'shape'
                                    ? 'Shape'
                                    : 'Unknown'}
                        </span>
                        <span>
                          x: {Math.round(el.x)}, y: {Math.round(el.y)}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => recenterElement(el.id)}
                      className="wysiwyg-chip"
                      style={{ flexShrink: 0 }}
                    >
                      Recenter
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div
            className="card"
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
              minWidth: 0,
              overflow: 'hidden',
              alignSelf: 'stretch',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
                flexWrap: 'wrap',
              }}
            >
              <div>
                <h3 style={{ margin: 0, fontSize: 16, letterSpacing: 0.2 }}>Theme Variables</h3>
                <div style={{ fontSize: 12, color: palette.muted }}>
                  Define reusable values and drag them onto the canvas.
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span className="wysiwyg-chip">{themeVariables.length} total</span>
                <button
                  onClick={() => setShowThemePane((open) => !open)}
                  className="wysiwyg-chip secondary"
                >
                  {showThemePane ? 'Collapse' : 'Expand'}
                </button>
              </div>
            </div>
            {showThemePane ? (
              <>
                <div style={{ display: 'grid', gap: 8, minWidth: 0 }}>
                  <label style={{ display: 'grid', gap: 4, minWidth: 0 }}>
                    <span style={{ fontSize: 12, color: palette.muted }}>Name</span>
                    <input
                      value={newThemeName}
                      onChange={(e) => setNewThemeName(e.target.value)}
                      placeholder="e.g. Primary Color"
                      style={{
                        padding: '8px 10px',
                        borderRadius: 10,
                        border: `1px solid ${palette.border}`,
                        background: palette.cardAlt,
                        color: palette.text,
                        width: '100%',
                        minWidth: 0,
                      }}
                    />
                  </label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <label style={{ display: 'grid', gap: 4, minWidth: 0 }}>
                      <span style={{ fontSize: 12, color: palette.muted }}>Type</span>
                      <select
                        value={newThemeType}
                        onChange={(e) => {
                          const next = e.target.value as ThemeVariableType;
                          setNewThemeType(next);
                          if (
                            next === 'boolean' &&
                            newThemeValue !== 'true' &&
                            newThemeValue !== 'false'
                          )
                            setNewThemeValue('true');
                          if (next === 'select') {
                            setNewThemeOptions([]);
                            setNewThemeValue('');
                          } else {
                            setNewThemeOptions([]);
                          }
                        }}
                        style={{
                          padding: '8px 10px',
                          borderRadius: 10,
                          border: `1px solid ${palette.border}`,
                          background: palette.cardAlt,
                          color: palette.text,
                          width: '100%',
                          minWidth: 0,
                        }}
                      >
                        <option value="string">String</option>
                        <option value="number">Number</option>
                        <option value="boolean">Boolean</option>
                        <option value="date">Date</option>
                        <option value="select">Select (Dropdown)</option>
                      </select>
                    </label>
                    <label style={{ display: 'grid', gap: 4, minWidth: 0 }}>
                      <span style={{ fontSize: 12, color: palette.muted }}>Value</span>
                      {newThemeType === 'boolean' ? (
                        <select
                          value={newThemeValue === '' ? 'true' : newThemeValue}
                          onChange={(e) => setNewThemeValue(e.target.value)}
                          style={{
                            padding: '8px 10px',
                            borderRadius: 10,
                            border: `1px solid ${palette.border}`,
                            background: palette.cardAlt,
                            color: palette.text,
                            width: '100%',
                            minWidth: 0,
                          }}
                        >
                          <option value="true">True</option>
                          <option value="false">False</option>
                        </select>
                      ) : newThemeType === 'date' ? (
                        <input
                          type="date"
                          value={newThemeValue}
                          onChange={(e) => setNewThemeValue(e.target.value)}
                          style={{
                            padding: '8px 10px',
                            borderRadius: 10,
                            border: `1px solid ${palette.border}`,
                            background: palette.cardAlt,
                            color: palette.text,
                            width: '100%',
                            minWidth: 0,
                          }}
                        />
                      ) : newThemeType === 'select' ? (
                        <select
                          value={newThemeValue}
                          onChange={(e) => setNewThemeValue(e.target.value)}
                          disabled={newThemeOptions.length === 0}
                          style={{
                            padding: '8px 10px',
                            borderRadius: 10,
                            border: `1px solid ${palette.border}`,
                            background: palette.cardAlt,
                            color: palette.text,
                            width: '100%',
                            minWidth: 0,
                          }}
                        >
                          {newThemeOptions.length === 0 ? (
                            <option value="">Add options below</option>
                          ) : (
                            newThemeOptions
                              .map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))
                          )}
                        </select>
                      ) : (
                        <input
                          type={newThemeType === 'number' ? 'number' : 'text'}
                          value={newThemeValue}
                          onChange={(e) => setNewThemeValue(e.target.value)}
                          style={{
                            padding: '8px 10px',
                            borderRadius: 10,
                            border: `1px solid ${palette.border}`,
                            background: palette.cardAlt,
                            color: palette.text,
                            width: '100%',
                            minWidth: 0,
                          }}
                        />
                      )}
                    </label>
                  </div>
                  {newThemeType === 'select' && (
                    <div style={{ display: 'grid', gap: 8, padding: '8px 0' }}>
                      <span style={{ fontSize: 12, color: palette.muted }}>Dropdown Options</span>
                      {newThemeOptions.length > 0 && (
                        <div style={{ display: 'grid', gap: 4 }}>
                          {newThemeOptions.map((opt, idx) => (
                            <div
                              key={idx}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                padding: '6px 8px',
                                borderRadius: 8,
                                background: palette.cardBg,
                                border: `1px solid ${palette.border}`,
                                minWidth: 0,
                              }}
                            >
                              <span
                                style={{
                                  flex: 1,
                                  fontSize: 13,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  minWidth: 0,
                                }}
                              >
                                {opt.label}
                              </span>
                              <code
                                style={{
                                  fontSize: 11,
                                  color: palette.muted,
                                  flexShrink: 0,
                                  maxWidth: '40%',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                              >
                                {opt.value}
                              </code>
                              <button
                                onClick={() => {
                                  const next = newThemeOptions.filter((_, i) => i !== idx);
                                  setNewThemeOptions(next);
                                  if (newThemeValue === opt.value) {
                                    setNewThemeValue(next.length > 0 ? next[0].value : '');
                                  }
                                }}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  color: palette.muted,
                                  cursor: 'pointer',
                                  padding: '2px 6px',
                                  fontSize: 14,
                                  flexShrink: 0,
                                }}
                                title="Remove option"
                              >
                                &times;
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <input
                          type="text"
                          placeholder="Value"
                          id="new-option-value"
                          style={{
                            flex: '1 1 80px',
                            minWidth: 0,
                            padding: '6px 8px',
                            borderRadius: 8,
                            border: `1px solid ${palette.border}`,
                            background: palette.cardBg,
                            color: palette.text,
                            fontSize: 13,
                          }}
                        />
                        <input
                          type="text"
                          placeholder="Label (optional)"
                          id="new-option-label"
                          style={{
                            flex: '1 1 80px',
                            minWidth: 0,
                            padding: '6px 8px',
                            borderRadius: 8,
                            border: `1px solid ${palette.border}`,
                            background: palette.cardBg,
                            color: palette.text,
                            fontSize: 13,
                          }}
                        />
                        <button
                          onClick={() => {
                            const valueInput = document.getElementById(
                              'new-option-value',
                            ) as HTMLInputElement;
                            const labelInput = document.getElementById(
                              'new-option-label',
                            ) as HTMLInputElement;
                            const value = valueInput?.value?.trim();
                            const label = labelInput?.value?.trim() || value;
                            if (value) {
                              const isFirst = newThemeOptions.length === 0;
                              setNewThemeOptions([...newThemeOptions, { value, label }]);
                              if (isFirst) setNewThemeValue(value);
                              valueInput.value = '';
                              labelInput.value = '';
                            }
                          }}
                          className="wysiwyg-chip secondary"
                          style={{ padding: '6px 12px', fontSize: 12, flexShrink: 0 }}
                        >
                          Add
                        </button>
                      </div>
                    </div>
                  )}
                  <button
                    onClick={addThemeVariable}
                    className="wysiwyg-chip"
                    style={{ justifyContent: 'center' }}
                    disabled={
                      !newThemeName.trim() ||
                      (newThemeType === 'select' && newThemeOptions.length === 0)
                    }
                  >
                    Add variable
                  </button>
                </div>
                <div
                  onDragOver={(e) => {
                    if (themeVarDragIdx !== null) {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = 'move';
                    }
                  }}
                  style={{
                    display: 'grid',
                    gap: 8,
                    overflow: 'auto',
                    paddingRight: 4,
                    minWidth: 0,
                  }}
                >
                  {themeVariables.length === 0 && (
                    <div style={{ color: palette.muted, fontSize: 13 }}>
                      No theme variables yet.
                    </div>
                  )}
                  {themeVariables.map((variable, varIdx) => {
                    const coalescedValue = parseThemeVariableValue(variable.value, variable.type);
                    const showPlaceholderBefore =
                      themeVarDragIdx !== null &&
                      themeVarDropIdx === varIdx &&
                      themeVarDragIdx !== varIdx;
                    return (
                      <React.Fragment key={variable.id}>
                        {showPlaceholderBefore && (
                          <div
                            onDragOver={(e) => {
                              if (themeVarDragIdx !== null) {
                                e.preventDefault();
                                e.dataTransfer.dropEffect = 'move';
                              }
                            }}
                            onDrop={(e) => {
                              if (themeVarDragIdx !== null) {
                                e.preventDefault();
                                reorderThemeVariables(themeVarDragIdx, varIdx);
                                setThemeVarDragIdx(null);
                                setThemeVarDropIdx(null);
                              }
                            }}
                            style={{
                              height: 50,
                              borderRadius: 12,
                              border: '2px dashed #4f8cff',
                              background: 'rgba(79, 140, 255, 0.05)',
                            }}
                          />
                        )}
                        <div
                          draggable
                          onDragStart={(e) => {
                            const payload: VariableOption = {
                              key: variable.key,
                              metrcName: variable.name,
                              type: variable.type,
                              description: 'Theme variable',
                              example: String(parseThemeVariableValue(variable.value, variable.type)),
                            };
                            e.dataTransfer.setData('application/x-theme-variable', JSON.stringify(payload));
                            e.dataTransfer.setData('application/x-theme-reorder', String(varIdx));
                            e.dataTransfer.effectAllowed = 'copyMove';
                            setThemeVarDragIdx(varIdx);
                            startDragViewportAutoScroll(e.clientX, e.clientY);
                          }}
                          onDragEnd={() => {
                            stopDragViewportAutoScroll();
                            setThemeVarDragIdx(null);
                            setThemeVarDropIdx(null);
                          }}
                        onDragOver={(e) => {
                          if (themeVarDragIdx !== null) {
                            e.preventDefault();
                            e.dataTransfer.dropEffect = 'move';
                            if (themeVarDropIdx !== varIdx) setThemeVarDropIdx(varIdx);
                          }
                        }}
                        onDrop={(e) => {
                            if (themeVarDragIdx !== null) {
                              e.preventDefault();
                              reorderThemeVariables(themeVarDragIdx, varIdx);
                              setThemeVarDragIdx(null);
                              setThemeVarDropIdx(null);
                            }
                          }}
                          style={{
                            padding: '10px 12px',
                            borderRadius: 12,
                            border: `1px solid ${palette.border}`,
                            background: palette.cardAlt,
                            display: 'grid',
                            gap: 8,
                            cursor: 'grab',
                            minWidth: 0,
                            opacity: themeVarDragIdx === varIdx ? 0.4 : 1,
                            transition: 'opacity 0.15s',
                          }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 8,
                            minWidth: 0,
                          }}
                        >
                          <div
                            data-theme-grip
                            title="Drag to reorder"
                            style={{
                              cursor: 'grab',
                              display: 'flex',
                              alignItems: 'center',
                              padding: '4px 2px',
                              color: palette.muted,
                              flexShrink: 0,
                            }}
                          >
                            <svg width="10" height="16" viewBox="0 0 10 16" fill="currentColor">
                              <circle cx="3" cy="2" r="1.5" />
                              <circle cx="7" cy="2" r="1.5" />
                              <circle cx="3" cy="6" r="1.5" />
                              <circle cx="7" cy="6" r="1.5" />
                              <circle cx="3" cy="10" r="1.5" />
                              <circle cx="7" cy="10" r="1.5" />
                              <circle cx="3" cy="14" r="1.5" />
                              <circle cx="7" cy="14" r="1.5" />
                            </svg>
                          </div>
                          <input
                            value={variable.name}
                            onChange={(e) => renameThemeVariable(variable.id, e.target.value)}
                            style={{
                              padding: '6px 8px',
                              borderRadius: 8,
                              border: `1px solid ${palette.border}`,
                              background: palette.cardBg,
                              color: palette.text,
                              flex: 1,
                              minWidth: 0,
                            }}
                          />
                          <code
                            style={{
                              fontSize: 11,
                              color: palette.muted,
                              background: palette.cardBg,
                              padding: '2px 6px',
                              borderRadius: 6,
                              border: `1px solid ${palette.border}`,
                              flexShrink: 0,
                              maxWidth: '50%',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {variable.key}
                          </code>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                          <label style={{ display: 'grid', gap: 4, minWidth: 0 }}>
                            <span style={{ fontSize: 12, color: palette.muted }}>Type</span>
                            <select
                              value={variable.type}
                              onChange={(e) => {
                                const nextType = e.target.value as ThemeVariableType;
                                changeThemeVariableType(variable.id, nextType);
                                // Initialize empty options array when switching to select type
                                if (
                                  nextType === 'select' &&
                                  (!variable.options || variable.options.length === 0)
                                ) {
                                  updateThemeVariable(variable.id, (v) => ({ ...v, options: [] }));
                                }
                              }}
                              style={{
                                padding: '8px 10px',
                                borderRadius: 10,
                                border: `1px solid ${palette.border}`,
                                background: palette.cardBg,
                                color: palette.text,
                                width: '100%',
                                minWidth: 0,
                              }}
                            >
                              <option value="string">String</option>
                              <option value="number">Number</option>
                              <option value="boolean">Boolean</option>
                              <option value="date">Date</option>
                              <option value="select">Select (Dropdown)</option>
                            </select>
                          </label>
                          <label style={{ display: 'grid', gap: 4, minWidth: 0 }}>
                            <span style={{ fontSize: 12, color: palette.muted }}>Value</span>
                            {variable.type === 'boolean' ? (
                              <select
                                value={coalescedValue ? 'true' : 'false'}
                                onChange={(e) =>
                                  changeThemeVariableValue(variable.id, e.target.value)
                                }
                                style={{
                                  padding: '8px 10px',
                                  borderRadius: 10,
                                  border: `1px solid ${palette.border}`,
                                  background: palette.cardBg,
                                  color: palette.text,
                                  width: '100%',
                                  minWidth: 0,
                                }}
                              >
                                <option value="true">True</option>
                                <option value="false">False</option>
                              </select>
                            ) : variable.type === 'date' ? (
                              <input
                                type="date"
                                value={String(variable.value ?? '')}
                                onChange={(e) =>
                                  changeThemeVariableValue(variable.id, e.target.value)
                                }
                                style={{
                                  padding: '8px 10px',
                                  borderRadius: 10,
                                  border: `1px solid ${palette.border}`,
                                  background: palette.cardBg,
                                  color: palette.text,
                                  width: '100%',
                                  minWidth: 0,
                                }}
                              />
                            ) : variable.type === 'select' ? (
                              <select
                                value={String(variable.value ?? '')}
                                onChange={(e) =>
                                  changeThemeVariableValue(variable.id, e.target.value)
                                }
                                disabled={!variable.options || variable.options.length === 0}
                                style={{
                                  padding: '8px 10px',
                                  borderRadius: 10,
                                  border: `1px solid ${palette.border}`,
                                  background: palette.cardBg,
                                  color: palette.text,
                                  width: '100%',
                                  minWidth: 0,
                                }}
                              >
                                {!variable.options || variable.options.length === 0 ? (
                                  <option value="">Add options below</option>
                                ) : (
                                  <>
                                    <option value="">-- Select --</option>
                                    {variable.options
                                      .map((opt) => (
                                        <option key={opt.value} value={opt.value}>
                                          {opt.label}
                                        </option>
                                      ))}
                                  </>
                                )}
                              </select>
                            ) : (
                              <input
                                type={variable.type === 'number' ? 'number' : 'text'}
                                value={String(variable.value ?? '')}
                                onChange={(e) =>
                                  changeThemeVariableValue(variable.id, e.target.value)
                                }
                                style={{
                                  padding: '8px 10px',
                                  borderRadius: 10,
                                  border: `1px solid ${palette.border}`,
                                  background: palette.cardBg,
                                  color: palette.text,
                                  width: '100%',
                                  minWidth: 0,
                                }}
                              />
                            )}
                          </label>
                        </div>
                        {variable.type === 'select' && (
                          <div style={{ display: 'grid', gap: 8, padding: '8px 0' }}>
                            <span style={{ fontSize: 12, color: palette.muted }}>
                              Dropdown Options
                            </span>
                            {variable.options && variable.options.length > 0 && (
                              <div
                                onDragOver={(e) => {
                                  if (optionDragState && optionDragState.variableId === variable.id) {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    e.dataTransfer.dropEffect = 'move';
                                  }
                                }}
                                style={{ display: 'grid', gap: 4 }}
                              >
                                {variable.options.map((opt, idx) => {
                                  const showOptPlaceholder =
                                    optionDragState?.variableId === variable.id &&
                                    optionDropIdx === idx &&
                                    optionDragState?.fromIdx !== idx;
                                  return (
                                    <React.Fragment key={idx}>
                                      {showOptPlaceholder && (
                                        <div
                                          onDragOver={(e) => {
                                            if (optionDragState && optionDragState.variableId === variable.id) {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              e.dataTransfer.dropEffect = 'move';
                                            }
                                          }}
                                          onDrop={(e) => {
                                            if (optionDragState && optionDragState.variableId === variable.id) {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              reorderThemeVariableOptions(variable.id, optionDragState.fromIdx, idx);
                                              setOptionDragState(null);
                                              setOptionDropIdx(null);
                                            }
                                          }}
                                          style={{
                                            height: 32,
                                            borderRadius: 8,
                                            border: '2px dashed #4f8cff',
                                            background: 'rgba(79, 140, 255, 0.05)',
                                          }}
                                        />
                                      )}
                                      <div
                                        draggable
                                        onDragStart={(e) => {
                                          e.stopPropagation();
                                          e.dataTransfer.setData('application/x-option-reorder', String(idx));
                                          e.dataTransfer.effectAllowed = 'move';
                                          setOptionDragState({ variableId: variable.id, fromIdx: idx });
                                        }}
                                        onDragEnd={() => {
                                          setOptionDragState(null);
                                          setOptionDropIdx(null);
                                        }}
                                        onDragOver={(e) => {
                                          if (optionDragState && optionDragState.variableId === variable.id) {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            e.dataTransfer.dropEffect = 'move';
                                            if (optionDropIdx !== idx) setOptionDropIdx(idx);
                                          }
                                        }}
                                        onDrop={(e) => {
                                          if (optionDragState && optionDragState.variableId === variable.id) {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            reorderThemeVariableOptions(variable.id, optionDragState.fromIdx, idx);
                                            setOptionDragState(null);
                                            setOptionDropIdx(null);
                                          }
                                        }}
                                        style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 8,
                                          padding: '6px 8px',
                                          borderRadius: 8,
                                          background: palette.cardBg,
                                          border: `1px solid ${palette.border}`,
                                          minWidth: 0,
                                          opacity: optionDragState?.variableId === variable.id && optionDragState?.fromIdx === idx ? 0.4 : 1,
                                          cursor: 'grab',
                                          transition: 'opacity 0.15s',
                                        }}
                                      >
                                    <div
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        color: palette.muted,
                                        flexShrink: 0,
                                        cursor: 'grab',
                                      }}
                                      title="Drag to reorder"
                                    >
                                      <svg width="8" height="12" viewBox="0 0 8 12" fill="currentColor">
                                        <circle cx="2" cy="2" r="1.2" />
                                        <circle cx="6" cy="2" r="1.2" />
                                        <circle cx="2" cy="6" r="1.2" />
                                        <circle cx="6" cy="6" r="1.2" />
                                        <circle cx="2" cy="10" r="1.2" />
                                        <circle cx="6" cy="10" r="1.2" />
                                      </svg>
                                    </div>
                                    <span
                                      style={{
                                        flex: 1,
                                        fontSize: 13,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        minWidth: 0,
                                      }}
                                    >
                                      {opt.label}
                                    </span>
                                    <code
                                      style={{
                                        fontSize: 11,
                                        color: palette.muted,
                                        flexShrink: 0,
                                        maxWidth: '40%',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                      }}
                                    >
                                      {opt.value}
                                    </code>
                                    <button
                                      onClick={() => {
                                        const nextOptions = (variable.options || []).filter(
                                          (_, i) => i !== idx,
                                        );
                                        updateThemeVariable(variable.id, (v) => ({
                                          ...v,
                                          options: nextOptions,
                                          value:
                                            v.value === opt.value
                                              ? nextOptions.length > 0
                                                ? nextOptions[0].value
                                                : ''
                                              : v.value,
                                        }));
                                      }}
                                      style={{
                                        background: 'none',
                                        border: 'none',
                                        color: palette.muted,
                                        cursor: 'pointer',
                                        padding: '2px 6px',
                                        fontSize: 14,
                                        flexShrink: 0,
                                      }}
                                      title="Remove option"
                                    >
                                      &times;
                                    </button>
                                  </div>
                                    </React.Fragment>
                                  );
                                })}
                              </div>
                            )}
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                              <input
                                type="text"
                                placeholder="Value"
                                id={`edit-option-value-${variable.id}`}
                                style={{
                                  flex: '1 1 80px',
                                  minWidth: 0,
                                  padding: '6px 8px',
                                  borderRadius: 8,
                                  border: `1px solid ${palette.border}`,
                                  background: palette.cardBg,
                                  color: palette.text,
                                  fontSize: 13,
                                }}
                              />
                              <input
                                type="text"
                                placeholder="Label (optional)"
                                id={`edit-option-label-${variable.id}`}
                                style={{
                                  flex: '1 1 80px',
                                  minWidth: 0,
                                  padding: '6px 8px',
                                  borderRadius: 8,
                                  border: `1px solid ${palette.border}`,
                                  background: palette.cardBg,
                                  color: palette.text,
                                  fontSize: 13,
                                }}
                              />
                              <button
                                onClick={() => {
                                  const valueInput = document.getElementById(
                                    `edit-option-value-${variable.id}`,
                                  ) as HTMLInputElement;
                                  const labelInput = document.getElementById(
                                    `edit-option-label-${variable.id}`,
                                  ) as HTMLInputElement;
                                  const value = valueInput?.value?.trim();
                                  const label = labelInput?.value?.trim() || value;
                                  if (value) {
                                    const currentOptions = variable.options || [];
                                    const isFirst = currentOptions.length === 0;
                                    updateThemeVariable(variable.id, (v) => ({
                                      ...v,
                                      options: [...currentOptions, { value, label }],
                                      value: isFirst ? value : v.value,
                                    }));
                                    valueInput.value = '';
                                    labelInput.value = '';
                                  }
                                }}
                                className="wysiwyg-chip secondary"
                                style={{ padding: '6px 12px', fontSize: 12, flexShrink: 0 }}
                              >
                                Add
                              </button>
                            </div>
                          </div>
                        )}
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 8,
                          }}
                        >
                          <div style={{ fontSize: 12, color: palette.muted }}>
                            Drag to add to canvas
                          </div>
                          <button
                            onClick={() => removeThemeVariable(variable.id)}
                            className="wysiwyg-chip secondary"
                          >
                            Remove
                          </button>
                        </div>
                        </div>
                      </React.Fragment>
                    );
                  })}
                </div>
              </>
            ) : null}
          </div>
        </div>
      </div>

      {/* Asset Browser Modal */}
      {assetBrowserState?.isOpen && api && organizationId && (
        <AssetBrowser
          isOpen={assetBrowserState.isOpen}
          onClose={() => setAssetBrowserState(null)}
          onSelect={handleAssetSelected}
          category={assetBrowserState.category}
          organizationId={organizationId}
          api={api as unknown as AssetApi}
        />
      )}
    </div>
  );
};

export default LabelEditor;
